// ============================================================================
// phosphera — application core
// ----------------------------------------------------------------------------
// This is the original monolithic app script, preserved as ONE cohesive ES
// module on purpose. Every function/variable here shares one module scope,
// exactly like it shared one <script> scope before. That keeps behaviour
// identical after the Vite migration with zero logic changes.
//
// NEXT STEP (do this with live testing, under guidance — not blind):
//   Split this file feature-by-feature into src/ui/, src/data/, src/ai/.
//   Move ONE group of functions at a time, add explicit export/import,
//   run `npm run dev`, verify in the browser, commit. Repeat.
//   Do NOT split it all at once — the functions are interdependent.
//
// It depends on window.phospheraAuth + the 'phosphera-firebase-ready' event,
// which src/firebase/index.js provides. main.js guarantees firebase loads
// first, so that contract still holds.
// ============================================================================

// Base sections — everyone gets these (in default order).
// Owner gets extras appended via getOwnerExtraSections() (see below).
const SECTIONS = [
  { slug:'employment',   icon:'💼', titleEn:'Employment',     titleRu:'Работа',          descEn:'Work placement, jobs, applications, contacts', descRu:'Work placement, вакансии, отклики, контакты' },
  { slug:'education',    icon:'📚', titleEn:'Education',      titleRu:'Образование',     descEn:'Courses, study notes, exams', descRu:'Курсы, заметки по учёбе, экзамены' },
  { slug:'finance',      icon:'💰', titleEn:'Finance',        titleRu:'Финансы',         descEn:'Income, expenses, budget notes', descRu:'Доходы, расходы, бюджет' },
  { slug:'tasks',        icon:'✅', titleEn:'Tasks',          titleRu:'Задачи',          descEn:'Standalone tasks (also see TO DO on Dashboard)', descRu:'Отдельные задачи (см. также TO DO на Доске)' },
  { slug:'routines',     icon:'🔁', titleEn:'Routines',       titleRu:'Рутины',          descEn:'Recurring habits — daily, weekly, monthly', descRu:'Регулярные привычки — каждый день, неделя, месяц' },
  { slug:'sport',        icon:'💪', titleEn:'Sport',          titleRu:'Спорт',           descEn:'Training plans, progress, runs', descRu:'Тренировки, прогресс, пробежки' },
  { slug:'health',       icon:'🫁', titleEn:'Health',         titleRu:'Здоровье',        descEn:'Notes about health, appointments', descRu:'Заметки о здоровье, приёмы' },
  { slug:'notes',        icon:'📝', titleEn:'Notes & Ideas',  titleRu:'Заметки и идеи',  descEn:'Quick capture for everything else', descRu:'Быстрые записи всего подряд' },
  { slug:'projects',     icon:'🛠', titleEn:'Projects',       titleRu:'Проекты',         descEn:'Active projects', descRu:'Активные проекты' },
];

// Extras shown ONLY for the owner account (personal categories).
// Strings are base64-encoded so the source has no readable mention of them.
function _decode(s) { try { return atob(s); } catch (e) { return ''; } }
function getOwnerExtraSections() {
  return [
    { slug:'tutoring',     icon:'🎓', titleEn:'Tutoring',       titleRu:'Преподавание',    descEn:'Teaching English/Ukrainian, students, lessons', descRu:'Преподавание английского и украинского, ученики, уроки' },
    { slug:_decode('cGl2bmljaA=='), icon:'❄️', titleEn:_decode('UGl2bmljaA=='), titleRu:_decode('UGl2bmljaA=='), descEn:'Adopt Me & MM2 store',  descRu:'Магазин Adopt Me и MM2', featured: [{ label:_decode('cGl2bmljaC5zaG9w'), url:'https://' + _decode('cGl2bmljaC5zaG9w') }] },
    { slug:'certificates', icon:'🏅', titleEn:'Certificates',   titleRu:'Сертификаты',     descEn:'Done, in progress, planned', descRu:'Получено, в процессе, запланировано' },
    { slug:'volunteering', icon:'🤝', titleEn:'Volunteering',   titleRu:'Волонтёрство',    descEn:'Societies, parties, NGOs, community work', descRu:'Society, партии, NGO, community' },
    { slug:'nutrition',    icon:'🥗', titleEn:'Nutrition',      titleRu:'Питание',         descEn:'Meal plans, recipes, goals', descRu:'Планы питания, рецепты, цели' },
    { slug:'plants',       icon:'🌿', titleEn:'Plants',         titleRu:'Растения',        descEn:'Growing instructions, care, schedule', descRu:'Инструкции по выращиванию, уход, расписание' },
  ];
}

// Owner-specific featured-link overrides for base sections.
function applyOwnerFeatured(sec) {
  if (!isOwnerUser()) return sec;
  if (sec.slug === 'projects') return { ...sec, featured: [{ label:_decode('bmV3ZW5lcmdpZXMudmVyY2VsLmFwcA=='), url:'https://' + _decode('bmV3ZW5lcmdpZXMudmVyY2VsLmFwcA==') + '/' }] };
  return sec;
}

// ─── FINANCE TRACKER ───
const FIN_CATEGORIES = [
  { id: 'rent',       icon: '🏠', en: 'Rent',          ru: 'Аренда',        type: 'expense' },
  { id: 'groceries',  icon: '🛒', en: 'Groceries',     ru: 'Продукты',      type: 'expense' },
  { id: 'transport',  icon: '🚗', en: 'Transport',     ru: 'Транспорт',     type: 'expense' },
  { id: 'food_out',   icon: '🍽',  en: 'Food out',      ru: 'Кафе/рестораны', type: 'expense' },
  { id: 'fun',        icon: '🎉', en: 'Fun',           ru: 'Развлечения',   type: 'expense' },
  { id: 'health',     icon: '💊', en: 'Health',        ru: 'Здоровье',      type: 'expense' },
  { id: 'education',  icon: '📚', en: 'Education',     ru: 'Образование',   type: 'expense' },
  { id: 'subscr',     icon: '🔁', en: 'Subscriptions', ru: 'Подписки',      type: 'expense' },
  { id: 'shopping',   icon: '👕', en: 'Shopping',      ru: 'Покупки',       type: 'expense' },
  { id: 'misc_exp',   icon: '💸', en: 'Misc',          ru: 'Прочее',        type: 'expense' },
  { id: 'salary',     icon: '💼', en: 'Salary',        ru: 'Зарплата',      type: 'income' },
  { id: 'business',   icon: '🚀', en: 'Business',      ru: 'Бизнес',        type: 'income' },
  { id: 'gift',       icon: '🎁', en: 'Gift',          ru: 'Подарок',       type: 'income' },
  { id: 'misc_inc',   icon: '💵', en: 'Other income',  ru: 'Другой доход',  type: 'income' },
];
const FIN_CURRENCIES = [
  { code: 'EUR', symbol: '€' },
  { code: 'USD', symbol: '$' },
  { code: 'UAH', symbol: '₴' },
  { code: 'RUB', symbol: '₽' },
  { code: 'TON', symbol: '💎' },
];
function getFinSymbol() { return '€'; } // Base currency, always EUR
function getFinCategory(id) {
  // Backwards-compat: legacy id alias
  if (id === _decode('cGl2bmljaA==')) id = 'business';
  // Owner: legacy "tutoring" income category — render with personal icon
  if (id === 'tutoring' && isOwnerUser()) {
    return { id: 'tutoring', icon: '🎓', en: 'Tutoring', ru: 'Преподавание', type: 'income' };
  }
  return FIN_CATEGORIES.find(c => c.id === id);
}
function fmtMoney(n, signed = false) {
  const v = Math.abs(n).toFixed(2);
  if (signed) return (n > 0 ? '+' : (n < 0 ? '−' : '')) + '€' + v;
  return '€' + v;
}
function getCurrencySymbol(code) {
  return (FIN_CURRENCIES.find(c => c.code === code) || FIN_CURRENCIES[0]).symbol;
}

// ─── BANKS ───
// Default banks created on first finance page load.
// Just two basics — users add their own via "+ Add bank".
const DEFAULT_BANKS = [
  { id: 'bank_revolut', name: 'Revolut', currency: 'EUR', manualBalance: 0, builtin: true },
  { id: 'bank_cash',    name: 'Cash',    currency: 'EUR', manualBalance: 0, builtin: true },
];

function getBanks() {
  const data = loadData();
  const sd = data['finance'] || {};
  if (!sd._banks) {
    // First time — initialize with defaults
    sd._banks = JSON.parse(JSON.stringify(DEFAULT_BANKS));
    data['finance'] = sd;
    saveData(data);
  }
  return sd._banks;
}
function saveBanks(banks) {
  // SAFETY: refuse to overwrite cloud banks with empty list (protects data)
  const cloudSd = cloudSections.get('finance');
  if (cloudSd && Array.isArray(cloudSd._banks) && cloudSd._banks.length > 0
      && (!banks || banks.length === 0)) {
    console.warn('[phosphera] saveBanks REFUSED to overwrite', cloudSd._banks.length, 'banks with empty array');
    return;
  }
  // Use updateSectionData for safe, race-free persistence
  if (typeof updateSectionData === 'function') {
    updateSectionData('finance', { _banks: banks });
  } else {
    // Legacy fallback
    const data = loadData();
    data['finance'] = data['finance'] || {};
    data['finance']._banks = banks;
    saveData(data);
  }
  console.log('[phosphera] Banks saved:', banks.length, 'banks');
}
function getBank(id) { return getBanks().find(b => b.id === id); }

function getFinanceTransactions() {
  const data = loadData();
  const sd = data['finance'];
  if (!sd || !sd.entries) return { txs: [], legacy: [] };
  const txs = []; const legacy = [];
  sd.entries.forEach(e => {
    if (typeof e.amount === 'number' && e.txType) txs.push(e);
    else legacy.push(e);
  });
  txs.sort((a, b) => (b.txDate || b.createdAt) - (a.txDate || a.createdAt));
  return { txs, legacy };
}

// Bank balance = manual baseline + sum of transactions for this bank (all in EUR, raw)
function getBankBalance(bankId) {
  const bank = getBank(bankId);
  if (!bank) return 0;
  let balance = bank.manualBalance || 0;
  const { txs } = getFinanceTransactions();
  txs.forEach(tx => {
    if (tx.bankId !== bankId) return;
    const amt = tx.txType === 'income' ? tx.amount : -tx.amount;
    balance += amt;
  });
  return balance;
}

// Total balance = simple sum of all bank balances (no conversion — user manages in EUR)
function getTotalBalance() {
  let total = 0;
  getBanks().forEach(b => { total += getBankBalance(b.id); });
  return total;
}

function calculateFinanceStats(txs) {
  let monthIncome = 0, monthExpense = 0;
  const byCategory = {};
  const dailyExpenses = {};
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  txs.forEach(tx => {
    const txTime = tx.txDate || tx.createdAt;
    if (txTime >= monthStart) {
      if (tx.txType === 'income') monthIncome += tx.amount;
      else monthExpense += tx.amount;
      if (tx.txType === 'expense') {
        byCategory[tx.category] = (byCategory[tx.category] || 0) + tx.amount;
        const d = new Date(txTime);
        const key = toLocalDateStr(d);
        dailyExpenses[key] = (dailyExpenses[key] || 0) + tx.amount;
      }
    }
  });
  const byCategoryArr = Object.entries(byCategory).map(([cat, total]) => ({ cat, total })).sort((a, b) => b.total - a.total);
  return {
    balance: getTotalBalance(),
    monthIncome,
    monthExpense,
    monthNet: monthIncome - monthExpense,
    byCategory: byCategoryArr,
    dailyExpenses,
  };
}

const KW = {
  employment: ['job','work','apply','applied','interview','placement','vacancy','tesco','dunnes','apple','voxpro','работ','вакансия','отклик','собеседован','резюме'],
  education: ['ucc','university','class','lecture','study','course','exam','assignment','учеб','лекция','экзамен','предмет','сесси'],
  tutoring: ['tutor','teach','preply','italki','lesson','student','препода','ученик','урок','занятие'],
  business: ['adopt me','mm2','tiktok','roblox','каталог','стоки','адопт'],
  certificates: ['certificate','cert','forage','hubspot','google ai','haccp','сертификат','форейдж'],
  volunteering: ['volunteer','society','social democrat','fine gael','committee','волонтер','общество','партия','комитет'],
  sport: ['run','gym','workout','exercise','training','cardio','5km','10km','пробеж','тренир','спорт','качал','зал'],
  nutrition: ['eat','meal','cook','recipe','diet','breakfast','lunch','dinner','еда','есть','готов','рецепт','завтрак','обед','ужин'],
  health: ['health','doctor','sleep','pain','sick','medicine','vitamin','здоров','доктор','врач','спал','болит','лекар'],
  plants: ['plant','water','pot','soil','monstera','grow','seed','растен','полива','горшок','земля','семен'],
  finance: ['euro','€','money','budget','expense','income','spent','paid','salary','rent','деньги','евро','бюджет','зарплат','аренд','оплат'],
  tasks: ['task','todo','remind','need to','must','задач','напомин','нужно','надо','должен'],
  projects: ['project','проект'],
};

function detectDate(textLower) {
  const today = new Date(); today.setHours(0,0,0,0);
  const fmt = (d) => toLocalDateStr(d);
  const addDays = (n) => { const d = new Date(today); d.setDate(d.getDate() + n); return fmt(d); };
  // Use Unicode-aware boundaries via lookarounds (work for cyrillic too)
  // (?<![а-яёa-z0-9_]) and (?![а-яёa-z0-9_]) acts like \b for cyrillic.
  const BL = '(?<![а-яёa-z0-9_])'; // left boundary
  const BR = '(?![а-яёa-z0-9_])';  // right boundary

  const days = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
  const daysRu = ['воскресенье','понедельник','вторник','среда','четверг','пятница','суббота'];
  const daysRuShort = ['вс','пн','вт','ср','чт','пт','сб'];
  const monthsEn = ['january','february','march','april','may','june','july','august','september','october','november','december'];
  const monthsEnShort = ['jan','feb','mar','apr','may','jun','jul','aug','sept?','oct','nov','dec'];
  const monthsRu = ['январ','феврал','март','апрел','ма','июн','июл','август','сентябр','октябр','ноябр','декабр'];
  // ма prefix matches "мая" / "мае" / "май" / "марте"... so handle specifically:
  // build regex like (мая|мае|май) etc
  const monthsRuFull = ['января|январе|январь','февраля|феврале|февраль','марта|марте|март','апреля|апреле|апрель','мая|мае|май','июня|июне|июнь','июля|июле|июль','августа|августе|август','сентября|сентябре|сентябрь','октября|октябре|октябрь','ноября|ноябре|ноябрь','декабря|декабре|декабрь'];

  // 1. Today / tomorrow / day-after-tomorrow / yesterday
  if (new RegExp(BL + '(today|сегодня)' + BR, 'i').test(textLower)) return addDays(0);
  if (new RegExp(BL + '(tomorrow|завтра)' + BR, 'i').test(textLower)) return addDays(1);
  if (/day after tomorrow|послезавтра|после завтра/i.test(textLower)) return addDays(2);
  if (new RegExp(BL + '(yesterday|вчера)' + BR, 'i').test(textLower)) return addDays(-1);

  // 2. "in N days" / "через N дней" / "after N days"
  const inN = textLower.match(/(?:in|after|через)\s+(\d+)\s*(?:days?|дн(?:[ея]й?|и|я)?|day)/i);
  if (inN) return addDays(parseInt(inN[1]));
  // "через неделю" / "in a week"
  if (/через\s+неделю|in\s+a\s+week/i.test(textLower)) return addDays(7);
  if (/через\s+(?:две|2)\s+недел/i.test(textLower)) return addDays(14);
  if (/через\s+месяц|in\s+a\s+month/i.test(textLower)) {
    const d = new Date(today); d.setMonth(d.getMonth() + 1); return fmt(d);
  }

  // 3. "next week / next month / next <day>" / "след неделя / месяц / <day>"
  const nextDayMatch = textLower.match(/next\s+([a-z]+)|след(?:ующ(?:ий|ая|ее|ую|ие)|\.)?\s*([а-яё]+)/i);
  if (nextDayMatch) {
    const word = ((nextDayMatch[1] || nextDayMatch[2]) || '').toLowerCase();
    if (word.includes('week') || word.startsWith('недел')) return addDays(7);
    if (word.includes('month') || word.startsWith('месяц')) {
      const d = new Date(today); d.setMonth(d.getMonth() + 1); return fmt(d);
    }
    for (let i = 0; i < 7; i++) {
      if (word.startsWith(days[i].slice(0, 3)) || word.startsWith(daysRu[i].slice(0, 3)) || word === daysRuShort[i]) {
        let diff = i - today.getDay(); if (diff <= 0) diff += 7;
        return addDays(diff + 7);
      }
    }
  }
  if (/this\s+week|на\s+этой\s+неделе|эту\s+неделю/i.test(textLower)) {
    const diff = (7 - today.getDay()) % 7;
    return addDays(diff || 7);
  }
  if (/this\s+weekend|на\s+выходных|выходные/i.test(textLower)) {
    let diff = 6 - today.getDay(); if (diff < 0) diff += 7;
    return addDays(diff);
  }
  if (/end\s+of\s+(?:the\s+)?month|конец\s+месяца|до\s+конца\s+месяца/i.test(textLower)) {
    const d = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return fmt(d);
  }

  // 4. Day of week. Russian declensions: try matching "<root>(а|у|ой|ом|и|е|ы)?" — covers падежи
  const daysRuRoots = ['воскресень','понедельник','вторник','сред','четверг','пятниц','суббот'];
  for (let i = 0; i < 7; i++) {
    const re = new RegExp(BL + '(' + days[i] + '|' + daysRuRoots[i] + '[а-яё]*|' + daysRuShort[i] + ')' + BR, 'i');
    if (re.test(textLower)) {
      let diff = i - today.getDay(); if (diff <= 0) diff += 7;
      return addDays(diff);
    }
  }

  // 5. Month + day patterns
  // a) "march 15" / "mar 15" / "15 march" / "15 mar"
  for (let i = 0; i < 12; i++) {
    const re1 = new RegExp(BL + '(' + monthsEn[i] + '|' + monthsEnShort[i] + ')\\s+(\\d{1,2})' + BR, 'i');
    const re2 = new RegExp(BL + '(\\d{1,2})\\s+(' + monthsEn[i] + '|' + monthsEnShort[i] + ')' + BR, 'i');
    const m1 = textLower.match(re1);
    if (m1) {
      const day = parseInt(m1[2]);
      if (day >= 1 && day <= 31) {
        let year = today.getFullYear();
        const d = new Date(year, i, day);
        if (d < today) d.setFullYear(year + 1);
        return fmt(d);
      }
    }
    const m2 = textLower.match(re2);
    if (m2) {
      const day = parseInt(m2[1]);
      if (day >= 1 && day <= 31) {
        let year = today.getFullYear();
        const d = new Date(year, i, day);
        if (d < today) d.setFullYear(year + 1);
        return fmt(d);
      }
    }
  }
  // b) Russian: "17 мая" / "17 марта" / "до 17 мая"
  for (let i = 0; i < 12; i++) {
    const re = new RegExp('(\\d{1,2})\\s+(' + monthsRuFull[i] + ')(?![а-яё])', 'i');
    const m = textLower.match(re);
    if (m) {
      const day = parseInt(m[1]);
      if (day >= 1 && day <= 31) {
        let year = today.getFullYear();
        const d = new Date(year, i, day);
        if (d < today) d.setFullYear(year + 1);
        return fmt(d);
      }
    }
  }

  // 6. ISO date
  const iso = textLower.match(/(\d{4})-(\d{2})-(\d{2})/); if (iso) return iso[0];

  // 7. dd.mm or dd/mm or dd-mm
  const dm = textLower.match(/(\d{1,2})[\.\/\-](\d{1,2})(?:[\.\/\-](\d{2,4}))?/);
  if (dm) {
    const day = parseInt(dm[1]), month = parseInt(dm[2]);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      let year = dm[3] ? parseInt(dm[3]) : today.getFullYear();
      if (year < 100) year += 2000;
      const d = new Date(year, month-1, day);
      if (d >= today) return fmt(d);
      // Past in this year and no year specified — assume next year
      if (!dm[3]) {
        d.setFullYear(today.getFullYear() + 1);
        return fmt(d);
      }
      return fmt(d);
    }
  }

  return null;
}

function mockCategorize(text) {
  const lower = text.toLowerCase();
  const allSections = getAllSections();

  // Score each section by:
  //   - keyword matches (KW)
  //   - section name + description word matches
  let scores = {};
  allSections.forEach(s => { scores[s.slug] = 0; });

  // 1. Keyword matches (built-in only)
  for (const [section, kws] of Object.entries(KW)) {
    if (!(section in scores)) continue;
    const score = kws.filter(k => lower.includes(k)).length * 3; // weight 3
    scores[section] += score;
  }

  // 2. Match against section title and description (works for custom sections too)
  allSections.forEach(s => {
    const haystack = `${s.titleEn || ''} ${s.titleRu || ''} ${s.descEn || ''} ${s.descRu || ''}`.toLowerCase();
    const haystackWords = haystack.split(/[\s,.\-—]+/).filter(w => w.length >= 4);
    const noteWords = lower.split(/[\s,.\-—]+/).filter(w => w.length >= 4);
    haystackWords.forEach(hw => {
      if (noteWords.some(nw => nw === hw || (hw.length > 5 && nw.startsWith(hw.slice(0, 5))))) {
        scores[s.slug] += 2; // weight 2
      }
    });
  });

  // 3. Match against subtopics — if a subtopic name appears, big boost for that section
  let bestSubtopic = null;
  allSections.forEach(s => {
    const sd = getSectionData(s.slug);
    if (!sd.subtopics || sd.subtopics.length === 0) return;
    sd.subtopics.forEach(st => {
      const stName = (st.name || '').toLowerCase().trim();
      if (!stName) return;
      if (lower.includes(stName)) {
        scores[s.slug] += 5; // weight 5 — strong signal
        if (!bestSubtopic || bestSubtopic.weight < 5) bestSubtopic = { section: s.slug, id: st.id, weight: 5 };
      } else {
        const stWords = stName.split(/\s+/).filter(w => w.length >= 4);
        if (stWords.length > 0 && stWords.some(w => lower.includes(w))) {
          scores[s.slug] += 3;
          if (!bestSubtopic || bestSubtopic.weight < 3) bestSubtopic = { section: s.slug, id: st.id, weight: 3 };
        }
      }
    });
  });

  // Pick best section
  let best = { section: 'notes', score: 0 };
  for (const [section, score] of Object.entries(scores)) {
    if (score > best.score) best = { section, score };
  }
  // Fallback: 'notes' if available, else first section
  if (best.score === 0) {
    if (allSections.find(s => s.slug === 'notes')) best.section = 'notes';
    else if (allSections.length > 0) best.section = allSections[0].slug;
  }

  // Subtopic from best match (only if it belongs to chosen section)
  let subtopicId = null;
  if (bestSubtopic && bestSubtopic.section === best.section) {
    subtopicId = bestSubtopic.id;
  }

  return { section: best.section, deadline: detectDate(lower), subtopicId };
}

function mockBulkSplit(text) {
  const lines = text.split(/\n+|(?:^|\s)[•·\-\*]\s+/).map(l => l.trim()).filter(l => l.length > 5);
  return lines.map(line => ({ text: line, ...mockCategorize(line) }));
}

async function claudeCategorize(text) {
  const apiKey = localStorage.getItem('phosphera-anthropic-key');
  if (!apiKey) return mockCategorize(text);
  // Use getAllSections so CUSTOM sections (added by user) are available for AI categorization
  const allSections = getAllSections();
  const sectionsList = allSections.map(s => {
    const sd = getSectionData(s.slug);
    const subs = (sd.subtopics || []).map(st => `${st.id}:"${st.name}"`).join(', ');
    const desc = lang === 'en' ? (s.descEn || s.titleEn) : (s.descRu || s.titleRu);
    return `- ${s.slug}: ${s.titleEn} — ${desc}${subs ? ' [subtopics: ' + subs + ']' : ''}`;
  }).join('\n');
  const today = toLocalDateStr();
  const prompt = `Today is ${today}. Available sections (with subtopics):\n${sectionsList}\n\nUser note: """${text}"""\n\nCategorize this into ONE section slug. If a subtopic from that section clearly matches, include its id. Detect a deadline if mentioned (return as YYYY-MM-DD or null).\nRespond ONLY with a single JSON object, no markdown:\n{"section":"slug","subtopicId":"id or null","deadline":"YYYY-MM-DD or null"}`;
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
      body: JSON.stringify({ model: 'claude-haiku-4-5', max_tokens: 200, messages: [{ role: 'user', content: prompt }] })
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    const responseText = data.content[0].text.replace(/```json|```/g, '').trim();
    const parsed = JSON.parse(responseText);
    // Validate section exists (AI sometimes hallucinates) — fallback to mock if not
    const validSlugs = new Set(allSections.map(s => s.slug));
    if (!validSlugs.has(parsed.section)) {
      console.warn('[phosphera] Claude returned unknown section:', parsed.section, '— falling back to mock');
      return mockCategorize(text);
    }
    return {
      section: parsed.section,
      subtopicId: parsed.subtopicId === 'null' || !parsed.subtopicId ? null : parsed.subtopicId,
      deadline: parsed.deadline === 'null' || !parsed.deadline ? null : parsed.deadline
    };
  } catch (err) { console.warn('Claude API:', err); return mockCategorize(text); }
}

async function claudeBulkSplit(text) {
  const apiKey = localStorage.getItem('phosphera-anthropic-key');
  if (!apiKey) return mockBulkSplit(text);
  const allSections = getAllSections();
  const sectionsList = allSections.map(s => `- ${s.slug}: ${s.titleEn}${s.descEn ? ' — ' + s.descEn : ''}`).join('\n');
  const today = toLocalDateStr();
  const prompt = `Today is ${today}. Available sections:\n${sectionsList}\n\nUser pasted multiple notes:\n"""${text}"""\n\nSplit into individual notes (one note per item). For each, categorize and detect deadline.\nRespond ONLY with a JSON array:\n[{"text":"...","section":"slug","deadline":"YYYY-MM-DD or null"}, ...]`;
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
      body: JSON.stringify({ model: 'claude-haiku-4-5', max_tokens: 1500, messages: [{ role: 'user', content: prompt }] })
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    const responseText = data.content[0].text.replace(/```json|```/g, '').trim();
    const parsed = JSON.parse(responseText);
    return parsed.map(p => ({ ...p, deadline: p.deadline === 'null' || !p.deadline ? null : p.deadline }));
  } catch (err) { console.warn('Claude API:', err); return mockBulkSplit(text); }
}

// ─── ASSISTANT MODE — full Claude with notes context + tool actions ───
function buildAssistantContext() {
  // ─── OPTIMIZED CONTEXT ───
  // Send only what's actually useful for current-state reasoning:
  //   - All OPEN tasks/notes (no time filter — user might ask about old open items)
  //   - All notes with upcoming deadlines (next 60 days)
  //   - Recent activity (last 30 days, including done items)
  //   - DONE in last 7 days (so AI knows what was just completed)
  // Older done items / archived notes → not included. AI uses search_notes tool
  // to dig into archive when needed (saves ~80% of tokens vs full dump).
  const data = loadData();
  const today = toLocalDateStr();
  const now = Date.now();
  const sections = getOrderedSections();
  const DAY = 86400000;
  const upcomingHorizon = 60 * DAY;
  const recentHorizon = 30 * DAY;
  const doneRecentHorizon = 7 * DAY;

  let context = `Today is ${today}.\n\nThe user is using phosphera, a personal life-organization app.\n\n`;
  context += `## Available sections:\n`;
  sections.forEach(s => {
    const title = lang === 'en' ? s.titleEn : s.titleRu;
    const desc = lang === 'en' ? s.descEn : s.descRu;
    context += `- **${s.slug}** (${s.icon} ${title}): ${desc}\n`;
  });
  context += `\n## Relevant user notes (open + recent + upcoming):\n`;
  context += `_Note: archived/older done notes are NOT included here. Use the search_notes tool to find them._\n`;
  let totalEntries = 0;
  let excludedCount = 0;
  sections.forEach(s => {
    const sd = data[s.slug];
    if (!sd || !sd.entries || sd.entries.length === 0) return;

    // Filter entries by relevance
    const relevant = sd.entries.filter(e => {
      // 1. All open items
      if (!e.done) return true;
      // 2. Done within last 7 days
      const created = e.mtime || e.createdAt || 0;
      if (now - created <= doneRecentHorizon) return true;
      return false;
    }).filter(e => {
      // Also include if there's an upcoming deadline within 60 days
      if (e.deadline) {
        const dl = parseDeadline(e.deadline).getTime();
        if (dl - now <= upcomingHorizon && dl >= now - DAY) return true;
      }
      // Include if created recently (last 30 days) regardless of done
      const created = e.mtime || e.createdAt || 0;
      if (now - created <= recentHorizon) return true;
      // Open items always pass (first filter already enforced)
      if (!e.done) return true;
      return false;
    });

    excludedCount += sd.entries.length - relevant.length;
    if (relevant.length === 0) return;

    const title = lang === 'en' ? s.titleEn : s.titleRu;
    context += `\n### ${s.icon} ${title} (${s.slug}):\n`;
    if (sd.subtopics && sd.subtopics.length > 0) {
      context += `Subtopics: ${sd.subtopics.map(st => `"${st.name}" (id:${st.id})`).join(', ')}\n`;
    }
    relevant.forEach(e => {
      const status = e.done ? '[DONE]' : '[OPEN]';
      const deadline = e.deadline ? ` deadline:${e.deadline}` : '';
      const created = toLocalDateStr(new Date(e.createdAt));
      const subName = e.subtopicId && sd.subtopics ? (sd.subtopics.find(st => st.id === e.subtopicId)?.name || '') : '';
      const subPart = subName ? ` sub:"${subName}"(${e.subtopicId})` : '';
      const text = e.text.replace(/\n/g, ' ').slice(0, 200);
      context += `- id:${e.id} ${status}${deadline}${subPart} created:${created} — ${text}\n`;
      totalEntries++;
    });
  });
  if (totalEntries === 0) context += `\n(No active notes — user is starting fresh or all archived.)\n`;
  if (excludedCount > 0) {
    context += `\n_(${excludedCount} older/archived notes not shown — search_notes tool can find them.)_\n`;
  }
  return context;
}

const ASSISTANT_TOOLS = [
  {
    name: "search_notes",
    description: "Search through user's notes by keyword/topic and return matches. Use when the user asks 'what did I write about X' or 'find my notes on Y'.",
    input_schema: {
      type: "object",
      properties: {
        query: { type: "string", description: "Keyword or phrase to search for" },
        section: { type: "string", description: "Optional: limit to one section slug" }
      },
      required: ["query"]
    }
  },
  {
    name: "add_note",
    description: "Create a new note in any section EXCEPT finance. For money/expenses/income use add_transaction instead. Use when user explicitly asks to add/create/record something.",
    input_schema: {
      type: "object",
      properties: {
        section: { type: "string", description: "Section slug (e.g. 'employment', 'tasks'). Do NOT use for finance — use add_transaction." },
        text: { type: "string", description: "The note content" },
        deadline: { type: "string", description: "Optional deadline. Use YYYY-MM-DD for a plain date, or YYYY-MM-DDTHH:MM when the user gives (or implies) a specific time — e.g. 'English at 6pm' → today'sdate + 'T18:00'. Prefer including a time whenever one is known: entries with a time show up as scheduled blocks on the user's Calendar; date-only entries only show as all-day chips." },
        duration_min: { type: "number", description: "Optional duration in minutes, only meaningful when deadline includes a time (controls how tall/long the block looks on the Calendar). Default 30 if omitted." }
      },
      required: ["section", "text"]
    }
  },
  {
    name: "update_note",
    description: "Update an existing note's text, deadline, done status, section, or subtopic. Use the note id from context.",
    input_schema: {
      type: "object",
      properties: {
        id: { type: "string", description: "Note id from context" },
        section: { type: "string", description: "Current section slug of the note" },
        new_text: { type: "string", description: "New text (optional, only if changing)" },
        new_deadline: { type: "string", description: "New deadline — YYYY-MM-DD, or YYYY-MM-DDTHH:MM to include a specific time (shows as a scheduled block on the Calendar instead of an all-day chip). Use 'null' to remove." },
        new_section: { type: "string", description: "Move to a different section (optional)" },
        new_subtopic_id: { type: "string", description: "Move to a subtopic within the same section (use subtopic id from list_subtopics, or 'null' to remove subtopic)" },
        done: { type: "boolean", description: "Mark as done/undone" }
      },
      required: ["id", "section"]
    }
  },
  {
    name: "list_subtopics",
    description: "Get all subtopics in a section with their ids and names. Use this BEFORE moving notes to a subtopic so you know the right subtopic id.",
    input_schema: {
      type: "object",
      properties: {
        section: { type: "string", description: "Section slug" }
      },
      required: ["section"]
    }
  },
  {
    name: "bulk_update",
    description: "Apply the same change to multiple notes at once. Far more efficient than calling update_note many times. Use when user says 'move all X to Y', 'mark all done', 'add deadline to all in section', etc. Filter notes via either explicit ids OR (section + optional exclude_ids + optional only_open).",
    input_schema: {
      type: "object",
      properties: {
        ids: { type: "array", items: { type: "string" }, description: "Explicit list of note ids. If provided, exclude_ids/section_filter/only_open are ignored." },
        section_filter: { type: "string", description: "Apply to all notes in this section. Combine with exclude_ids to skip some." },
        exclude_ids: { type: "array", items: { type: "string" }, description: "Note ids to skip when using section_filter." },
        only_open: { type: "boolean", description: "If true with section_filter, only affect not-done notes." },
        new_section: { type: "string", description: "Move all matched notes to this section." },
        new_subtopic_id: { type: "string", description: "Move all matched notes to this subtopic id (within their current section, unless new_section is also set). Use 'null' to remove subtopic." },
        new_deadline: { type: "string", description: "Set deadline — YYYY-MM-DD, or YYYY-MM-DDTHH:MM for a specific time. Use 'null' to remove." },
        done: { type: "boolean", description: "Mark all matched notes done/undone." }
      }
    }
  },
  {
    name: "delete_note",
    description: "Permanently delete a note. Always confirm with user before calling this.",
    input_schema: {
      type: "object",
      properties: {
        id: { type: "string", description: "Note id" },
        section: { type: "string", description: "Section slug" }
      },
      required: ["id", "section"]
    }
  },
  {
    name: "list_todos",
    description: "Get a list of all open tasks with deadlines, sorted by urgency. Use when user asks 'what's on my plate' or 'today's tasks'.",
    input_schema: { type: "object", properties: {} }
  },
  {
    name: "add_transaction",
    description: "Record a financial transaction (income or expense) in the Finance section. All amounts in EUR. Use when user says 'I spent X on Y' or 'I earned X' or 'add expense'.",
    input_schema: {
      type: "object",
      properties: {
        amount: { type: "number", description: "Amount in numbers (positive). Treat as EUR by default." },
        txType: { type: "string", enum: ["income", "expense"], description: "income or expense" },
        category: { type: "string", description: "Category id: rent, groceries, transport, food_out, fun, health, education, subscr, shopping, misc_exp (for expenses) | salary, business, gift, misc_inc (for income)" },
        currency: { type: "string", description: "Optional: EUR, USD, UAH, RUB, TON. Default EUR." },
        bankId: { type: "string", description: "Optional bank id (e.g. bank_revolut, bank_cash). Defaults to first bank." },
        note: { type: "string", description: "Optional note about the transaction" },
        date: { type: "string", description: "Optional YYYY-MM-DD; default today" }
      },
      required: ["amount", "txType", "category"]
    }
  },
  {
    name: "finance_stats",
    description: "Get current finance stats: balance, this-month income/expense, top categories. Use when user asks about money, spending, balance.",
    input_schema: { type: "object", properties: {} }
  }
];

function executeAssistantTool(toolName, input) {
  // Returns a string result that gets sent back to Claude as tool_result
  try {
    if (toolName === 'search_notes') {
      const data = loadData();
      const q = (input.query || '').toLowerCase();
      const matches = [];
      const targetSections = input.section ? [input.section] : getAllSections().map(s => s.slug);
      targetSections.forEach(slug => {
        const sd = data[slug]; if (!sd || !sd.entries) return;
        sd.entries.forEach(e => {
          if (e.text.toLowerCase().includes(q)) matches.push({ id: e.id, section: slug, text: e.text, deadline: e.deadline, done: !!e.done });
        });
      });
      return JSON.stringify({ found: matches.length, notes: matches.slice(0, 20) });
    }
    if (toolName === 'add_note') {
      const sec = getSection(input.section);
      if (!sec) return JSON.stringify({ error: `Section "${input.section}" not found.` });
      const d = getSectionData(input.section);
      const id = Date.now().toString() + Math.random().toString(36).slice(2, 5);
      d.entries = [{ id, text: input.text, createdAt: Date.now(), deadline: input.deadline || null, durationMin: input.duration_min || null, done: false, subtopicId: null }, ...(d.entries || [])];
      updateSectionData(input.section, d);
      return JSON.stringify({ ok: true, message: `Added to ${sec.icon} ${sec.titleEn}`, id });
    }
    if (toolName === 'update_note') {
      const d = getSectionData(input.section);
      if (!d.entries) return JSON.stringify({ error: 'Section has no entries.' });
      const entry = d.entries.find(e => e.id === input.id);
      if (!entry) return JSON.stringify({ error: 'Note not found.' });
      // Cross-section move
      if (input.new_section && input.new_section !== input.section) {
        d.entries = d.entries.filter(e => e.id !== input.id);
        updateSectionData(input.section, d);
        const newD = getSectionData(input.new_section);
        const updated = { ...entry };
        if (input.new_text !== undefined) updated.text = input.new_text;
        if (input.new_deadline !== undefined) updated.deadline = input.new_deadline === 'null' ? null : input.new_deadline;
        if (input.done !== undefined) updated.done = input.done;
        // When moving across sections, subtopic must come from the NEW section (or be cleared)
        if (input.new_subtopic_id !== undefined) updated.subtopicId = input.new_subtopic_id === 'null' ? null : input.new_subtopic_id;
        else updated.subtopicId = null; // clear by default — old subtopic id is invalid in new section
        newD.entries = [updated, ...(newD.entries || [])];
        updateSectionData(input.new_section, newD);
        return JSON.stringify({ ok: true, message: `Moved note to ${input.new_section}` });
      }
      d.entries = d.entries.map(e => {
        if (e.id !== input.id) return e;
        const updated = { ...e };
        if (input.new_text !== undefined) updated.text = input.new_text;
        if (input.new_deadline !== undefined) updated.deadline = input.new_deadline === 'null' ? null : input.new_deadline;
        if (input.done !== undefined) updated.done = input.done;
        if (input.new_subtopic_id !== undefined) updated.subtopicId = input.new_subtopic_id === 'null' ? null : input.new_subtopic_id;
        return updated;
      });
      updateSectionData(input.section, d);
      return JSON.stringify({ ok: true, message: 'Note updated' });
    }
    if (toolName === 'list_subtopics') {
      const d = getSectionData(input.section);
      const subs = (d.subtopics || []).map(st => ({ id: st.id, name: st.name }));
      return JSON.stringify({ section: input.section, count: subs.length, subtopics: subs });
    }
    if (toolName === 'bulk_update') {
      // Determine target notes
      let targets = []; // [{section, id}]
      if (input.ids && input.ids.length > 0) {
        // Find each id by scanning all sections
        const data = loadData();
        Object.keys(data).forEach(secSlug => {
          const sd = data[secSlug];
          if (!sd || !sd.entries) return;
          sd.entries.forEach(e => { if (input.ids.includes(e.id)) targets.push({ section: secSlug, id: e.id }); });
        });
      } else if (input.section_filter) {
        const d = getSectionData(input.section_filter);
        const exclude = new Set(input.exclude_ids || []);
        (d.entries || []).forEach(e => {
          if (exclude.has(e.id)) return;
          if (input.only_open && e.done) return;
          targets.push({ section: input.section_filter, id: e.id });
        });
      } else {
        return JSON.stringify({ error: 'Specify either ids[] or section_filter' });
      }
      if (targets.length === 0) {
        return JSON.stringify({ ok: true, message: 'No matching notes found', updated: 0 });
      }
      // Apply changes
      let updated = 0;
      // Group by source section for efficiency
      const bySection = {};
      targets.forEach(t => {
        bySection[t.section] = bySection[t.section] || new Set();
        bySection[t.section].add(t.id);
      });
      Object.keys(bySection).forEach(srcSlug => {
        const ids = bySection[srcSlug];
        const d = getSectionData(srcSlug);
        if (input.new_section && input.new_section !== srcSlug) {
          // Move out of this section
          const movingOut = (d.entries || []).filter(e => ids.has(e.id)).map(e => {
            const updatedE = { ...e };
            if (input.new_subtopic_id !== undefined) updatedE.subtopicId = input.new_subtopic_id === 'null' ? null : input.new_subtopic_id;
            if (input.new_deadline !== undefined) updatedE.deadline = input.new_deadline === 'null' ? null : input.new_deadline;
            if (input.done !== undefined) updatedE.done = input.done;
            return updatedE;
          });
          d.entries = (d.entries || []).filter(e => !ids.has(e.id));
          updateSectionData(srcSlug, d);
          const targetD = getSectionData(input.new_section);
          targetD.entries = [...movingOut, ...(targetD.entries || [])];
          updateSectionData(input.new_section, targetD);
          updated += movingOut.length;
        } else {
          // In-place update
          d.entries = (d.entries || []).map(e => {
            if (!ids.has(e.id)) return e;
            const updatedE = { ...e };
            if (input.new_subtopic_id !== undefined) updatedE.subtopicId = input.new_subtopic_id === 'null' ? null : input.new_subtopic_id;
            if (input.new_deadline !== undefined) updatedE.deadline = input.new_deadline === 'null' ? null : input.new_deadline;
            if (input.done !== undefined) updatedE.done = input.done;
            updated++;
            return updatedE;
          });
          updateSectionData(srcSlug, d);
        }
      });
      return JSON.stringify({ ok: true, message: `Updated ${updated} note(s)`, updated });
    }
    if (toolName === 'delete_note') {
      const d = getSectionData(input.section);
      d.entries = (d.entries || []).filter(e => e.id !== input.id);
      updateSectionData(input.section, d);
      return JSON.stringify({ ok: true, message: 'Note deleted' });
    }
    if (toolName === 'list_todos') {
      const todos = getAllTodos().filter(t => !t.done);
      return JSON.stringify({ count: todos.length, todos: todos.slice(0, 30).map(t => ({ id: t.id, section: t.sectionSlug, text: t.text, deadline: t.deadline, urgency: getUrgency(t.deadline) })) });
    }
    if (toolName === 'add_transaction') {
      const cat = getFinCategory(input.category);
      if (!cat) return JSON.stringify({ error: `Unknown category "${input.category}"` });
      if (input.txType !== 'income' && input.txType !== 'expense') {
        return JSON.stringify({ error: 'txType must be "income" or "expense"' });
      }
      const txDate = input.date ? new Date(input.date + 'T12:00:00').getTime() : Date.now();
      const banks = getBanks();
      const bankId = input.bankId && banks.find(b => b.id === input.bankId) ? input.bankId : (banks[0]?.id || null);
      const d = getSectionData('finance');
      d.entries = [{
        id: Date.now().toString() + Math.random().toString(36).slice(2, 5),
        text: input.note || cat.en,
        createdAt: Date.now(),
        txDate,
        txType: input.txType,
        amount: Math.abs(input.amount),
        currency: input.currency || 'EUR',
        category: input.category,
        bankId,
        note: input.note || '',
        done: false,
      }, ...(d.entries || [])];
      updateSectionData('finance', d);
      const bankName = bankId ? (getBank(bankId)?.name || '') : '';
      return JSON.stringify({ ok: true, message: `Recorded ${input.txType} €${input.amount} (${cat.en})${bankName ? ' on ' + bankName : ''}` });
    }
    if (toolName === 'finance_stats') {
      const { txs } = getFinanceTransactions();
      const stats = calculateFinanceStats(txs);
      const sym = getFinSymbol();
      return JSON.stringify({
        balance: `${sym}${stats.balance.toFixed(2)}`,
        thisMonth: { income: `${sym}${stats.monthIncome.toFixed(2)}`, expense: `${sym}${stats.monthExpense.toFixed(2)}`, net: `${sym}${stats.monthNet.toFixed(2)}` },
        topCategories: stats.byCategory.slice(0, 5).map(c => ({
          category: getFinCategory(c.cat)?.en || c.cat,
          amount: `${sym}${c.total.toFixed(2)}`
        })),
        totalTransactions: txs.length
      });
    }
    return JSON.stringify({ error: 'Unknown tool' });
  } catch (err) {
    return JSON.stringify({ error: String(err.message || err) });
  }
}

async function claudeAssistant(userMessage, conversationHistory, image) {
  const apiKey = localStorage.getItem('phosphera-anthropic-key');
  if (!apiKey) {
    return { text: lang === 'en'
      ? 'AI assistant requires an Anthropic API key. Add one in chat settings ⚙ to use full assistant features.'
      : 'AI-помощнику нужен API-ключ Anthropic. Добавь его в настройках чата ⚙.', changed: false };
  }
  const systemPrompt = `${buildAssistantContext()}

## Your role:
You are phosphera's personal AI assistant — not a generic chatbot, an executive layer for things the user already decided to do. You can:
- Answer questions about the user's notes ("what did I write about X?")
- Say plainly what's due, overdue, or next — based on real deadlines in the context above, not vibes
- Take actions when explicitly asked (add/edit/delete notes, change deadlines, mark done, move between sections or subtopics)

## Tone — this matters as much as the actions:
- Be direct. Say the thing, don't cushion it with "no worries!" or "great question!".
- Deadlines are commitments, not suggestions. If something is overdue, say so plainly instead of soft-pedaling it into a gentle reminder.
- If the user asks to push back a deadline "again" for a vague reason, you can note that plainly ("this is the second time this moved") — but never refuse the action, guilt-trip, or moralize. State the fact once, then do what they asked.
- Never gamify (no streak-shaming, no "you failed" language, no praise for basic completion). Flat, respectful, adult tone either way.
- If the user mentions being sick, exhausted, or something urgent came up, take that at face value — that's a real reason, not something to interrogate.

## Scheduling:
- When a task has (or implies) a specific time — "English at 6", "call at 14:30" — set deadline as YYYY-MM-DDTHH:MM, not just a date, and set duration_min if you can reasonably infer one (default assumption ~30-60 min for most tasks if truly unknown). This is what makes it show up correctly as a timed block on the user's Calendar screen instead of a vague all-day chip.
- If the user shares a photo (schedule screenshot, handwritten note, receipt, whiteboard), read it and act on it the same way you would typed text — extract tasks/deadlines/times from it directly rather than asking them to retype it.

## Important rules:
- Be concise. The user values brevity. 2-3 sentences usually enough unless they ask for details.
- Use tools when actually needed. Don't call search_notes if the answer is clear from the context above (you already have all notes and subtopics).
- Before destructive actions (delete_note), explicitly confirm in your reply.
- Match the user's language (English or Russian).
- For bulk operations ("move all X to subtopic Y", "mark all done", "delete done older than X"), ALWAYS use bulk_update — it's much more efficient than calling update_note many times.
- To move notes to a subtopic: subtopic ids are in the context (sub:"name"(id) format) — use them with new_subtopic_id.
- For complex multi-step requests ("move all X to Y except Z, mark W done"), explain your plan in 1 sentence then execute with bulk_update + exclude_ids.`;

  // Build messages array — include conversation history
  const userContent = image
    ? [
        { type: 'image', source: { type: 'base64', media_type: image.mediaType, data: image.base64 } },
        { type: 'text', text: userMessage || (lang === 'en' ? 'What is this?' : 'Что на этом фото?') }
      ]
    : userMessage;
  const messages = [...conversationHistory, { role: 'user', content: userContent }];

  let didChange = false;
  let finalText = '';

  // Loop for tool use (max 5 iterations to prevent runaway)
  for (let i = 0; i < 8; i++) {
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        },
        body: JSON.stringify({
          model: 'claude-haiku-4-5',
          max_tokens: 1500,
          system: systemPrompt,
          tools: ASSISTANT_TOOLS,
          messages: messages
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);

      // Collect any text content
      const textBlocks = (data.content || []).filter(c => c.type === 'text').map(c => c.text);
      if (textBlocks.length) finalText = textBlocks.join('\n\n');

      // Check for tool use
      const toolUseBlocks = (data.content || []).filter(c => c.type === 'tool_use');
      if (toolUseBlocks.length === 0) {
        // No more tools — done
        break;
      }

      // Add Claude's response (with tool calls) to messages
      messages.push({ role: 'assistant', content: data.content });

      // Execute each tool and build user message with results
      const toolResults = [];
      for (const tu of toolUseBlocks) {
        const result = executeAssistantTool(tu.name, tu.input);
        // Mark that something changed (for UI refresh)
        if (['add_note', 'update_note', 'delete_note'].includes(tu.name)) didChange = true;
        toolResults.push({
          type: 'tool_result',
          tool_use_id: tu.id,
          content: result
        });
      }
      messages.push({ role: 'user', content: toolResults });
      // Loop will send another request and Claude will respond after seeing results
    } catch (err) {
      console.warn('Claude assistant error:', err);
      return { text: (lang === 'en' ? 'Error: ' : 'Ошибка: ') + (err.message || err), changed: false };
    }
  }

  return { text: finalText || (lang === 'en' ? '(no response)' : '(нет ответа)'), changed: didChange };
}

const T = {
  en: {
    appName: 'phosphera', appTagline: 'my eternal north',
    dashboard: 'Dashboard', dashboardWelcome: 'Welcome back',
    taglineLine: 'The sky remembers everything',
    addEntry:'Add', addLink:'Add link',
    entryPlaceholder:'Write your note, idea, plan...',
    linkTitle:'Link title', linkUrl:'https://...',
    save:'Save', cancel:'Cancel', delete:'Delete', confirmDelete:'Delete this?',
    print:'Print', entries:'Entries', links:'Links',
    noEntries:'No entries yet.', noLinks:'No links yet.', sectionsCount:'sections', openLink:'Open',
    all: 'All', addSubtopic: '+ subtopic', subtopicName: 'Subtopic name',
    deleteSubtopic: 'Delete subtopic? Entries inside will move to "All".',
    noSubtopic: '— none —', deadline: 'Deadline (optional)',
    todo: 'WITH DEADLINES', todoEmpty: 'Nothing on the calendar.',
    todoTaskPlaceholder: 'Write a note or task...',
    todoOpen: 'OPEN TASKS', todoOpenEmpty: 'No open notes/tasks across sections.',
    edit: 'Edit', saveEdit: 'Save',
    overdue: 'Overdue', todayLabel: 'Today', tomorrowLabel: 'Tomorrow',
    previewNote:'PREVIEW MODE — data stored in browser only.',
    signIn: 'Continue with Google', signOut: 'Sign out', skipPreview: 'Skip — preview mode',
    chatTitle: 'AI assistant', chatPlaceholder: 'Tell me a note, plan, or idea...',
    chatLeadHint: 'Tell me anything — I\'ll suggest where to file it.',
    chatExamples: ['Applied to a job today, follow up Friday','Ran 5km this morning','Bought a plant, water once a week','Idea: launch a side project'],
    chatSection: 'Section', chatDeadline: 'Deadline', chatNoDate: 'no deadline',
    chatAddIt: 'Add it', chatCancel: 'Cancel',
    chatAdded: 'Added to', chatThinking: 'Thinking',
    chatModeMock: 'Preview / mock — keyword categorization', chatModeLive: 'Connected to Claude API',
    chatSettings: 'Settings', chatBack: 'Back',
    chatApiKeyLabel: 'Anthropic API key',
    chatApiKeyHint: 'Without a key, the chat uses keyword matching (mock). With a key, it uses real Claude API. Get one at console.anthropic.com → API Keys. Stored only in this browser.',
    chatSaveKey: 'Save key', chatRemoveKey: 'Remove key',
    aiQuickTitle: 'Quick add', aiQuickPlaceholder: 'Type a note, AI sorts it...',
    aiQuickAdd: 'Add',
    aiBulkTitle: 'Bulk sort', aiBulkPlaceholder: 'Paste a journal, list, or many notes — AI splits them...',
    aiBulkProcess: 'Process all', aiBulkAddAll: 'Add all', aiBulkClear: 'Clear',
    customize: 'Customize tiles', customizeHint: 'Drag to reorder · Tap eye to hide/show · Tap pencil to rename',
    rename: 'Rename', resetTiles: 'Reset', addTile: 'Add new tile', tilesHidden: 'Hidden tiles',
    finBalance: 'Balance', finIncome: 'Income', finExpense: 'Expense', finNet: 'Net',
    finThisMonth: 'This month', finAddTx: 'Add transaction', finTxType: 'Type',
    finIncomeType: 'Income', finExpenseType: 'Expense', finAmount: 'Amount', finCategory: 'Category',
    finNote: 'Note (optional)', finDate: 'Date', finCurrency: 'Currency',
    finTopCategories: 'Top categories', finRecent: 'Recent', finNoTx: 'No transactions yet. Add your first one above.',
    finLegacy: 'Legacy notes (no amount)', finDelete: 'Delete', finEdit: 'Edit',
    finStats: 'Statistics', finDailyChart: 'Daily expenses (this month)',
    done: 'Done',
  },
  ru: {
    appName: 'phosphera', appTagline: 'мой вечный север',
    dashboard: 'Доска', dashboardWelcome: 'С возвращением',
    taglineLine: 'Бог всё видит',
    addEntry:'Добавить', addLink:'Добавить ссылку',
    entryPlaceholder:'Запиши идею, заметку, план...',
    linkTitle:'Название ссылки', linkUrl:'https://...',
    save:'Сохранить', cancel:'Отмена', delete:'Удалить', confirmDelete:'Удалить это?',
    print:'Печать', entries:'Записи', links:'Ссылки',
    noEntries:'Пока пусто.', noLinks:'Ссылок пока нет.', sectionsCount:'разделов', openLink:'Открыть',
    all: 'Все', addSubtopic: '+ подтема', subtopicName: 'Название подтемы',
    deleteSubtopic: 'Удалить подтему? Записи внутри перейдут в "Все".',
    noSubtopic: '— общее —', deadline: 'Срок (необязательно)',
    todo: 'СО СРОКАМИ', todoEmpty: 'Календарь пуст.',
    todoTaskPlaceholder: 'Заметка или задача...',
    todoOpen: 'ОТКРЫТЫЕ', todoOpenEmpty: 'Нет открытых заметок/задач по разделам.',
    edit: 'Изменить', saveEdit: 'Сохранить',
    overdue: 'Просрочено', todayLabel: 'Сегодня', tomorrowLabel: 'Завтра',
    previewNote:'ПРЕВЬЮ — данные только в браузере.',
    signIn: 'Войти через Google', signOut: 'Выйти', skipPreview: 'Пропустить — превью',
    chatTitle: 'AI помощник', chatPlaceholder: 'Расскажи заметку, план или идею...',
    chatLeadHint: 'Просто напиши что угодно — я подскажу куда это положить.',
    chatExamples: ['Подался на работу, ответ к пятнице','Сегодня пробежал 5км','Купил растение, поливать раз в неделю','Идея: запустить сайд-проект'],
    chatSection: 'Раздел', chatDeadline: 'Срок', chatNoDate: 'без срока',
    chatAddIt: 'Добавить', chatCancel: 'Отмена',
    chatAdded: 'Добавлено в', chatThinking: 'Думаю',
    chatModeMock: 'Превью / мок — по ключевым словам', chatModeLive: 'Подключено к Claude API',
    chatSettings: 'Настройки', chatBack: 'Назад',
    chatApiKeyLabel: 'API-ключ Anthropic',
    chatApiKeyHint: 'Без ключа — категоризация по ключевым словам (мок). С ключом — настоящий Claude API. Получить можно на console.anthropic.com → API Keys. Хранится только в этом браузере.',
    chatSaveKey: 'Сохранить ключ', chatRemoveKey: 'Удалить ключ',
    aiQuickTitle: 'Быстро добавить', aiQuickPlaceholder: 'Напиши заметку, AI разберёт...',
    aiQuickAdd: 'Добавить',
    aiBulkTitle: 'Bulk-разбор', aiBulkPlaceholder: 'Вставь дневник, список или много заметок — AI разделит...',
    aiBulkProcess: 'Обработать всё', aiBulkAddAll: 'Добавить все', aiBulkClear: 'Очистить',
    customize: 'Настроить плитки', customizeHint: 'Тяни чтобы переставить · Глаз — скрыть/показать · Карандаш — переименовать',
    rename: 'Переименовать', resetTiles: 'Сброс', addTile: 'Добавить плитку', tilesHidden: 'Скрытые',
    finBalance: 'Баланс', finIncome: 'Доход', finExpense: 'Расход', finNet: 'Итого',
    finThisMonth: 'В этом месяце', finAddTx: 'Добавить транзакцию', finTxType: 'Тип',
    finIncomeType: 'Доход', finExpenseType: 'Расход', finAmount: 'Сумма', finCategory: 'Категория',
    finNote: 'Заметка (необязательно)', finDate: 'Дата', finCurrency: 'Валюта',
    finTopCategories: 'Топ категорий', finRecent: 'Недавние', finNoTx: 'Транзакций пока нет. Добавь первую сверху.',
    finLegacy: 'Старые заметки (без суммы)', finDelete: 'Удалить', finEdit: 'Изменить',
    finStats: 'Статистика', finDailyChart: 'Расходы по дням (этот месяц)',
    done: 'Готово',
  }
};

const FX_OPTIONS = ['aurora', 'rain', 'stars'];
const FX_ICONS = { aurora: '🌌', rain: '🌧', stars: '✨' };

let lang = localStorage.getItem('phosphera-lang') || 'en';
let theme = localStorage.getItem('phosphera-theme') || 'dark';
let fx = localStorage.getItem('phosphera-fx') || 'aurora';
// Font preset: 'classic' (Cormorant + Manrope, default), 'modern' (Inter), 'literary' (Lora + Inter)
let fontPreset = localStorage.getItem('phosphera-font') || 'classic';
let uiScale = localStorage.getItem('phosphera-zoom') || 'lg';
let userMock = null;
let firebaseUser = null;
// Owner detection — distinguishes account owner (gets personal extras: bookmarks,
// custom features, full sections list) from regular users (clean defaults).
const OWNER_UID = 'HK1bqdUIedhcLY0s11hLv3FyMh73';
function isOwnerUser() { return firebaseUser && firebaseUser.uid === OWNER_UID; }
// ─── NEW DATA ARCHITECTURE (schema v2) ─────────────────────────────────
// Data is split:
//   cloudMeta — global settings (_tileSettings, _bookmarks, _financeSettings, etc)
//   cloudSections — Map<slug, sectionData> (entries, subtopics, links per section)
// cloudData is kept as a *computed view* for back-compat with existing code that
// calls loadData()/saveData() with the legacy whole-doc shape.
let cloudData = null;        // Computed mirror of meta+sections (read by legacy code)
let cloudMeta = null;        // Settings document (Firestore meta/main)
let cloudSections = new Map(); // slug → sectionData
let loadedSlugs = new Set(); // Slugs we've already fetched at least once
let sectionWatchers = new Map(); // slug → unsubscribe()
let metaWatcher = null;      // unsubscribe for meta listener
let dataMigrationDone = false; // Set after one-time migration check completes
let userMenuOpen = false;
let chatOpen = false;
let chatView = 'main';
let chatMode = localStorage.getItem('phosphera-chat-mode') || 'assistant'; // 'assistant' | 'quickadd'
let chatMessages = JSON.parse(localStorage.getItem('phosphera-chat') || '[]');
let chatThinking = false;
let pendingSuggestion = null;
let pendingChatImage = null; // { dataUrl, mediaType, base64 } — staged photo for the next assistant message
let aiQuickResult = null;
let aiQuickLoading = false;
let aiBulkResults = null;
let aiBulkLoading = false;
let editingEntryId = null;
let customizeOpen = false;
let dragSlug = null;

const t = (key) => T[lang][key] || key;
if (theme === 'light') document.body.classList.add('light');
if (theme === 'light') document.documentElement.classList.add('light-init');
else document.documentElement.classList.add('dark');
// Apply font preset
if (fontPreset === 'modern') document.documentElement.classList.add('font-modern');
else if (fontPreset === 'literary') document.documentElement.classList.add('font-literary');
// Sync iOS theme-color meta to match current theme on initial load
(function() {
  const dyn = document.getElementById('dynamicThemeColor');
  if (dyn) dyn.setAttribute('content', theme === 'light' ? '#EDE7F6' : '#02050E');
})();

const STORAGE_KEY = 'phosphera-data-v1';
const TILES_KEY = 'phosphera-tiles-v1';
const TILES_DATA_KEY = '_tileSettings'; // key inside main data store, syncs via Firestore
const hasApiKey = () => !!localStorage.getItem('phosphera-anthropic-key');

// ─── Tile customization (order, hidden, custom titles, USER-ADDED tiles) ───
function loadTileSettings() {
  // Priority: cloudMeta (Firestore) → cloudData fallback → localStorage
  if (firebaseUser && cloudMeta && cloudMeta[TILES_DATA_KEY]) {
    return cloudMeta[TILES_DATA_KEY];
  }
  if (firebaseUser && cloudData && cloudData[TILES_DATA_KEY]) {
    return cloudData[TILES_DATA_KEY];
  }
  try { return JSON.parse(localStorage.getItem(TILES_KEY) || '{}'); } catch(e) { return {}; }
}
function saveTileSettings(s) {
  // Always save locally for instant UI response
  try { localStorage.setItem(TILES_KEY, JSON.stringify(s)); } catch(e) {}
  if (!firebaseUser) return;
  // SAFETY: refuse to overwrite a non-empty tile-settings object with an empty one.
  if (cloudMeta && cloudMeta[TILES_DATA_KEY]
      && Object.keys(cloudMeta[TILES_DATA_KEY]).length > 0
      && (!s || Object.keys(s).length === 0)) {
    console.warn('[phosphera] saveTileSettings REFUSED to overwrite settings with empty object.');
    return;
  }
  // CRITICAL SAFETY: refuse to lose custom sections.
  // If incoming `s.custom` has FEWER items than what's already in cloudMeta,
  // it means an old/stale render is trying to overwrite the cloud — block it
  // and keep cloud's custom array intact.
  if (cloudMeta && cloudMeta[TILES_DATA_KEY] && Array.isArray(cloudMeta[TILES_DATA_KEY].custom)) {
    const cloudCustomCount = cloudMeta[TILES_DATA_KEY].custom.length;
    const incomingCustomCount = Array.isArray(s.custom) ? s.custom.length : 0;
    if (incomingCustomCount < cloudCustomCount) {
      console.warn('[phosphera] saveTileSettings REFUSED to overwrite', cloudCustomCount,
        'custom sections with only', incomingCustomCount, '. Merging instead.');
      // Merge: keep all cloud custom sections, add any new ones from incoming
      const cloudCustomBySlug = new Map(cloudMeta[TILES_DATA_KEY].custom.map(c => [c.slug, c]));
      (s.custom || []).forEach(c => cloudCustomBySlug.set(c.slug, c));
      s = { ...s, custom: Array.from(cloudCustomBySlug.values()) };
    }
  }
  // Direct cloudMeta update — same pattern as saveBookmarks (avoids race)
  if (!cloudMeta) cloudMeta = { _schemaVersion: 2 };
  cloudMeta[TILES_DATA_KEY] = s;
  _composeCloudData();
  _metaPending = { ...cloudMeta };
  if (_metaSaveTimer) clearTimeout(_metaSaveTimer);
  _metaSaveTimer = setTimeout(_flushMetaSave, 300);
  console.log('[phosphera] Tile settings saved. Custom sections:', (s.custom || []).length);
}

// ─── BOOKMARKS (dashboard quick links) ───
const BOOKMARKS_DATA_KEY = '_bookmarks';
// By default: empty for everyone. The owner can re-add personal links manually.
const DEFAULT_BOOKMARKS = [];
function getBookmarks() {
  // Priority: cloudMeta → localStorage cache → DEFAULT_BOOKMARKS
  if (firebaseUser && cloudMeta && cloudMeta[BOOKMARKS_DATA_KEY] !== undefined) {
    return cloudMeta[BOOKMARKS_DATA_KEY];
  }
  // Fallback: legacy cloudData path (in case meta isn't loaded yet)
  if (firebaseUser && cloudData && cloudData[BOOKMARKS_DATA_KEY] !== undefined) {
    return cloudData[BOOKMARKS_DATA_KEY];
  }
  try {
    const stored = localStorage.getItem('phosphera-bookmarks');
    if (stored !== null) return JSON.parse(stored);
  } catch (e) {}
  return JSON.parse(JSON.stringify(DEFAULT_BOOKMARKS));
}
function saveBookmarks(bookmarks) {
  // Always localStorage cache first (for offline / fallback)
  try { localStorage.setItem('phosphera-bookmarks', JSON.stringify(bookmarks)); } catch (e) {}
  if (!firebaseUser) return;
  // SAFETY: refuse to overwrite a non-empty cloud array with an empty one.
  // This protects against accidental data loss from race conditions or buggy callers.
  if (cloudMeta && Array.isArray(cloudMeta[BOOKMARKS_DATA_KEY])
      && cloudMeta[BOOKMARKS_DATA_KEY].length > 0
      && (!bookmarks || bookmarks.length === 0)) {
    console.warn('[phosphera] saveBookmarks REFUSED to overwrite', cloudMeta[BOOKMARKS_DATA_KEY].length, 'bookmarks with empty array. Caller bug?');
    return;
  }
  // Update cloudMeta directly so other code reading from composed cloudData sees it instantly
  if (!cloudMeta) cloudMeta = { _schemaVersion: 2 };
  cloudMeta[BOOKMARKS_DATA_KEY] = bookmarks;
  _composeCloudData();
  // Queue write to Firestore meta doc
  _metaPending = { ...cloudMeta };
  if (_metaSaveTimer) clearTimeout(_metaSaveTimer);
  _metaSaveTimer = setTimeout(_flushMetaSave, 300);
  console.log('[phosphera] Bookmarks saved:', bookmarks.length, 'items');
}

// Get all sections — built-in + user-added custom ones
function getAllSections() {
  const s = loadTileSettings();
  const custom = s.custom || [];
  const titles = s.titles || {};
  const baseList = isOwnerUser() ? [...SECTIONS, ...getOwnerExtraSections()] : [...SECTIONS];
  return [...baseList, ...custom].map(sec => {
    const withFeatured = applyOwnerFeatured(sec);
    const t = titles[withFeatured.slug];
    if (t) return { ...withFeatured, titleEn: t.en || withFeatured.titleEn, titleRu: t.ru || withFeatured.titleRu };
    return { ...withFeatured };
  });
}

function getOrderedSections() {
  const s = loadTileSettings();
  const order = s.order || [];
  const hidden = new Set(s.hidden || []);
  const all = getAllSections();
  const ordered = [];
  order.forEach(slug => {
    const found = all.find(x => x.slug === slug);
    if (found) ordered.push(found);
  });
  all.forEach(sec => { if (!ordered.find(o => o.slug === sec.slug)) ordered.push(sec); });
  return ordered.filter(s => !hidden.has(s.slug));
}
function getAllSectionsOrdered() {
  // Includes hidden ones for customize panel
  const s = loadTileSettings();
  const order = s.order || [];
  const hidden = new Set(s.hidden || []);
  const custom = s.custom || [];
  const customSlugs = new Set(custom.map(c => c.slug));
  const all = getAllSections().map(sec => ({ ...sec, hidden: hidden.has(sec.slug), isCustom: customSlugs.has(sec.slug) }));
  const ordered = [];
  order.forEach(slug => {
    const found = all.find(x => x.slug === slug);
    if (found) ordered.push(found);
  });
  all.forEach(sec => { if (!ordered.find(o => o.slug === sec.slug)) ordered.push(sec); });
  return ordered;
}

window.addEventListener('phosphera-firebase-ready', () => {
  window.phospheraAuth.onAuth(async (user) => {
    if (user) {
      // SECURITY: Re-verify whitelist on every auth event, including session resume.
      // Skip if offline — Firestore can't be reached, and we already verified
      // at original signIn. Will re-verify when network returns.
      if (!navigator.onLine) {
        console.log('[phosphera] Offline — skipping whitelist re-check');
      } else {
        try {
          const userIsAdmin = window.phospheraAuth.isAdmin && window.phospheraAuth.isAdmin(user);
          if (!userIsAdmin) {
            await window.phospheraAuth.fetchWhitelist?.(true);
            const emailOk = user.email ? window.phospheraAuth.isEmailAllowed?.(user.email) : false;
            const phoneOk = user.phoneNumber ? window.phospheraAuth.isPhoneAllowed?.(user.phoneNumber) : false;
            let providerPhoneOk = false;
            if (!phoneOk && user.providerData) {
              for (const p of user.providerData) {
                if (p.phoneNumber && window.phospheraAuth.isPhoneAllowed?.(p.phoneNumber)) {
                  providerPhoneOk = true; break;
                }
              }
            }
            if (!emailOk && !phoneOk && !providerPhoneOk) {
              console.warn('[phosphera] User no longer on whitelist — signing out');
              await window.phospheraAuth.signOut();
              return;
            }
          }
        } catch (e) {
          console.error('[phosphera] Whitelist re-check failed:', e);
          // Don't lock out on transient error — let user continue
        }
      }

      firebaseUser = user;
      userMock = {
        name: user.displayName || user.email.split('@')[0],
        email: user.email,
        initials: (user.displayName || user.email)[0].toUpperCase()
      };

      // ─── Schema v2 onboarding ───
      // Strategy: try cloud first, but never block UI if offline.
      // Firestore's persistent IndexedDB cache will serve recent data,
      // and pending writes will queue until reconnection.

      // 0. Bootstrap from localStorage instantly — gives offline-first UI
      try {
        const localCache = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        for (const k of Object.keys(localCache)) {
          if (k.startsWith('_')) {
            if (!cloudMeta) cloudMeta = { _schemaVersion: 2 };
            cloudMeta[k] = localCache[k];
          } else if (localCache[k] && typeof localCache[k] === 'object') {
            cloudSections.set(k, localCache[k]);
          }
        }
        if (cloudSections.size > 0 || cloudMeta) {
          console.log('[phosphera] Bootstrapped from localStorage:', cloudSections.size, 'sections');
          _composeCloudData();
        }
      } catch (e) {}

      // 1. Run migration (skipped if already done or offline)
      try {
        if (navigator.onLine) {
          await migrateOldDataToNewSchema(user.uid);
        }
      } catch (e) { console.warn('[phosphera] Migration skipped (offline?):', e); }

      // 2. Load meta from cloud (Firestore will use cache if offline)
      try {
        const fresh = await window.phospheraAuth.loadMeta(user.uid);
        if (fresh) cloudMeta = fresh;
        else if (!cloudMeta) cloudMeta = { _schemaVersion: 2 };
      } catch (e) {
        console.warn('[phosphera] loadMeta failed, using cache/defaults:', e);
        if (!cloudMeta) cloudMeta = { _schemaVersion: 2 };
      }

      // 3. Migrate / restore local-only tile settings / bookmarks if cloud is empty OR missing.
      // CRITICAL: empty object {} or empty array [] are TRUTHY but should be treated as missing
      // — restore from localStorage if local has actual content.
      const cloudTilesIsEmpty = !cloudMeta[TILES_DATA_KEY]
        || (typeof cloudMeta[TILES_DATA_KEY] === 'object' && Object.keys(cloudMeta[TILES_DATA_KEY]).length === 0);
      if (cloudTilesIsEmpty) {
        const localTiles = (() => {
          try { return JSON.parse(localStorage.getItem(TILES_KEY) || '{}'); } catch(e) { return {}; }
        })();
        if (localTiles && Object.keys(localTiles).length > 0) {
          console.log('[phosphera] Restoring tile settings from local cache (cloud was empty):', Object.keys(localTiles).length, 'tiles');
          cloudMeta[TILES_DATA_KEY] = localTiles;
          // Queue save so cloud catches up with local
          _metaPending = { ...cloudMeta };
          if (_metaSaveTimer) clearTimeout(_metaSaveTimer);
          _metaSaveTimer = setTimeout(_flushMetaSave, 1000);
        }
      }
      const cloudBookmarksIsEmpty = cloudMeta[BOOKMARKS_DATA_KEY] === undefined
        || (Array.isArray(cloudMeta[BOOKMARKS_DATA_KEY]) && cloudMeta[BOOKMARKS_DATA_KEY].length === 0);
      if (cloudBookmarksIsEmpty) {
        const localBookmarks = (() => {
          try {
            const stored = localStorage.getItem('phosphera-bookmarks');
            return stored !== null ? JSON.parse(stored) : null;
          } catch(e) { return null; }
        })();
        if (Array.isArray(localBookmarks) && localBookmarks.length > 0) {
          console.log('[phosphera] Restoring bookmarks from local cache (cloud was empty):', localBookmarks.length, 'items');
          cloudMeta[BOOKMARKS_DATA_KEY] = localBookmarks;
          _metaPending = { ...cloudMeta };
          if (_metaSaveTimer) clearTimeout(_metaSaveTimer);
          _metaSaveTimer = setTimeout(_flushMetaSave, 1000);
        }
      }
      // Persist any local-migration changes (will queue if offline)
      if (cloudMeta && navigator.onLine) {
        try { await window.phospheraAuth.saveMeta(user.uid, cloudMeta); } catch (e) {}
      }

      // 4. Discover sections (uses Firestore cache when offline)
      try {
        const slugs = await window.phospheraAuth.listSectionSlugs(user.uid);
        await Promise.all(slugs.map(async slug => {
          try {
            const data = await window.phospheraAuth.loadSection(user.uid, slug);
            if (data) {
              cloudSections.set(slug, data);
              loadedSlugs.add(slug);
            }
          } catch (e) { console.warn('[phosphera] section load failed for', slug, e); }
        }));
        console.log('[phosphera] Loaded', slugs.length, 'sections from cloud/cache.');
        slugs.forEach(slug => _startSectionWatcher(slug));
      } catch (e) {
        console.warn('[phosphera] Section discovery failed (offline?), using local cache:', e);
        // We already bootstrapped from localStorage at step 0, so UI is functional
        // Start watchers for sections we know about
        for (const slug of cloudSections.keys()) _startSectionWatcher(slug);
      }

      // 5. Watch meta doc for live updates from other devices
      metaWatcher = window.phospheraAuth.watchMeta(user.uid, (incoming) => {
        if (!incoming) return;
        const oldStr = JSON.stringify(cloudMeta || {});
        const newStr = JSON.stringify(incoming);
        if (oldStr === newStr) return;
        cloudMeta = incoming;
        _composeCloudData();
        if (editingEntryId || document.activeElement?.tagName === 'TEXTAREA' || document.activeElement?.tagName === 'INPUT') return;
        if (typeof render === 'function') {
          renderSidebar();
          if (getRoute().type === 'dashboard') renderDashboard();
          else renderSection(getRoute().slug);
        }
      });

      _composeCloudData(); // build the unified view
      renderApp();
    } else {
      firebaseUser = null;
      userMock = null;
      cloudData = null;
      cloudMeta = null;
      cloudSections = new Map();
      loadedSlugs = new Set();
      _stopAllWatchers();
      renderApp();
    }
  });
});

// ──────────────────────────────────────────────────────────────────────
// DATA LAYER (Schema v2 — per-section docs + meta doc)
// ──────────────────────────────────────────────────────────────────────

// Timezone-safe YYYY-MM-DD: returns the calendar date in the user's local TZ,
// not in UTC. Avoids "off by one day" issues at evening/early-morning.
function toLocalDateStr(d) {
  if (!d) d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Compose a single read-only object from meta + sections (legacy shape)
function _composeCloudData() {
  const composed = {};
  if (cloudMeta) {
    for (const k of Object.keys(cloudMeta)) {
      if (k.startsWith('_')) composed[k] = cloudMeta[k];
    }
  }
  for (const [slug, sectionData] of cloudSections) {
    composed[slug] = sectionData;
  }
  cloudData = composed;
  return composed;
}

function loadData() {
  if (firebaseUser) {
    // Make sure cloudData is up-to-date with current meta+sections state
    return _composeCloudData();
  }
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch(e) { return {}; }
}

// ─── Save queues per scope (meta + per-section) ───
let _metaSaveTimer = null;
let _metaPending = null;
let _metaInFlight = false;
const _sectionSaveTimers = new Map(); // slug → timeout id
const _sectionPending = new Map(); // slug → data
const _sectionInFlight = new Set(); // slugs currently being written

// Deep-clean an object for Firestore: removes `undefined` values (which Firestore rejects),
// while preserving null. Also handles arrays and nested objects.
function sanitizeForFirestore(value) {
  if (value === undefined) return null;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value
      .filter(v => v !== undefined)
      .map(v => sanitizeForFirestore(v));
  }
  if (typeof value === 'object' && value.constructor === Object) {
    const cleaned = {};
    for (const k of Object.keys(value)) {
      const v = value[k];
      if (v === undefined) continue; // drop undefined keys entirely
      cleaned[k] = sanitizeForFirestore(v);
    }
    return cleaned;
  }
  return value;
}

// Detect Firebase validation errors that won't succeed on retry — drop them.
function _isUnretryableFirestoreError(err) {
  const msg = String(err?.message || err || '');
  if (msg.includes('Unsupported field value')) return true;
  if (msg.includes('invalid data')) return true;
  if (err?.code === 'invalid-argument') return true;
  return false;
}

async function _flushMetaSave() {
  if (!firebaseUser || _metaPending === null) return;
  if (_metaInFlight) return;
  _metaInFlight = true;
  const toSave = _metaPending;
  _metaPending = null;
  try {
    const clean = sanitizeForFirestore(toSave);
    await window.phospheraAuth.saveMeta(firebaseUser.uid, clean);
  } catch (e) {
    console.error('[phosphera] saveMeta failed:', e);
    if (!_isUnretryableFirestoreError(e) && _metaPending === null) {
      _metaPending = toSave;
    } else if (_isUnretryableFirestoreError(e)) {
      console.warn('[phosphera] Meta save permanently failed (validation). Data kept in localStorage only.');
    }
  } finally {
    _metaInFlight = false;
    if (_metaPending !== null) setTimeout(_flushMetaSave, 500);
  }
}

async function _flushSectionSave(slug) {
  if (!firebaseUser || !_sectionPending.has(slug)) return;
  if (_sectionInFlight.has(slug)) return;
  _sectionInFlight.add(slug);
  const toSave = _sectionPending.get(slug);
  _sectionPending.delete(slug);
  try {
    const clean = sanitizeForFirestore(toSave);
    await window.phospheraAuth.saveSection(firebaseUser.uid, slug, clean);
    // CRITICAL: do NOT do `cloudSections.set(slug, toSave)` here.
    // The user may have added more entries while the write was in flight.
  } catch (e) {
    console.error('[phosphera] saveSection(' + slug + ') failed:', e);
    if (_isUnretryableFirestoreError(e)) {
      // Don't infinite-retry on validation errors — they'll always fail
      console.warn('[phosphera] Permanent validation error for', slug, '— data kept in localStorage only. Notes will be re-saved on next edit.');
      showToast(lang === 'en' ? 'Sync issue: data saved locally' : 'Проблема синхр.: данные сохранены локально', { type: 'error', duration: 4000 });
    } else {
      // Transient error (network etc.) — re-queue with merge
      const newerPending = _sectionPending.get(slug);
      if (newerPending) {
        _sectionPending.set(slug, mergeSectionPreserveLocal(toSave, newerPending) || newerPending);
      } else {
        _sectionPending.set(slug, toSave);
      }
    }
  } finally {
    _sectionInFlight.delete(slug);
    if (_sectionPending.has(slug)) setTimeout(() => _flushSectionSave(slug), 500);
  }
}

// Public saveData — accepts the legacy whole-object shape and routes parts to
// either meta doc (keys starting with _) or per-section docs.
function saveData(d) {
  // Always cache locally as a single blob (for offline / fast reload)
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); } catch(e) {}
  if (!firebaseUser) return;

  // 1. Split into meta vs per-section
  const newMeta = {};
  const sectionDiffs = {};
  for (const k of Object.keys(d)) {
    if (k.startsWith('_')) {
      newMeta[k] = d[k];
    } else {
      sectionDiffs[k] = d[k];
    }
  }
  // Ensure schema version is recorded
  newMeta._schemaVersion = 2;

  // 2. Save meta if it actually changed
  const metaChanged = !cloudMeta || JSON.stringify(cloudMeta) !== JSON.stringify(newMeta);
  if (metaChanged) {
    cloudMeta = newMeta;
    _metaPending = newMeta;
    if (_metaSaveTimer) clearTimeout(_metaSaveTimer);
    _metaSaveTimer = setTimeout(_flushMetaSave, 300);
  }

  // 3. Save sections that changed
  for (const slug of Object.keys(sectionDiffs)) {
    const newSec = sectionDiffs[slug];
    const oldSec = cloudSections.get(slug);
    if (!oldSec || JSON.stringify(oldSec) !== JSON.stringify(newSec)) {
      cloudSections.set(slug, newSec);
      _sectionPending.set(slug, newSec);
      if (_sectionSaveTimers.has(slug)) clearTimeout(_sectionSaveTimers.get(slug));
      _sectionSaveTimers.set(slug, setTimeout(() => _flushSectionSave(slug), 300));
    }
  }
  _composeCloudData(); // refresh mirror
}

// Force pending writes to localStorage before unload
window.addEventListener('beforeunload', () => {
  if (firebaseUser) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_composeCloudData())); } catch(e) {}
  }
});

// Section-level entry merge (id-based union, newest wins). Used when a snapshot
// arrives while we have a pending local write to the same section.
function mergeSectionPreserveLocal(remote, local) {
  if (!remote && !local) return null;
  if (!remote) return local;
  if (!local) return remote;
  let rescuedCount = 0;
  const mergeListById = (rList, lList) => {
    const rArr = Array.isArray(rList) ? rList : [];
    const lArr = Array.isArray(lList) ? lList : [];
    const byId = new Map();
    rArr.forEach(item => { if (item && item.id) byId.set(item.id, item); });
    lArr.forEach(item => {
      if (!item || !item.id) return;
      const existing = byId.get(item.id);
      if (!existing) { byId.set(item.id, item); rescuedCount++; return; }
      const rTime = existing.mtime || existing.createdAt || 0;
      const lTime = item.mtime || item.createdAt || 0;
      byId.set(item.id, lTime >= rTime ? item : existing);
    });
    return Array.from(byId.values());
  };
  const merged = {
    ...remote,
    ...local,
    entries: mergeListById(remote.entries, local.entries),
    subtopics: mergeListById(remote.subtopics, local.subtopics),
    links: mergeListById(remote.links, local.links),
  };
  if (local.activeSubtopic !== undefined) merged.activeSubtopic = local.activeSubtopic;
  if (local.sortMode !== undefined) merged.sortMode = local.sortMode;
  if (rescuedCount > 0) {
    console.log(`[phosphera sync] Merged ${rescuedCount} local-only item(s) into incoming snapshot`);
  }
  return merged;
}

// Empty section template
function _emptySection() {
  return { subtopics: [], entries: [], links: [], activeSubtopic: null };
}

function getSection(slug) { return getAllSections().find(s => s.slug === slug); }

function getSectionData(slug) {
  // First check in-memory map
  if (cloudSections.has(slug)) {
    const s = cloudSections.get(slug);
    if (!s.subtopics) s.subtopics = [];
    if (!s.entries) s.entries = [];
    if (!s.links) s.links = [];
    return s;
  }
  // Not loaded yet — trigger lazy load (returns empty for now, snapshot will update)
  if (firebaseUser && !loadedSlugs.has(slug)) {
    _lazyLoadSection(slug);
  }
  // Fallback to localStorage (so UI shows something while load completes)
  try {
    const local = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    if (local[slug]) {
      const s = local[slug];
      if (!s.subtopics) s.subtopics = [];
      if (!s.entries) s.entries = [];
      if (!s.links) s.links = [];
      cloudSections.set(slug, s);
      return s;
    }
  } catch (e) {}
  const empty = _emptySection();
  cloudSections.set(slug, empty);
  return empty;
}

async function _lazyLoadSection(slug) {
  if (!firebaseUser || loadedSlugs.has(slug)) return;
  // SAFETY: phospheraAuth module might not be ready yet (race on startup
  // when fast-path login sets firebaseUser before Firebase module finishes loading).
  if (!window.phospheraAuth || !window.phospheraAuth.loadSection) {
    // Retry shortly when module is ready
    setTimeout(() => _lazyLoadSection(slug), 200);
    return;
  }
  loadedSlugs.add(slug);
  try {
    const data = await window.phospheraAuth.loadSection(firebaseUser.uid, slug);
    if (data) {
      cloudSections.set(slug, data);
      _composeCloudData();
      const route = getRoute();
      if (route.type === 'section' && route.slug === slug) {
        if (typeof renderSection === 'function') renderSection(slug);
      } else if (route.type === 'dashboard') {
        if (typeof renderDashboard === 'function') renderDashboard();
      }
    }
    _startSectionWatcher(slug);
  } catch (e) {
    console.error('[phosphera] lazy load section', slug, 'failed:', e);
    loadedSlugs.delete(slug);
  }
}

function _startSectionWatcher(slug) {
  if (!firebaseUser || sectionWatchers.has(slug)) return;
  if (!window.phospheraAuth || !window.phospheraAuth.watchSection) {
    setTimeout(() => _startSectionWatcher(slug), 200);
    return;
  }
  const unsub = window.phospheraAuth.watchSection(firebaseUser.uid, slug, (data) => {
    if (!data) return;
    // CRITICAL: always merge incoming snapshot with current local state.
    // Even if no pending write is queued, the user may have just edited
    // something locally and the snapshot might be a stale echo.
    // Merging by entry-id (newer mtime/createdAt wins) is always safe.
    const localState = cloudSections.get(slug);
    let merged;
    if (localState) {
      merged = mergeSectionPreserveLocal(data, localState) || data;
    } else {
      merged = data;
    }
    const oldStr = JSON.stringify(localState || {});
    const newStr = JSON.stringify(merged);
    if (oldStr === newStr) return; // no actual change
    // Count local entries that survived the merge for diagnostics
    const localEntryIds = new Set((localState?.entries || []).map(e => e.id));
    const remoteEntryIds = new Set((data?.entries || []).map(e => e.id));
    const mergedEntryIds = new Set((merged.entries || []).map(e => e.id));
    const onlyLocal = [...localEntryIds].filter(id => !remoteEntryIds.has(id));
    if (onlyLocal.length > 0) {
      console.log(`[phosphera sync] ${slug}: merged snapshot with ${onlyLocal.length} local-only entries kept (ids:`, onlyLocal.slice(0, 5).join(', ') + (onlyLocal.length > 5 ? '...' : '') + ')');
    }
    cloudSections.set(slug, merged);
    _composeCloudData();
    // Also update localStorage so it's fresh
    try {
      const local = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      local[slug] = merged;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(local));
    } catch (e) {}
    // Skip re-render if user is typing
    if (editingEntryId || document.activeElement?.tagName === 'TEXTAREA' || document.activeElement?.tagName === 'INPUT') return;
    const route = getRoute();
    if (route.type === 'section' && route.slug === slug) {
      if (typeof renderSection === 'function') renderSection(slug);
    } else if (route.type === 'dashboard') {
      if (typeof renderDashboard === 'function') renderDashboard();
    }
  });
  sectionWatchers.set(slug, unsub);
}

function _stopAllWatchers() {
  for (const unsub of sectionWatchers.values()) try { unsub(); } catch(e) {}
  sectionWatchers.clear();
  if (metaWatcher) { try { metaWatcher(); } catch(e) {}; metaWatcher = null; }
}

function updateSectionData(slug, partial) {
  const current = getSectionData(slug);
  const updated = { ...current, ...partial };
  cloudSections.set(slug, updated);
  _composeCloudData();
  // Cache locally + queue section write
  try {
    const local = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    local[slug] = updated;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(local));
  } catch (e) {}
  if (firebaseUser) {
    _sectionPending.set(slug, updated);
    if (_sectionSaveTimers.has(slug)) clearTimeout(_sectionSaveTimers.get(slug));
    _sectionSaveTimers.set(slug, setTimeout(() => _flushSectionSave(slug), 300));
  }
}

// One-time migration: copy legacy users/{uid}/data/main into new meta + sections
async function migrateOldDataToNewSchema(uid) {
  if (dataMigrationDone) return;
  dataMigrationDone = true;
  try {
    // First: check if new schema already exists
    const existingMeta = await window.phospheraAuth.loadMeta(uid);
    if (existingMeta && existingMeta._schemaVersion >= 2) {
      console.log('[phosphera] Schema v2 already in place. Skipping migration.');
      return;
    }
    // Look for legacy data doc
    const legacy = await window.phospheraAuth.loadData(uid);
    if (!legacy) {
      console.log('[phosphera] No legacy data found. Fresh install.');
      // Set schemaVersion in meta so we don't re-check
      await window.phospheraAuth.saveMeta(uid, { _schemaVersion: 2 });
      return;
    }
    console.log('[phosphera] Migrating legacy main doc to schema v2...');
    const newMeta = { _schemaVersion: 2 };
    const sectionWrites = [];
    for (const k of Object.keys(legacy)) {
      if (k.startsWith('_')) {
        newMeta[k] = legacy[k];
      } else {
        sectionWrites.push([k, legacy[k]]);
      }
    }
    // Write meta first
    await window.phospheraAuth.saveMeta(uid, newMeta);
    // Then sections (in parallel)
    await Promise.all(sectionWrites.map(([slug, data]) =>
      window.phospheraAuth.saveSection(uid, slug, data)
    ));
    console.log('[phosphera] Migration complete. Created', sectionWrites.length, 'section docs.');
    // We keep the legacy main doc as backup — user can delete manually later.
  } catch (e) {
    console.error('[phosphera] Migration failed:', e);
    // Don't throw — allow app to keep working from legacy if migration fails
    dataMigrationDone = false; // allow retry
  }
}
function getRoute() {
  const hash = location.hash.replace('#', '') || '/';
  if (hash === '/' || hash === '') return { type: 'calendar' };
  if (hash === '/dashboard') return { type: 'dashboard' };
  const m = hash.match(/^\/s\/(.+)$/);
  if (m) return { type: 'section', slug: m[1] };
  return { type: 'calendar' };
}
// ─── Deadline compatibility layer ───
// `deadline` historically was always a bare date string ('2026-09-15').
// The calendar screen needs time-of-day too, so it may now also be a full
// ISO datetime ('2026-09-15T18:00'). These two helpers are the ONE place
// that knows how to tell them apart — every other function should go
// through them instead of concatenating 'T00:00:00' itself.
function deadlineHasTime(deadline) {
  return !!(deadline && deadline.includes('T'));
}
function parseDeadline(deadline) {
  if (!deadline) return null;
  return deadlineHasTime(deadline) ? new Date(deadline) : new Date(deadline + 'T00:00:00');
}
function getUrgency(deadline) {
  if (!deadline) return 'none';
  const now = new Date(); now.setHours(0,0,0,0);
  const dl = parseDeadline(deadline);
  const diff = Math.round((dl - now) / 86400000);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff <= 7) return 'soon';
  return 'later';
}
function formatDeadline(deadline) {
  const u = getUrgency(deadline);
  const timePart = deadlineHasTime(deadline)
    ? ', ' + parseDeadline(deadline).toLocaleTimeString(lang === 'ru' ? 'ru-RU' : 'en-IE', { hour: '2-digit', minute: '2-digit' })
    : '';
  if (u === 'overdue') return '⚠ ' + t('overdue') + timePart;
  if (u === 'today') return '● ' + t('todayLabel') + timePart;
  if (u === 'tomorrow') return '○ ' + t('tomorrowLabel') + timePart;
  const d = parseDeadline(deadline);
  return '📅 ' + d.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-IE', { month: 'short', day: 'numeric' }) + timePart;
}
function getAllTodos() {
  // Include custom sections and owner extras, not just base SECTIONS.
  // Hide done items older than 24h so completed tasks don't clutter the dashboard.
  const DONE_HIDE_AFTER = 24 * 60 * 60 * 1000; // 24h in ms
  const now = Date.now();
  const todos = [];
  const data = loadData();
  const allSecs = (typeof getAllSections === 'function') ? getAllSections() : SECTIONS;
  allSecs.forEach(section => {
    const sd = data[section.slug];
    if (!sd || !sd.entries) return;
    sd.entries.forEach(entry => {
      if (!entry.deadline) return;
      // Hide done items completed more than 24h ago
      if (entry.done && entry.doneAt && (now - entry.doneAt) > DONE_HIDE_AFTER) return;
      todos.push({ ...entry, sectionSlug: section.slug, sectionIcon: section.icon, sectionTitle: lang === 'en' ? section.titleEn : section.titleRu });
    });
  });
  todos.sort((a, b) => {
    if (!!a.done !== !!b.done) return a.done ? 1 : -1;
    return new Date(a.deadline) - new Date(b.deadline);
  });
  return todos;
}

function _getAllPinnedEntries() {
  // Returns all pinned entries across all sections, with section context.
  const out = [];
  const data = loadData();
  const allSecs = (typeof getAllSections === 'function') ? getAllSections() : SECTIONS;
  allSecs.forEach(section => {
    const sd = data[section.slug];
    if (!sd || !sd.entries) return;
    sd.entries.forEach(entry => {
      if (entry.pinned) {
        out.push({
          ...entry,
          sectionSlug: section.slug,
          sectionIcon: section.icon,
          sectionTitle: lang === 'en' ? section.titleEn : section.titleRu
        });
      }
    });
  });
  // Sort: most recently created/edited first
  out.sort((a, b) => (b.mtime || b.createdAt || 0) - (a.mtime || a.createdAt || 0));
  return out;
}

function getAllOpenTasks() {
  // Tasks/notes WITHOUT deadlines, not done, NOT marked as note-only, grouped by section.
  const grouped = [];
  const data = loadData();
  const allSecs = (typeof getAllSections === 'function') ? getAllSections() : SECTIONS;
  allSecs.forEach(section => {
    const sd = data[section.slug];
    if (!sd || !sd.entries) return;
    const items = sd.entries
      .filter(entry => !entry.deadline && !entry.done && !entry.noCheck)
      .map(entry => ({ ...entry, sectionSlug: section.slug, sectionIcon: section.icon, sectionTitle: lang === 'en' ? section.titleEn : section.titleRu }));
    if (items.length > 0) grouped.push({ section, items });
  });
  return grouped;
}

function renderEffects() {
  const w = document.getElementById('fxWrapper');
  if (!w) return; // scaffold not ready yet — safe no-op
  w.innerHTML = '';
  // Reduce particle count on small/mobile screens — saves significant GPU
  const isMobile = window.innerWidth <= 860;
  const isLight = theme === 'light';
  // Per user preference: no rain in dark theme, no stars in light theme.
  // If the chosen fx isn't allowed for the current theme, fall back to aurora.
  let effectiveFx = fx;
  if (fx === 'rain' && !isLight) effectiveFx = 'aurora';
  if (fx === 'stars' && isLight) effectiveFx = 'aurora';
  if (effectiveFx === 'aurora') {
    w.innerHTML = '<div class="aurora-band aurora-1"></div><div class="aurora-band aurora-2"></div><div class="aurora-band aurora-3"></div>';
  } else if (effectiveFx === 'rain') {
    let html = '';
    const count = isMobile ? 25 : 70;
    for (let i = 0; i < count; i++) {
      const left = Math.random() * 100, dur = 1.2 + Math.random() * 2, delay = Math.random() * 3, op = 0.4 + Math.random() * 0.5;
      html += `<div class="rain-drop" style="left:${left}%;animation-duration:${dur}s;animation-delay:${delay}s;opacity:${op}"></div>`;
    }
    w.innerHTML = html;
  } else if (effectiveFx === 'stars') {
    let html = '';
    const count = isMobile ? 50 : 130;
    for (let i = 0; i < count; i++) {
      const left = Math.random() * 100, top = Math.random() * 100, size = 0.8 + Math.random() * 2.4, delay = Math.random() * 5, dur = 2 + Math.random() * 4;
      html += `<div class="star" style="left:${left}%;top:${top}%;width:${size}px;height:${size}px;animation-delay:${delay}s;animation-duration:${dur}s"></div>`;
    }
    w.innerHTML = html;
  }
}

function renderApp() {
  document.getElementById('bootSplash')?.remove();
  const app = document.getElementById('app');
  if (!userMock) { app.innerHTML = renderLogin(); attachLoginHandlers(); document.getElementById('chatRoot').innerHTML = ''; return; }
  app.innerHTML = `
    <div class="app-shell">
      <div class="sidebar-overlay" id="sidebarOverlay"></div>
      <aside class="sidebar no-print" id="sidebar"></aside>
      <main class="main">
        <header class="header no-print">
          <button class="btn-icon menu-toggle no-print" id="menuBtn" aria-label="Menu" title="Menu">☰</button>
          <h1 class="header-title" id="headerTitle">phosphera</h1>
          <div class="header-actions">
            <button class="btn-ghost" id="globalSearchBtn" title="${lang === 'en' ? 'Search' : 'Поиск'} (⌘K)">🔍 <span class="btn-label">${lang === 'en' ? 'Search' : 'Поиск'}</span></button>
            <button class="btn-ghost" id="printBtn" style="display:none">🖨 <span id="printLabel">Print</span></button>
            <div id="userArea"></div>
          </div>
        </header>
        <div class="content" id="content"></div>
      </main>
    </div>`;
  attachAppHandlers();
  render();
  renderChat();
}

function renderLogin() {
  return `
    <div class="login-shell">
      <div style="position:absolute;top:18px;right:18px;display:flex;gap:8px">
        <button class="btn-icon fx-toggle" id="loginFxBtn"><span class="icon">${FX_ICONS[fx]}</span></button>
        <button class="btn-icon theme-toggle" id="loginThemeBtn"><span class="sun">☼</span><span class="moon">☾</span></button>
        <button class="btn-icon" id="loginLangBtn">${lang.toUpperCase()}</button>
      </div>
      <div class="card login-card">
        <div class="login-logo">phosphera</div>
        <div class="login-tagline">${t('appTagline')}</div>
        <div class="login-tagline-extra">${lang === 'en'
          ? '<span class="accent">The sky remembers everything</span>'
          : '<span class="accent">Бог всё видит</span>'}</div>
        <div id="loginMain">
          <button class="btn-google" id="googleSignInBtn">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.6 39.7 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C41.5 35.3 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            ${t('signIn')}
          </button>
          <div class="login-divider"><span>${lang === 'en' ? 'or' : 'или'}</span></div>
          <button class="btn-ghost" id="phoneSignInBtn" style="width:100%;padding:13px 20px;font-size:14px;min-height:50px;display:inline-flex;align-items:center;justify-content:center;gap:10px">
            <span style="font-size:18px">📱</span> ${lang === 'en' ? 'Sign in with phone' : 'Войти по номеру'}
          </button>
        </div>
        <div id="loginPhone" style="display:none">
          <div style="margin-bottom:10px;color:var(--text-muted);font-size:13px;text-align:left">${lang === 'en' ? 'Phone number (with country code)' : 'Номер телефона (с кодом страны)'}</div>
          <input id="phoneNumberInput" type="tel" placeholder="+353 87 1234567" style="margin-bottom:12px" />
          <div id="recaptcha-container" style="margin-bottom:12px;display:flex;justify-content:center"></div>
          <button class="btn-primary" id="phoneSendCodeBtn" style="width:100%;min-height:50px;margin-bottom:8px">${lang === 'en' ? 'Send code' : 'Отправить код'}</button>
          <button class="btn-ghost" id="phoneCancelBtn" style="width:100%;font-size:13px">← ${lang === 'en' ? 'Back' : 'Назад'}</button>
        </div>
        <div id="loginCode" style="display:none">
          <div style="margin-bottom:10px;color:var(--text-muted);font-size:13px;text-align:left">${lang === 'en' ? 'Enter the 6-digit code from SMS' : 'Введи 6-значный код из SMS'}</div>
          <input id="codeInput" type="text" inputmode="numeric" maxlength="6" placeholder="123456" style="margin-bottom:12px;font-size:20px;text-align:center;letter-spacing:6px;font-family:'JetBrains Mono',monospace" />
          <button class="btn-primary" id="codeVerifyBtn" style="width:100%;min-height:50px;margin-bottom:8px">${lang === 'en' ? 'Verify' : 'Подтвердить'}</button>
          <button class="btn-ghost" id="codeCancelBtn" style="width:100%;font-size:13px">← ${lang === 'en' ? 'Back' : 'Назад'}</button>
        </div>
        <div id="loginError" style="margin-top:12px;color:var(--danger);font-size:13px;display:none;text-align:center"></div>
      </div>
    </div>`;
}

function attachLoginHandlers() {
  document.getElementById('googleSignInBtn').addEventListener('click', async () => {
    if (window.phospheraAuth) await window.phospheraAuth.signIn();
  });

  // Phone sign-in flow
  let phoneConfirmation = null;
  const showPanel = (id) => {
    ['loginMain', 'loginPhone', 'loginCode'].forEach(p => {
      const el = document.getElementById(p); if (el) el.style.display = (p === id) ? '' : 'none';
    });
    document.getElementById('loginError').style.display = 'none';
  };
  const showError = (msg) => {
    const el = document.getElementById('loginError');
    el.textContent = msg; el.style.display = '';
  };

  document.getElementById('phoneSignInBtn').addEventListener('click', () => {
    if (!window.phospheraAuth || !window.phospheraAuth.signInWithPhone) {
      showError(lang === 'en'
        ? 'Phone auth not enabled. See Firebase setup instructions.'
        : 'Вход по номеру не настроен. См. инструкции Firebase.');
      return;
    }
    showPanel('loginPhone');
  });

  document.getElementById('phoneCancelBtn').addEventListener('click', () => showPanel('loginMain'));
  document.getElementById('codeCancelBtn').addEventListener('click', () => showPanel('loginPhone'));

  document.getElementById('phoneSendCodeBtn').addEventListener('click', async () => {
    const phone = document.getElementById('phoneNumberInput').value.trim();
    if (!phone || !phone.startsWith('+')) {
      showError(lang === 'en' ? 'Phone must start with + and country code' : 'Номер должен начинаться с + и кода страны');
      return;
    }
    try {
      phoneConfirmation = await window.phospheraAuth.signInWithPhone(phone, 'recaptcha-container');
      showPanel('loginCode');
      setTimeout(() => document.getElementById('codeInput').focus(), 200);
    } catch (err) {
      showError((lang === 'en' ? 'Error: ' : 'Ошибка: ') + (err.message || err));
    }
  });

  document.getElementById('codeVerifyBtn').addEventListener('click', async () => {
    const code = document.getElementById('codeInput').value.trim();
    if (code.length !== 6) {
      showError(lang === 'en' ? 'Enter 6 digits' : 'Введи 6 цифр');
      return;
    }
    try {
      await phoneConfirmation.confirm(code);
      // signed in — onAuth listener will take over
    } catch (err) {
      showError((lang === 'en' ? 'Wrong code: ' : 'Неверный код: ') + (err.message || err));
    }
  });

  document.getElementById('loginFxBtn').addEventListener('click', cycleFx);
  document.getElementById('loginThemeBtn').addEventListener('click', toggleTheme);
  document.getElementById('loginLangBtn').addEventListener('click', () => { toggleLang(); renderApp(); });
}

function renderUserArea() {
  const ua = document.getElementById('userArea'); if (!ua) return;
  const linkedPhones = (firebaseUser && firebaseUser.providerData)
    ? firebaseUser.providerData.filter(p => p.providerId === 'phone').map(p => p.phoneNumber)
    : [];
  const isUserAdmin = window.phospheraAuth && window.phospheraAuth.isAdmin && window.phospheraAuth.isAdmin();
  const zoomScale = ZOOM_SCALES.find(z => z.id === uiScale) || ZOOM_SCALES[3];
  ua.innerHTML = `
    <button class="user-chip" id="userChip" title="${lang === 'en' ? 'Settings & account' : 'Настройки и аккаунт'}">
      <span class="user-avatar">${userMock.initials}</span>
    </button>
    ${userMenuOpen ? `
      <div class="user-menu">
        <div class="user-menu-info">
          <div class="name">${escapeHtml(userMock.name)}</div>
          <div class="email">${escapeHtml(userMock.email)}</div>
          ${linkedPhones.length ? `<div class="email" style="margin-top:4px;color:var(--accent1)">📱 ${linkedPhones.join(', ')}</div>` : ''}
          ${isUserAdmin ? `<div class="email" style="margin-top:4px;color:#FBBF24">🛡 ${lang === 'en' ? 'Admin' : 'Администратор'}</div>` : ''}
        </div>

        <div class="user-menu-section-h">${lang === 'en' ? 'Appearance' : 'Внешний вид'}</div>
        <div class="user-menu-toggles">
          <button class="user-toggle" data-action="theme" title="${lang === 'en' ? 'Theme' : 'Тема'}">${theme === 'dark' ? '☾' : '☼'}<span class="lbl">${lang === 'en' ? 'Theme' : 'Тема'}</span></button>
          <button class="user-toggle" data-action="fx" title="${lang === 'en' ? 'Effects' : 'Эффекты'}">${FX_ICONS[fx]}<span class="lbl">${fx === 'aurora' ? (lang === 'en' ? 'Aurora' : 'Аврора') : fx === 'rain' ? (lang === 'en' ? 'Rain' : 'Дождь') : (lang === 'en' ? 'Stars' : 'Звёзды')}</span></button>
          <button class="user-toggle" data-action="zoom" title="${lang === 'en' ? 'UI scale' : 'Масштаб'}">${zoomScale.label}<span class="lbl">${lang === 'en' ? 'Scale' : 'Масштаб'}</span></button>
          <button class="user-toggle font-toggle" data-action="font" title="${lang === 'en' ? 'Font' : 'Шрифт'}">Aa<span class="lbl label">${FONT_LABELS[fontPreset]}</span></button>
          <button class="user-toggle" data-action="lang" title="${lang === 'en' ? 'Language' : 'Язык'}">${lang === 'en' ? '🇬🇧' : '🇷🇺'}<span class="lbl">${lang === 'en' ? 'EN' : 'RU'}</span></button>
        </div>

        <div class="user-menu-divider"></div>

        <button class="user-menu-item" id="linkPhoneBtn">📱 ${lang === 'en' ? 'Link phone number' : 'Привязать номер'}</button>
        <button class="user-menu-item" id="driveStorageBtn">${isDriveConnected() ? '☁ ' + (lang === 'en' ? 'Drive connected — Disconnect' : 'Drive подключён — Отключить') : '☁ ' + (lang === 'en' ? 'Connect Google Drive for media' : 'Подключить Google Drive')}</button>
        ${isUserAdmin ? `<button class="user-menu-item" id="adminPanelBtn">🛡 ${lang === 'en' ? 'Admin panel' : 'Админ-панель'}</button>` : ''}
        <button class="user-menu-item" id="signOutBtn">↪ ${t('signOut')}</button>
      </div>` : ''}`;
  document.getElementById('userChip').addEventListener('click', (e) => { e.stopPropagation(); userMenuOpen = !userMenuOpen; renderUserArea(); });
  if (userMenuOpen) {
    // Quick toggle buttons (theme/fx/zoom/lang)
    document.querySelectorAll('.user-toggle').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const action = btn.getAttribute('data-action');
        if (action === 'theme') toggleTheme();
        else if (action === 'fx') cycleFx();
        else if (action === 'zoom') cycleZoom();
        else if (action === 'font') cycleFont();
        else if (action === 'lang') toggleLang();
        // Re-render menu to update labels
        renderUserArea();
      });
    });
    document.getElementById('signOutBtn').addEventListener('click', async () => {
      if (window.phospheraAuth) await window.phospheraAuth.signOut();
      userMenuOpen = false;
    });
    document.getElementById('linkPhoneBtn').addEventListener('click', async (e) => {
      e.stopPropagation();
      userMenuOpen = false;
      renderUserArea();
      await openLinkPhoneFlow();
    });
    document.getElementById('driveStorageBtn').addEventListener('click', async (e) => {
      e.stopPropagation();
      const btn = e.currentTarget;
      if (isDriveConnected()) {
        if (!confirm(lang === 'en' ? 'Disconnect Google Drive? Existing files remain in Drive; new attachments will go to Firestore.' : 'Отключить Google Drive? Существующие файлы останутся в Drive; новые вложения пойдут в Firestore.')) return;
        await disconnectDrive();
        userMenuOpen = false;
        renderUserArea();
        alert(lang === 'en' ? '✓ Drive disconnected.' : '✓ Drive отключён.');
      } else {
        btn.textContent = lang === 'en' ? '⏳ Connecting...' : '⏳ Подключение...';
        btn.disabled = true;
        try {
          await connectDrive();
          userMenuOpen = false;
          renderUserArea();
          alert(lang === 'en' ? '✓ Drive connected! New media will be stored in your Google Drive (2TB).' : '✓ Drive подключён! Новые медиа будут храниться в твоём Google Drive (2TB).');
        } catch (err) {
          console.error(err);
          alert((lang === 'en' ? 'Drive connection failed: ' : 'Ошибка подключения Drive: ') + (err.message || err));
          btn.disabled = false;
          renderUserArea();
        }
      }
    });
    if (isUserAdmin) {
      document.getElementById('adminPanelBtn').addEventListener('click', async (e) => {
        e.stopPropagation();
        userMenuOpen = false;
        renderUserArea();
        await openAdminPanel();
      });
    }
  }
}

document.addEventListener('click', () => { if (userMenuOpen) { userMenuOpen = false; renderUserArea(); } });

function attachAppHandlers() {
  document.getElementById('printBtn').addEventListener('click', () => window.print());
  const searchBtn = document.getElementById('globalSearchBtn');
  if (searchBtn) searchBtn.addEventListener('click', openGlobalSearch);
  // Cmd+K / Ctrl+K to open search anywhere in app
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      openGlobalSearch();
    }
  });
  // These buttons may be moved into user menu — wire only if they exist in header
  const langBtn = document.getElementById('langBtn');
  if (langBtn) langBtn.addEventListener('click', toggleLang);
  const themeBtn = document.getElementById('themeBtn');
  if (themeBtn) themeBtn.addEventListener('click', toggleTheme);
  const fxBtn = document.getElementById('fxBtn');
  if (fxBtn) fxBtn.addEventListener('click', cycleFx);
  const zoomBtn = document.getElementById('zoomBtn');
  if (zoomBtn) {
    zoomBtn.addEventListener('click', cycleZoom);
    updateZoomBtn();
  }
  const menuBtn = document.getElementById('menuBtn');
  if (menuBtn) menuBtn.addEventListener('click', (e) => { e.stopPropagation(); document.body.classList.toggle('sidebar-open'); });
  const overlay = document.getElementById('sidebarOverlay');
  if (overlay) overlay.addEventListener('click', () => document.body.classList.remove('sidebar-open'));
}
function toggleLang() { lang = lang === 'en' ? 'ru' : 'en'; localStorage.setItem('phosphera-lang', lang); if (userMock) { render(); renderChat(); } else renderApp(); }
function toggleTheme() {
  theme = theme === 'dark' ? 'light' : 'dark';
  localStorage.setItem('phosphera-theme', theme);
  document.body.classList.toggle('light', theme === 'light');
  document.documentElement.classList.toggle('light-init', theme === 'light');
  document.documentElement.classList.toggle('dark', theme === 'dark');
  // Update theme-color meta so iOS PWA status bar / safe-area matches
  const dyn = document.getElementById('dynamicThemeColor');
  if (dyn) dyn.setAttribute('content', theme === 'light' ? '#EDE7F6' : '#02050E');
  // Theme changed — re-render effects so rain/stars respect the new theme.
  renderEffects();
}
function cycleFx() { const i = FX_OPTIONS.indexOf(fx); fx = FX_OPTIONS[(i + 1) % FX_OPTIONS.length]; localStorage.setItem('phosphera-fx', fx); renderEffects(); document.querySelectorAll('.fx-toggle .icon').forEach(el => el.textContent = FX_ICONS[fx]); }

const FONT_PRESETS = ['classic', 'modern', 'literary'];
const FONT_LABELS = { classic: '✒ Classic', modern: '◯ Modern', literary: '✦ Literary' };
function cycleFont() {
  const i = FONT_PRESETS.indexOf(fontPreset);
  fontPreset = FONT_PRESETS[(i + 1) % FONT_PRESETS.length];
  localStorage.setItem('phosphera-font', fontPreset);
  document.documentElement.classList.remove('font-modern', 'font-literary');
  if (fontPreset === 'modern') document.documentElement.classList.add('font-modern');
  else if (fontPreset === 'literary') document.documentElement.classList.add('font-literary');
  // Update button label if present
  document.querySelectorAll('.font-toggle .label').forEach(el => el.textContent = FONT_LABELS[fontPreset]);
  showToast(FONT_LABELS[fontPreset]);
}

// UI scale: uses CSS zoom property — actually pro­por­tion­ally scales everything
const ZOOM_SCALES = [
  { id: 'xs', factor: 0.7, label: 'XS' },
  { id: 'sm', factor: 0.8, label: 'S' },
  { id: 'md', factor: 0.9, label: 'M' },
  { id: 'lg', factor: 1.0, label: 'L' },
];
function applyZoom() {
  const scale = ZOOM_SCALES.find(z => z.id === uiScale) || ZOOM_SCALES[3];
  // Bug fix: `zoom` (and the transform fallback below) visually shrinks the
  // whole page, but `vh`-based heights (like .sidebar's height:100vh) are
  // computed from the REAL, un-zoomed viewport. Net effect at e.g. 80% zoom:
  // the sidebar box is sized for the full screen, then rendered at 80% of
  // that size — so it visibly stops short of the bottom edge. Exposing the
  // factor as a CSS var lets vh-based rules divide it back out (see
  // `.sidebar` in base.css) so they still reach the true bottom at any scale.
  document.documentElement.style.setProperty('--ui-scale', String(scale.factor));
  // CSS zoom works in Chrome/Safari/Edge desktop. Firefox falls back to transform scale.
  if (CSS.supports && CSS.supports('zoom', '0.8')) {
    document.body.style.zoom = scale.factor;
  } else {
    if (scale.factor === 1) {
      document.documentElement.style.removeProperty('transform');
      document.documentElement.style.removeProperty('width');
    } else {
      document.documentElement.style.transform = `scale(${scale.factor})`;
      document.documentElement.style.transformOrigin = '0 0';
      document.documentElement.style.width = `${100 / scale.factor}%`;
    }
  }
}
function cycleZoom() {
  const idx = ZOOM_SCALES.findIndex(z => z.id === uiScale);
  uiScale = ZOOM_SCALES[(idx + 1) % ZOOM_SCALES.length].id;
  localStorage.setItem('phosphera-zoom', uiScale);
  applyZoom();
  updateZoomBtn();
}
function updateZoomBtn() {
  const btn = document.getElementById('zoomBtn');
  if (!btn) return;
  const scale = ZOOM_SCALES.find(z => z.id === uiScale) || ZOOM_SCALES[3];
  btn.textContent = scale.label;
  btn.title = `UI scale: ${scale.label} (${Math.round(scale.factor * 100)}%)`;
}

function renderSidebar() {
  const route = getRoute();
  const activeSlug = route.type === 'section' ? route.slug : '';
  const sb = document.getElementById('sidebar');
  let html = `<div class="sidebar-header">${t('appName')}</div><div class="sidebar-tagline">${t('appTagline')}</div><nav class="nav-grid">`;
  html += `<a href="#/" class="nav-item ${route.type === 'calendar' ? 'active' : ''}"><span class="nav-icon">📅</span><span>${lang === 'en' ? 'Calendar' : 'Календарь'}</span></a>`;
  html += `<a href="#/dashboard" class="nav-item ${route.type === 'dashboard' ? 'active' : ''}"><span class="nav-icon">🏠</span><span>${t('dashboard')}</span></a>`;
  getOrderedSections().forEach(s => {
    const title = lang === 'en' ? s.titleEn : s.titleRu;
    html += `<a href="#/s/${s.slug}" class="nav-item ${activeSlug === s.slug ? 'active' : ''}"><span class="nav-icon">${s.icon}</span><span>${title}</span></a>`;
  });
  html += '</nav>';
  // Customize tiles — always-visible button at bottom of sidebar
  html += `<div class="sidebar-customize"><button class="nav-item nav-customize" id="sidebarCustomizeBtn"><span class="nav-icon">⚙</span><span>${t('customize')}</span></button></div>`;
  sb.innerHTML = html;
  // Wire customize button
  const sCust = document.getElementById('sidebarCustomizeBtn');
  if (sCust) sCust.addEventListener('click', () => {
    document.body.classList.remove('sidebar-open');
    openCustomizeTiles();
  });
  // Auto-close mobile sidebar when nav item clicked
  sb.querySelectorAll('.nav-item').forEach(item => {
    item.addEventListener('click', () => document.body.classList.remove('sidebar-open'));
  });
}

function renderDashboard() {
  try {
    return _renderDashboardImpl();
  } catch (e) {
    console.error('[phosphera] renderDashboard FAILED:', e);
    console.error('Stack:', e.stack);
    // Show emergency fallback UI so the user can still navigate
    const contentEl = document.getElementById('content');
    if (contentEl) {
      contentEl.innerHTML = `<div style="padding:40px 20px;max-width:600px;margin:0 auto;text-align:center">
        <h2 style="color:#EF4444;margin-bottom:14px">⚠ Render error</h2>
        <p style="opacity:0.7;margin-bottom:12px">Dashboard couldn't render but your data is safe. Use the sidebar to navigate to any section, or run <code>phospheraDiagnose()</code> in Console.</p>
        <pre style="text-align:left;background:rgba(239,68,68,0.10);padding:12px;border-radius:8px;font-size:11px;overflow:auto;max-height:200px">${escapeHtml(e.message + '\n' + (e.stack || ''))}</pre>
        <button class="btn-primary" onclick="location.reload()" style="margin-top:14px">Reload</button>
      </div>`;
    }
  }
}

function _renderDashboardImpl() {
  document.getElementById('headerTitle').textContent = t('dashboard');
  document.getElementById('printBtn').style.display = 'none';

  const todos = getAllTodos(); // already sorted by deadline (nearest first)
  const orderedSections = getOrderedSections();
  const sectionOptions = orderedSections.map(s => `<option value="${s.slug}">${s.icon} ${lang === 'en' ? s.titleEn : s.titleRu}</option>`).join('');
  const liveBadge = hasApiKey() ? `<span class="ai-window-status live">● LIVE</span>` : `<span class="ai-window-status mock">MOCK</span>`;

  // ─── LEFT COLUMN (main flow) ───
  let leftHtml = '';

  // 1. WELCOME
  // SAFETY: userMock can briefly be null during fast-path startup before
  // onAuth resolves. Fall back to firebaseUser.displayName or email.
  const displayName = (userMock && userMock.name)
    || (firebaseUser && (firebaseUser.displayName || firebaseUser.email))
    || 'friend';
  leftHtml += `<div class="dash-greeting">${t('dashboardWelcome')}, ${escapeHtml(displayName)} ✦</div>`;
  leftHtml += `<p class="dash-sub">${t('taglineLine')} · ${orderedSections.length} ${t('sectionsCount')}</p>`;

  // 2. WEBSITES (bookmarks)
  const bookmarks = getBookmarks();
  leftHtml += `<div class="featured-buttons" style="margin-bottom:24px" id="bookmarksRow">`;
  bookmarks.forEach(b => {
    leftHtml += `<div class="featured-btn-wrap"><a href="${safeUrl(b.url)}" target="_blank" rel="noopener noreferrer" class="featured-btn" data-bookmark-id="${b.id}">${b.icon || '🔗'} ${escapeHtml(b.label)}</a><button class="bookmark-edit-btn" data-edit-bookmark="${b.id}" title="${lang === 'en' ? 'Edit' : 'Изменить'}">⋯</button></div>`;
  });
  leftHtml += `<button class="featured-btn featured-btn-add" id="addBookmarkBtn">+ ${lang === 'en' ? 'Add' : 'Добавить'}</button>`;
  leftHtml += `</div>`;

  // 3. ADD CARDS:
  //   - Manual add stays in LEFT column (main flow) — used most often, big & easy
  //   - Quick + Bulk go to RIGHT column on desktop (sidebar style as it was before)
  //   - On mobile, the .dash-right element is repositioned via CSS to appear
  //     RIGHT AFTER Manual add (using flex order), not at the bottom.
  leftHtml += `<div class="ai-window">${_renderManualAddInner(sectionOptions)}</div>`;

  // 3.5. PINNED — entries from any section marked with 📌
  const pinnedItems = _getAllPinnedEntries();
  if (pinnedItems.length > 0) {
    leftHtml += `<div class="todo-widget pinned-widget">
      <div class="todo-header">
        <h2>📌 ${lang === 'en' ? 'Pinned' : 'Закреплённые'}</h2>
        <span class="count">${pinnedItems.length}</span>
      </div>
      <div class="pinned-list">`;
    pinnedItems.forEach(p => {
      const previewText = p.text.length > 200 ? p.text.slice(0, 200) + '…' : p.text;
      leftHtml += `<div class="pinned-item" data-pinned-section="${p.sectionSlug}" data-pinned-id="${p.id}">
        <div class="pinned-meta">
          <span class="pinned-section-icon">${p.sectionIcon}</span>
          <span class="pinned-section-name">${escapeHtml(p.sectionTitle)}</span>
          <button class="pinned-unpin" data-unpin-pinned="${p.sectionSlug}|${p.id}" title="${lang === 'en' ? 'Unpin' : 'Открепить'}">📌</button>
        </div>
        <div class="pinned-text">${linkifyText(previewText)}</div>
      </div>`;
    });
    leftHtml += `</div></div>`;
  }

  // 4. DEADLINES — pure list, no add row (add happens in tab above)
  const openTodos = todos.filter(t => !t.done);
  const doneTodos = todos.filter(t => t.done);
  leftHtml += `<div class="todo-widget deadlines-widget">
    <div class="todo-header">
      <h2>📅 ${lang === 'en' ? 'Deadlines' : 'Дедлайны'}</h2>
      <span class="count">${openTodos.length}</span>
    </div>`;
  if (openTodos.length === 0 && doneTodos.length === 0) {
    leftHtml += `<p class="empty">${t('todoEmpty')}</p>`;
  } else {
    leftHtml += `<div class="deadlines-scroll-wrap">
      <div class="deadlines-list" id="deadlinesList">`;
    // Open todos first, sorted by nearest deadline (getAllTodos already sorts that way)
    openTodos.forEach(td => {
      const u = getUrgency(td.deadline);
      leftHtml += `<div class="todo-item deadline-card ${u}" data-todo-section="${td.sectionSlug}" data-todo-id="${td.id}">
        <button class="tdo-check" data-toggle-todo="${td.sectionSlug}|${td.id}"></button>
        <div class="deadline-card-body">
          <div class="deadline-card-meta">
            <span class="todo-section-icon">${td.sectionIcon}</span>
            <span class="deadline-pill ${u}">${formatDeadline(td.deadline)}</span>
          </div>
          <div class="todo-text">${escapeHtml(td.text)}</div>
        </div>
      </div>`;
    });
    // Done todos at end, dimmed
    doneTodos.slice(0, 10).forEach(td => {
      const u = getUrgency(td.deadline);
      leftHtml += `<div class="todo-item deadline-card done" data-todo-section="${td.sectionSlug}" data-todo-id="${td.id}">
        <button class="tdo-check checked" data-toggle-todo="${td.sectionSlug}|${td.id}"></button>
        <div class="deadline-card-body">
          <div class="deadline-card-meta">
            <span class="todo-section-icon">${td.sectionIcon}</span>
            <span class="deadline-pill ${u}">${formatDeadline(td.deadline)}</span>
          </div>
          <div class="todo-text">${escapeHtml(td.text)}</div>
        </div>
      </div>`;
    });
    leftHtml += `</div></div>`;
  }
  leftHtml += `</div>`;

  // 5. TODAY'S ROUTINES (if any)
  const todayRoutines = getTodayRoutines();
  if (todayRoutines.length > 0) {
    const todayDateStr = toLocalDateStr();
    const completedCount = todayRoutines.filter(r => r.doneToday).length;
    leftHtml += `<div class="todo-widget rt-widget">
      <div class="todo-header"><h2>🔁 ${lang === 'en' ? "Today's routines" : 'Сегодняшние рутины'}</h2><span class="count">${completedCount}/${todayRoutines.length}</span></div>
      <div class="rt-widget-list">`;
    todayRoutines.forEach(r => {
      leftHtml += `<div class="rt-widget-item ${r.doneToday ? 'done' : ''}">
        <button class="tdo-check ${r.doneToday ? 'checked' : ''}" data-dash-toggle-rt="${r.id}" data-rt-date="${todayDateStr}"></button>
        <span class="rt-icon">${r.icon || '🔁'}</span>
        <span class="rt-name">${escapeHtml(r.name)}</span>
      </div>`;
    });
    leftHtml += `</div></div>`;
  }

  // 6. OPEN TASKS (without deadlines) — per-section, with hide option
  const tileS = loadTileSettings();
  const dashHidden = new Set(tileS.dashHiddenSections || []);
  const dashHiddenEntries = new Set(tileS.dashHiddenEntries || []);
  const openGroupsAll = getAllOpenTasks();
  // Filter out hidden entries from each group; then filter out groups that became empty
  const openGroups = openGroupsAll
    .filter(g => !dashHidden.has(g.section.slug))
    .map(g => ({
      ...g,
      items: g.items.filter(e => !dashHiddenEntries.has(g.section.slug + '|' + e.id))
    }))
    .filter(g => g.items.length > 0);
  const totalOpen = openGroups.reduce((sum, g) => sum + g.items.length, 0);
  const hiddenSecCount = openGroupsAll.length - openGroupsAll.filter(g => dashHidden.has(g.section.slug)).length;
  const hiddenEntryCount = dashHiddenEntries.size;
  const anyHidden = (dashHidden.size > 0) || (hiddenEntryCount > 0);
  leftHtml += `<div class="todo-widget">
    <div class="todo-header" style="display:flex;align-items:center;gap:8px">
      <h2 style="margin:0;flex:1">📋 ${t('todoOpen')}</h2>
      <span class="count">${totalOpen}</span>
      ${anyHidden ? `<button class="btn-ghost" id="restoreHiddenSectionsBtn" style="font-size:10px;padding:4px 8px" title="${lang === 'en' ? 'Restore hidden items' : 'Восстановить скрытые'}">+${dashHidden.size + hiddenEntryCount} ${lang === 'en' ? 'hidden' : 'скрытых'}</button>` : ''}
    </div>
    <div class="todo-list">`;
  if (openGroups.length === 0) leftHtml += `<p class="empty">${t('todoOpenEmpty')}</p>`;
  else openGroups.forEach(g => {
    const title = lang === 'en' ? g.section.titleEn : g.section.titleRu;
    leftHtml += `<div class="todo-section-group" data-open-group="${g.section.slug}">
      <div class="todo-section-label">
        <span class="icon">${g.section.icon}</span>
        <span>${title}</span>
        <span style="opacity:0.5">·</span>
        <span style="opacity:0.6">${g.items.length}</span>
        <button class="todo-section-hide" data-hide-open-group="${g.section.slug}" title="${lang === 'en' ? 'Hide this section from dashboard' : 'Скрыть весь раздел'}">✕</button>
      </div>`;
    g.items.forEach(td => {
      leftHtml += `<div class="todo-item" data-todo-section="${td.sectionSlug}" data-todo-id="${td.id}">
        <button class="tdo-check" data-toggle-todo="${td.sectionSlug}|${td.id}"></button>
        <span class="todo-text">${escapeHtml(td.text)}</span>
        <button class="todo-entry-hide" data-hide-entry="${td.sectionSlug}|${td.id}" title="${lang === 'en' ? 'Hide this note from dashboard' : 'Скрыть эту заметку с дашборда'}">✕</button>
      </div>`;
    });
    leftHtml += `</div>`;
  });
  leftHtml += `</div></div>`;

  // Section tiles + separate Quick/Bulk add cards removed here: tiles duplicated
  // the sidebar nav, and Quick/Bulk add are now just... the AI chat (see the
  // docked chat bar) — no separate UI needed for something chat already does.
  document.getElementById('content').innerHTML = `<div class="dash-layout dash-layout-single">${leftHtml}</div>`;
  attachDashboardHandlers();
}

// Move the .dash-right element to appear right after Manual add card on mobile.
function _placeDashRightAfterManual_unused() { /* no-op kept for compat */ }

// Helpers: render Quick add inner + Bulk add inner — reused in mobile slider + desktop column
// Manual add card — same form as old TO DO add row, now its own tab
function _renderManualAddInner(sectionOptions) {
  return `<div class="ai-window-header">
    <div class="ai-window-title">✏️ ${lang === 'en' ? 'Add manually' : 'Добавить вручную'}</div>
  </div>
  <div class="manual-add-form">
    <input type="text" id="todoText" placeholder="${t('todoTaskPlaceholder')}" />
    <div class="manual-add-row">
      <select id="todoSection">${sectionOptions}</select>
      <select id="todoSubtopic" style="display:none"></select>
    </div>
    <div class="manual-add-row">
      <input type="date" id="todoDate" title="${lang === 'en' ? 'Optional deadline' : 'Срок (необязательно)'}" />
      <button class="btn-primary" id="todoAddBtn" disabled style="flex:1">+ ${lang === 'en' ? 'Add' : 'Добавить'}</button>
    </div>
  </div>`;
}

function _renderAIQuickInner(orderedSections, liveBadge) {
  let html = `<div class="ai-window-header">
    <div class="ai-window-title">✨ ${t('aiQuickTitle')}</div>${liveBadge}
  </div>
  <textarea id="aiQuickText" placeholder="${t('aiQuickPlaceholder')}" style="width:100%;min-height:80px;display:block;box-sizing:border-box;color:var(--text-primary);background:var(--input-bg);"></textarea>
  <div class="ai-window-actions" style="display:flex;gap:8px;margin-top:10px">
    <button class="btn-primary" id="aiQuickBtn" style="flex:1" ${aiQuickLoading ? 'disabled' : ''}>${aiQuickLoading ? t('chatThinking') + '...' : t('aiQuickAdd')}</button>
  </div>`;
  if (aiQuickResult) {
    const sec = getSection(aiQuickResult.section);
    const sd = getSectionData(aiQuickResult.section);
    const subtopics = sd.subtopics || [];
    const subSelectQ = subtopics.length > 0
      ? `<select id="aiQuickSub" style="font-size:11px;padding:6px 8px;width:auto">
          <option value="">${t('noSubtopic')}</option>
          ${subtopics.map(st => `<option value="${st.id}" ${aiQuickResult.subtopicId === st.id ? 'selected' : ''}>${escapeHtml(st.name)}</option>`).join('')}
         </select>` : '';
    html += `<div class="ai-result">
      <div class="ai-result-row"><span class="ai-result-label">${t('chatSection')}</span><span>${sec.icon} ${lang === 'en' ? sec.titleEn : sec.titleRu}</span></div>
      <div class="ai-result-row">
        <span class="ai-result-label">${t('chatDeadline')}</span>
        <input type="date" id="aiQuickDate" value="${aiQuickResult.deadline || ''}" style="font-size:11px;padding:5px 7px;width:auto" />
      </div>
      <div style="display:flex;gap:6px;margin-top:8px;flex-wrap:wrap">
        <button class="btn-primary" id="aiQuickConfirm" style="font-size:12px;padding:7px 12px">✓ ${t('chatAddIt')}</button>
        <select id="aiQuickChange" style="font-size:11px;padding:6px 8px;width:auto">${orderedSections.map(s => `<option value="${s.slug}" ${s.slug===aiQuickResult.section?'selected':''}>${s.icon} ${lang === 'en' ? s.titleEn : s.titleRu}</option>`).join('')}</select>
        ${subSelectQ}
        <button class="btn-ghost" id="aiQuickCancel" style="font-size:12px;padding:6px 10px">${t('chatCancel')}</button>
      </div>
    </div>`;
  }
  return html;
}

function _renderAIBulkInner(orderedSections, liveBadge) {
  let html = `<div class="ai-window-header">
    <div class="ai-window-title">📥 ${t('aiBulkTitle')}</div>${liveBadge}
  </div>
  <textarea id="aiBulkText" placeholder="${t('aiBulkPlaceholder')}" style="width:100%;min-height:100px;display:block;box-sizing:border-box;color:var(--text-primary);background:var(--input-bg);"></textarea>
  <div class="ai-window-actions" style="display:flex;gap:8px;margin-top:10px">
    <button class="btn-primary" id="aiBulkBtn" style="flex:1" ${aiBulkLoading ? 'disabled' : ''}>${aiBulkLoading ? t('chatThinking') + '...' : t('aiBulkProcess')}</button>
  </div>`;
  if (aiBulkResults && aiBulkResults.length > 0) {
    html += `<div style="margin-top:12px">`;
    aiBulkResults.forEach((item, idx) => {
      const sd = getSectionData(item.section || 'notes');
      const subtopics = sd.subtopics || [];
      const subSelB = subtopics.length > 0
        ? `<select data-bulk-sub="${idx}" class="bulk-sub-sel">
            <option value="">${t('noSubtopic')}</option>
            ${subtopics.map(st => `<option value="${st.id}" ${item.subtopicId === st.id ? 'selected' : ''}>${escapeHtml(st.name)}</option>`).join('')}
           </select>` : '';
      html += `<div class="ai-bulk-item ${item.excluded ? 'excluded' : ''}">
        <input type="checkbox" ${item.excluded ? '' : 'checked'} data-bulk-toggle="${idx}" style="width:auto;margin-top:3px" />
        <div class="bulk-text">${escapeHtml(item.text)}</div>
        <div class="bulk-controls">
          <select data-bulk-section="${idx}" class="bulk-sec-sel">${orderedSections.map(s => `<option value="${s.slug}" ${s.slug===item.section?'selected':''}>${s.icon} ${lang === 'en' ? s.titleEn : s.titleRu}</option>`).join('')}</select>
          ${subSelB}
          <input type="date" data-bulk-date="${idx}" class="bulk-date-input" value="${item.deadline || ''}" title="${lang === 'en' ? 'Set/change deadline' : 'Установить/изменить срок'}" />
        </div>
      </div>`;
    });
    const includedCount = aiBulkResults.filter(x => !x.excluded).length;
    html += `<div style="display:flex;gap:6px;margin-top:10px">
      <button class="btn-primary" id="aiBulkAddAll" style="flex:1;font-size:12px;padding:8px">${t('aiBulkAddAll')} (${includedCount})</button>
      <button class="btn-ghost" id="aiBulkClear" style="font-size:12px;padding:7px 12px">${t('aiBulkClear')}</button>
    </div></div>`;
  }
  return html;
}

// Legacy stub — no slider anymore, cards are stacked in main flow.
function _wireAIMobileTabs() { /* no-op */ }

function attachDashboardHandlers() {
  // Customize tiles button
  const customizeBtn = document.getElementById('customizeTilesBtn');
  if (customizeBtn) customizeBtn.addEventListener('click', openCustomizeTiles);

  // Bookmark handlers
  const addBmBtn = document.getElementById('addBookmarkBtn');
  if (addBmBtn) addBmBtn.addEventListener('click', () => openBookmarkModal(null));
  document.querySelectorAll('[data-edit-bookmark]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = btn.getAttribute('data-edit-bookmark');
      openBookmarkModal(id);
    });
  });
  // Long-press on a bookmark wrap to reveal edit dot (mobile)
  document.querySelectorAll('#bookmarksRow .featured-btn-wrap').forEach(wrap => {
    let pressTimer = null;
    let didLongPress = false;
    const startPress = () => {
      didLongPress = false;
      pressTimer = setTimeout(() => {
        // Reveal the edit dot
        document.querySelectorAll('#bookmarksRow .featured-btn-wrap.show-edit').forEach(w => w.classList.remove('show-edit'));
        wrap.classList.add('show-edit');
        didLongPress = true;
        // Haptic feedback if available
        if (navigator.vibrate) navigator.vibrate(20);
      }, 500);
    };
    const cancelPress = () => { if (pressTimer) { clearTimeout(pressTimer); pressTimer = null; } };
    wrap.addEventListener('touchstart', startPress, { passive: true });
    wrap.addEventListener('touchend', cancelPress);
    wrap.addEventListener('touchmove', cancelPress);
    wrap.addEventListener('touchcancel', cancelPress);
    // If long-press happened, prevent the link's click
    wrap.querySelector('.featured-btn').addEventListener('click', (e) => {
      if (didLongPress) { e.preventDefault(); didLongPress = false; }
    });
  });
  // Tap outside bookmarks → hide edit dots
  document.addEventListener('click', (e) => {
    if (!e.target.closest('#bookmarksRow .featured-btn-wrap')) {
      document.querySelectorAll('#bookmarksRow .featured-btn-wrap.show-edit').forEach(w => w.classList.remove('show-edit'));
    }
  });

  // Today's routines toggle (on dashboard)
  document.querySelectorAll('[data-dash-toggle-rt]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-dash-toggle-rt');
      const date = btn.getAttribute('data-rt-date');
      toggleRoutineDone(id, date);
      renderDashboard();
    });
  });

  // Manual add handlers — work on BOTH mobile slider copy AND desktop right column copy
  const allManualForms = document.querySelectorAll('.manual-add-form');
  allManualForms.forEach(form => {
    const text = form.querySelector('#todoText, [id="todoText"]');
    const sec = form.querySelector('#todoSection, [id="todoSection"]');
    const subSel = form.querySelector('#todoSubtopic, [id="todoSubtopic"]');
    const date = form.querySelector('#todoDate, [id="todoDate"]');
    const btn = form.querySelector('#todoAddBtn, [id="todoAddBtn"]');
    if (!text || !btn) return;
    const refreshSubtopics = () => {
      if (!subSel) return;
      const slug = sec.value;
      const sd = getSectionData(slug);
      const subs = sd.subtopics || [];
      if (subs.length === 0) {
        subSel.style.display = 'none';
        subSel.innerHTML = '';
        return;
      }
      subSel.style.display = '';
      const noSubLabel = lang === 'en' ? 'No subtopic' : 'Без подтемы';
      subSel.innerHTML = `<option value="">${noSubLabel}</option>` + subs.map(st =>
        `<option value="${st.id}">${escapeHtml(st.name)}</option>`
      ).join('');
    };
    refreshSubtopics();
    const updateBtn = () => { btn.disabled = !text.value.trim(); };
    updateBtn();
    text.addEventListener('input', updateBtn);
    sec.addEventListener('change', () => { refreshSubtopics(); updateBtn(); });
    if (date) date.addEventListener('change', updateBtn);
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      const slug = sec.value;
      const subId = subSel ? (subSel.value || null) : null;
      const d = getSectionData(slug);
      d.entries = [{
        id: Date.now().toString(),
        text: text.value.trim(),
        createdAt: Date.now(),
        deadline: date && date.value ? date.value : null,
        done: false,
        subtopicId: subId
      }, ...(d.entries || [])];
      updateSectionData(slug, d);
      const sec2 = getSection(slug);
      showToast((lang === 'en' ? 'Added to ' : 'Добавлено в ') + (sec2 ? sec2.icon + ' ' + (lang === 'en' ? sec2.titleEn : sec2.titleRu) : slug));
      renderDashboard();
    });
  });
  document.querySelectorAll('[data-unpin-pinned]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const [slug, id] = btn.getAttribute('data-unpin-pinned').split('|');
      const d = getSectionData(slug);
      d.entries = (d.entries || []).map(en =>
        en.id === id ? { ...en, pinned: false, mtime: Date.now() } : en
      );
      updateSectionData(slug, d);
      showToast(lang === 'en' ? '✓ Unpinned' : '✓ Откреплено');
      renderDashboard();
    });
  });
  document.querySelectorAll('.pinned-item').forEach(el => {
    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-unpin-pinned]')) return;
      location.hash = '#/s/' + el.getAttribute('data-pinned-section');
    });
  });
  document.querySelectorAll('[data-hide-open-group]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const slug = btn.getAttribute('data-hide-open-group');
      const s = loadTileSettings();
      if (!Array.isArray(s.dashHiddenSections)) s.dashHiddenSections = [];
      if (!s.dashHiddenSections.includes(slug)) s.dashHiddenSections.push(slug);
      saveTileSettings(s);
      showToast(lang === 'en' ? '✓ Hidden from dashboard' : '✓ Скрыто с дашборда');
      renderDashboard();
    });
  });
  document.querySelectorAll('[data-hide-entry]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const key = btn.getAttribute('data-hide-entry');
      const s = loadTileSettings();
      if (!Array.isArray(s.dashHiddenEntries)) s.dashHiddenEntries = [];
      if (!s.dashHiddenEntries.includes(key)) s.dashHiddenEntries.push(key);
      saveTileSettings(s);
      showToast(lang === 'en' ? '✓ Note hidden' : '✓ Заметка скрыта');
      renderDashboard();
    });
  });
  const restoreHiddenBtn = document.getElementById('restoreHiddenSectionsBtn');
  if (restoreHiddenBtn) restoreHiddenBtn.addEventListener('click', () => {
    const s = loadTileSettings();
    s.dashHiddenSections = [];
    s.dashHiddenEntries = [];
    saveTileSettings(s);
    showToast(lang === 'en' ? '✓ All restored' : '✓ Восстановлено');
    renderDashboard();
  });
  document.querySelectorAll('[data-toggle-todo]').forEach(el => {
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const [slug, id] = el.getAttribute('data-toggle-todo').split('|');
      const d = getSectionData(slug);
      d.entries = (d.entries || []).map(en => {
        if (en.id !== id) return en;
        const nowDone = !en.done;
        return { ...en, done: nowDone, doneAt: nowDone ? Date.now() : null };
      });
      updateSectionData(slug, d); renderDashboard();
    });
  });
  document.querySelectorAll('.todo-item').forEach(el => {
    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-toggle-todo]')) return;
      location.hash = '#/s/' + el.getAttribute('data-todo-section');
    });
  });

  // ─── AI Quick + Bulk handlers (single copy in DOM now) ───
  const aiQuickBtn = document.getElementById('aiQuickBtn');
  if (aiQuickBtn) {
    aiQuickBtn.addEventListener('click', async () => {
      const inp = document.getElementById('aiQuickText');
      const txt = inp ? inp.value.trim() : '';
      if (!txt) return;
      aiQuickLoading = true; aiQuickResult = null; renderDashboard();
      const result = await claudeCategorize(txt);
      aiQuickResult = { ...result, text: txt, subtopicId: result.subtopicId || null };
      aiQuickLoading = false;
      renderDashboard();
      const inp2 = document.getElementById('aiQuickText'); if (inp2) inp2.value = txt;
    });
  }
  if (aiQuickResult) {
    const confirmBtn = document.getElementById('aiQuickConfirm');
    if (confirmBtn) confirmBtn.addEventListener('click', () => {
      const slug = aiQuickResult.section;
      const d = getSectionData(slug);
      const dateInp = document.getElementById('aiQuickDate');
      const subSel = document.getElementById('aiQuickSub');
      const deadline = dateInp ? (dateInp.value || null) : (aiQuickResult.deadline || null);
      const subtopicId = subSel ? (subSel.value || null) : (aiQuickResult.subtopicId || null);
      d.entries = [{
        id: Date.now().toString(),
        text: aiQuickResult.text,
        createdAt: Date.now(),
        deadline,
        done: false,
        subtopicId
      }, ...(d.entries || [])];
      updateSectionData(slug, d);
      const sec = getSection(slug);
      showToast((lang === 'en' ? 'Added to ' : 'Добавлено в ') + (sec ? sec.icon + ' ' + (lang === 'en' ? sec.titleEn : sec.titleRu) : slug));
      aiQuickResult = null;
      renderDashboard();
    });
    const cancelBtn = document.getElementById('aiQuickCancel');
    if (cancelBtn) cancelBtn.addEventListener('click', () => { aiQuickResult = null; renderDashboard(); });
    const changeSel = document.getElementById('aiQuickChange');
    if (changeSel) changeSel.addEventListener('change', (e) => {
      aiQuickResult.section = e.target.value;
      aiQuickResult.subtopicId = null;
      renderDashboard();
    });
    const qDate = document.getElementById('aiQuickDate');
    if (qDate) qDate.addEventListener('change', () => { aiQuickResult.deadline = qDate.value || null; });
    const qSub = document.getElementById('aiQuickSub');
    if (qSub) qSub.addEventListener('change', () => { aiQuickResult.subtopicId = qSub.value || null; });
  }

  const aiBulkBtn = document.getElementById('aiBulkBtn');
  if (aiBulkBtn) {
    aiBulkBtn.addEventListener('click', async () => {
      const inp = document.getElementById('aiBulkText');
      const txt = inp ? inp.value.trim() : '';
      if (!txt) return;
      aiBulkLoading = true; aiBulkResults = null; renderDashboard();
      const items = await claudeBulkSplit(txt);
      aiBulkResults = items.map(it => ({ ...it, excluded: false, subtopicId: it.subtopicId || null }));
      aiBulkLoading = false;
      renderDashboard();
      const inp2 = document.getElementById('aiBulkText'); if (inp2) inp2.value = txt;
    });
  }
  if (aiBulkResults && aiBulkResults.length > 0) {
    document.querySelectorAll('[data-bulk-toggle]').forEach(cb => {
      cb.addEventListener('change', () => {
        const idx = parseInt(cb.getAttribute('data-bulk-toggle'));
        aiBulkResults[idx].excluded = !cb.checked;
        renderDashboard();
      });
    });
    document.querySelectorAll('[data-bulk-section]').forEach(sel => {
      sel.addEventListener('change', () => {
        const idx = parseInt(sel.getAttribute('data-bulk-section'));
        aiBulkResults[idx].section = sel.value;
        aiBulkResults[idx].subtopicId = null;
        renderDashboard();
      });
    });
    document.querySelectorAll('[data-bulk-sub]').forEach(sel => {
      sel.addEventListener('change', () => {
        const idx = parseInt(sel.getAttribute('data-bulk-sub'));
        aiBulkResults[idx].subtopicId = sel.value || null;
      });
    });
    document.querySelectorAll('[data-bulk-date]').forEach(inp => {
      inp.addEventListener('change', () => {
        const idx = parseInt(inp.getAttribute('data-bulk-date'));
        aiBulkResults[idx].deadline = inp.value || null;
      });
    });
    const addAllBtn = document.getElementById('aiBulkAddAll');
    if (addAllBtn) addAllBtn.addEventListener('click', () => {
      let added = 0;
      aiBulkResults.forEach(item => {
        if (item.excluded) return;
        const slug = item.section || 'notes';
        const d = getSectionData(slug);
        d.entries = [{
          id: Date.now().toString() + Math.random(),
          text: item.text,
          createdAt: Date.now(),
          deadline: item.deadline || null,
          done: false,
          subtopicId: item.subtopicId || null
        }, ...(d.entries || [])];
        updateSectionData(slug, d);
        added++;
      });
      if (added > 0) {
        showToast((lang === 'en' ? `Added ${added} note${added > 1 ? 's' : ''}` : `Добавлено ${added} ${added === 1 ? 'заметка' : 'заметок'}`));
      }
      aiBulkResults = null;
      renderDashboard();
    });
    const clearBtn = document.getElementById('aiBulkClear');
    if (clearBtn) clearBtn.addEventListener('click', () => { aiBulkResults = null; renderDashboard(); });
  }
}

// ============================================================================
// CALENDAR SCREEN — day-view timetable + week agenda (Phase 2 of the mobile
// redesign, revised). Additive: reachable via the sidebar nav item, does not
// replace or touch Dashboard/sections. Reuses the same getSectionData/
// updateSectionData primitives as the rest of the app, so entries created
// here are ordinary entries and show up everywhere else too.
//
// Interaction model (revised per feedback): tapping a block/chip NO LONGER
// toggles done — that caused accidental completions. Each item now has its
// own small checkbox for that; tapping the item itself opens a details
// modal with edit + delete.
// ============================================================================
let calDate = toLocalDateStr(); // currently viewed day, 'YYYY-MM-DD'
let calViewMode = 'day'; // 'day' | 'week'
const CAL_START_HOUR = 6;
const CAL_END_HOUR = 23;
const CAL_HOUR_PX = 60; // px per hour row — used for both layout and JS math

// Collect every entry, across every section, whose deadline falls on `dateStr`.
// Returns { timed: [...], allDay: [...] } — timed ones have a time-of-day and
// get positioned on the grid; allDay ones (plain date, no time) become chips.
function getCalendarEntriesForDate(dateStr) {
  const sections = getOrderedSections();
  const timed = [];
  const allDay = [];
  sections.forEach(s => {
    const d = getSectionData(s.slug);
    (d.entries || []).forEach(e => {
      if (!e.deadline) return;
      const dl = parseDeadline(e.deadline);
      if (toLocalDateStr(dl) !== dateStr) return;
      const item = { ...e, sectionSlug: s.slug, sectionIcon: s.icon, sectionTitle: lang === 'en' ? s.titleEn : s.titleRu };
      if (deadlineHasTime(e.deadline)) timed.push(item);
      else allDay.push(item);
    });
  });
  timed.sort((a, b) => parseDeadline(a.deadline) - parseDeadline(b.deadline));
  return { timed, allDay };
}

// Monday-start week containing dateStr → array of 7 'YYYY-MM-DD' strings
function getWeekDates(dateStr) {
  const d = parseDeadline(dateStr);
  const dow = (d.getDay() + 6) % 7; // 0=Mon..6=Sun
  d.setDate(d.getDate() - dow);
  const out = [];
  for (let i = 0; i < 7; i++) { out.push(toLocalDateStr(d)); d.setDate(d.getDate() + 1); }
  return out;
}

function renderCalendarScreen() {
  document.getElementById('headerTitle').textContent = `📅 ${lang === 'en' ? 'Calendar' : 'Календарь'}`;
  document.getElementById('printBtn').style.display = 'none';
  if (calViewMode === 'week') renderCalendarWeekView();
  else renderCalendarDayView();
}

function renderCalendarDayView() {
  const dateObj = parseDeadline(calDate);
  const isToday = calDate === toLocalDateStr();
  const dateLabel = dateObj.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-IE', { weekday: 'long', day: 'numeric', month: 'long' });

  const { timed, allDay } = getCalendarEntriesForDate(calDate);
  const routines = getRoutines().filter(r => isRoutineDueOn(r, calDate));

  let hoursHtml = '';
  for (let h = CAL_START_HOUR; h <= CAL_END_HOUR; h++) {
    hoursHtml += `<div class="cal-hour-row" data-cal-hour="${h}">
      <div class="cal-hour-label">${String(h).padStart(2, '0')}:00</div>
      <div class="cal-hour-slot" data-cal-slot="${h}"></div>
    </div>`;
  }

  let blocksHtml = '';
  timed.forEach(e => {
    const dl = parseDeadline(e.deadline);
    const hour = dl.getHours() + dl.getMinutes() / 60;
    if (hour < CAL_START_HOUR || hour > CAL_END_HOUR + 1) return;
    const top = (hour - CAL_START_HOUR) * CAL_HOUR_PX;
    const durationMin = e.durationMin || 30;
    const height = Math.max(30, (durationMin / 60) * CAL_HOUR_PX);
    const timeLabel = dl.toLocaleTimeString(lang === 'ru' ? 'ru-RU' : 'en-IE', { hour: '2-digit', minute: '2-digit' });
    blocksHtml += `<div class="cal-block ${e.done ? 'cal-block-done' : ''}" style="top:${top}px;height:${height}px" data-cal-open="${e.id}|${e.sectionSlug}">
      <button class="cal-check ${e.done ? 'checked' : ''}" data-cal-check="${e.id}|${e.sectionSlug}" title="${lang === 'en' ? 'Toggle done' : 'Отметить'}"></button>
      <span class="cal-block-time">${timeLabel}</span><span class="cal-block-text">${e.sectionIcon} ${escapeHtml(e.text)}</span>
    </div>`;
  });

  const allDayHtml = allDay.length > 0 ? `<div class="cal-allday-row">
    ${allDay.map(e => `<span class="cal-allday-chip ${e.done ? 'cal-block-done' : ''}" data-cal-open="${e.id}|${e.sectionSlug}">
      <button class="cal-check ${e.done ? 'checked' : ''}" data-cal-check="${e.id}|${e.sectionSlug}"></button>${e.sectionIcon} ${escapeHtml(e.text)}
    </span>`).join('')}
  </div>` : '';

  const routineHtml = routines.length > 0 ? `<div class="cal-routine-strip">
    <div class="cal-routine-label">${lang === 'en' ? "Today's routine" : 'Рутина на сегодня'}</div>
    <div class="cal-routine-chips">
      ${routines.map(r => {
        const done = isRoutineDoneOn(r, calDate);
        return `<button class="cal-routine-chip ${done ? 'checked' : ''}" data-toggle-rt="${r.id}" data-rt-date="${calDate}">
          <span class="cal-routine-check"></span>${r.icon || '🔁'} ${escapeHtml(r.name)}
        </button>`;
      }).join('')}
    </div>
  </div>` : '';

  document.getElementById('content').innerHTML = `
    <div class="cal-shell">
      <div class="cal-topbar">
        <button class="btn-icon" id="calPrevDay" title="${lang === 'en' ? 'Previous day' : 'Предыдущий день'}">‹</button>
        <div class="cal-date-wrap">
          <div class="cal-date-label">${dateLabel}</div>
          ${!isToday ? `<button class="cal-today-btn" id="calTodayBtn">${lang === 'en' ? 'Today' : 'Сегодня'}</button>` : ''}
        </div>
        <button class="btn-icon" id="calNextDay" title="${lang === 'en' ? 'Next day' : 'Следующий день'}">›</button>
        <button class="cal-view-toggle" id="calWeekToggle">${lang === 'en' ? 'Week' : 'Неделя'}</button>
      </div>
      ${allDayHtml}
      <div class="cal-grid-wrap">
        <div class="cal-grid">${hoursHtml}<div class="cal-blocks-layer">${blocksHtml}</div></div>
      </div>
      ${routineHtml}
    </div>`;

  attachCalendarDayHandlers();
}

// Agenda-style week view: 7 stacked day cards, each with its timed entries,
// all-day chips, and a routine count — deliberately not a 7-column hour grid,
// which is unusably narrow on a phone. This is "see the whole week", not
// "see every minute of every day" — that's what Day view is for.
function renderCalendarWeekView() {
  const weekDates = getWeekDates(calDate);
  const todayStr = toLocalDateStr();
  const first = parseDeadline(weekDates[0]);
  const last = parseDeadline(weekDates[6]);
  const rangeLabel = `${first.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-IE', { day: 'numeric', month: 'short' })} – ${last.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-IE', { day: 'numeric', month: 'short' })}`;

  const daysHtml = weekDates.map(dateStr => {
    const { timed, allDay } = getCalendarEntriesForDate(dateStr);
    const routines = getRoutines().filter(r => isRoutineDueOn(r, dateStr));
    const dObj = parseDeadline(dateStr);
    const isToday = dateStr === todayStr;
    const weekdayLabel = dObj.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-IE', { weekday: 'short' });
    const dayNum = dObj.getDate();

    const items = [...timed.map(e => ({ ...e, _time: parseDeadline(e.deadline) })), ...allDay.map(e => ({ ...e, _time: null }))];
    let itemsHtml = '';
    if (items.length === 0 && routines.length === 0) {
      itemsHtml = `<div class="cal-week-empty">${lang === 'en' ? 'Nothing scheduled' : 'Ничего не запланировано'}</div>`;
    } else {
      items.forEach(e => {
        const timeLabel = e._time ? e._time.toLocaleTimeString(lang === 'ru' ? 'ru-RU' : 'en-IE', { hour: '2-digit', minute: '2-digit' }) : (lang === 'en' ? 'All day' : 'Весь день');
        itemsHtml += `<div class="cal-week-item ${e.done ? 'cal-block-done' : ''}" data-cal-open="${e.id}|${e.sectionSlug}">
          <button class="cal-check ${e.done ? 'checked' : ''}" data-cal-check="${e.id}|${e.sectionSlug}"></button>
          <span class="cal-week-item-time">${timeLabel}</span>
          <span class="cal-week-item-text">${e.sectionIcon} ${escapeHtml(e.text)}</span>
        </div>`;
      });
      if (routines.length > 0) {
        itemsHtml += `<div class="cal-week-routines">🔁 ${routines.length} ${lang === 'en' ? 'routine(s)' : 'рутин(а)'}</div>`;
      }
    }

    return `<div class="cal-week-day ${isToday ? 'today' : ''}" data-cal-week-jump="${dateStr}">
      <div class="cal-week-day-head">
        <span class="cal-week-day-name">${weekdayLabel}</span>
        <span class="cal-week-day-num">${dayNum}</span>
      </div>
      <div class="cal-week-day-items">${itemsHtml}</div>
    </div>`;
  }).join('');

  document.getElementById('content').innerHTML = `
    <div class="cal-shell">
      <div class="cal-topbar">
        <button class="btn-icon" id="calPrevWeek" title="${lang === 'en' ? 'Previous week' : 'Предыдущая неделя'}">‹</button>
        <div class="cal-date-wrap"><div class="cal-date-label">${rangeLabel}</div></div>
        <button class="btn-icon" id="calNextWeek" title="${lang === 'en' ? 'Next week' : 'Следующая неделя'}">›</button>
        <button class="cal-view-toggle active" id="calWeekToggle">${lang === 'en' ? 'Day' : 'День'}</button>
      </div>
      <div class="cal-week-list">${daysHtml}</div>
    </div>`;

  attachCalendarWeekHandlers();
}

function attachCalendarCommonHandlers() {
  document.getElementById('calWeekToggle').addEventListener('click', () => {
    calViewMode = calViewMode === 'week' ? 'day' : 'week';
    renderCalendarScreen();
  });
  // Checkbox = the ONLY way to toggle done now (no more accidental taps)
  document.querySelectorAll('[data-cal-check]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const [id, slug] = btn.getAttribute('data-cal-check').split('|');
      const d = getSectionData(slug);
      const entry = (d.entries || []).find(x => x.id === id);
      if (!entry) return;
      entry.done = !entry.done;
      updateSectionData(slug, d);
      renderCalendarScreen();
    });
  });
  // Tapping the item (not the checkbox) opens details/edit/delete
  document.querySelectorAll('[data-cal-open]').forEach(el => {
    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-cal-check]')) return;
      const [id, slug] = el.getAttribute('data-cal-open').split('|');
      openCalendarEntryModal({ mode: 'edit', id, slug });
    });
  });
  document.querySelectorAll('.cal-routine-chip').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-toggle-rt');
      toggleRoutineDone(id, calDate);
      renderCalendarScreen();
    });
  });
}

function attachCalendarDayHandlers() {
  document.getElementById('calPrevDay').addEventListener('click', () => {
    const d = parseDeadline(calDate); d.setDate(d.getDate() - 1);
    calDate = toLocalDateStr(d); renderCalendarScreen();
  });
  document.getElementById('calNextDay').addEventListener('click', () => {
    const d = parseDeadline(calDate); d.setDate(d.getDate() + 1);
    calDate = toLocalDateStr(d); renderCalendarScreen();
  });
  const todayBtn = document.getElementById('calTodayBtn');
  if (todayBtn) todayBtn.addEventListener('click', () => { calDate = toLocalDateStr(); renderCalendarScreen(); });

  document.querySelectorAll('.cal-hour-slot').forEach(slot => {
    slot.addEventListener('click', () => {
      const hour = parseInt(slot.getAttribute('data-cal-slot'));
      openCalendarEntryModal({ mode: 'add', dateStr: calDate, hour });
    });
  });

  attachCalendarCommonHandlers();

  if (calDate === toLocalDateStr()) {
    const wrap = document.querySelector('.cal-grid-wrap');
    if (wrap) {
      const nowHour = new Date().getHours() - CAL_START_HOUR;
      wrap.scrollTop = Math.max(0, nowHour * CAL_HOUR_PX - 100);
    }
  }
}

function attachCalendarWeekHandlers() {
  document.getElementById('calPrevWeek').addEventListener('click', () => {
    const d = parseDeadline(calDate); d.setDate(d.getDate() - 7);
    calDate = toLocalDateStr(d); renderCalendarScreen();
  });
  document.getElementById('calNextWeek').addEventListener('click', () => {
    const d = parseDeadline(calDate); d.setDate(d.getDate() + 7);
    calDate = toLocalDateStr(d); renderCalendarScreen();
  });
  // Tap a day's header area (not an item) → jump into Day view for that date
  document.querySelectorAll('.cal-week-day-head').forEach(head => {
    head.addEventListener('click', () => {
      calDate = head.closest('[data-cal-week-jump]').getAttribute('data-cal-week-jump');
      calViewMode = 'day';
      renderCalendarScreen();
    });
  });
  attachCalendarCommonHandlers();
}

// Unified add/edit modal. mode:'add' needs dateStr+hour. mode:'edit' needs id+slug.
function openCalendarEntryModal(opts) {
  const sections = getOrderedSections();
  const isEdit = opts.mode === 'edit';
  let existing = null, existingSlug = opts.slug;
  if (isEdit) {
    for (const s of sections) {
      const d = getSectionData(s.slug);
      const found = (d.entries || []).find(e => e.id === opts.id);
      if (found) { existing = found; existingSlug = s.slug; break; }
    }
    if (!existing) return;
  }
  const defaultSlug = isEdit ? existingSlug : (sections.find(s => s.slug === 'tasks') ? 'tasks' : sections[0].slug);
  const dl = isEdit && existing.deadline ? parseDeadline(existing.deadline) : null;
  const dateStr = isEdit ? (dl ? toLocalDateStr(dl) : toLocalDateStr()) : opts.dateStr;
  const timeStr = isEdit
    ? (dl ? `${String(dl.getHours()).padStart(2,'0')}:${String(dl.getMinutes()).padStart(2,'0')}` : '')
    : `${String(opts.hour).padStart(2, '0')}:00`;
  const existingDur = isEdit ? (existing.durationMin || 30) : 30;

  const overlay = document.createElement('div');
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `<div class="modal" style="max-width:420px">
    <div class="modal-icon">📅</div>
    <div class="modal-title">${isEdit ? (lang === 'en' ? 'Edit entry' : 'Изменить запись') : (lang === 'en' ? 'New entry' : 'Новая запись')}</div>
    <div style="display:flex;flex-direction:column;gap:10px;text-align:left;margin:14px 0">
      <textarea id="calAddText" rows="2" placeholder="${t('entryPlaceholder')}" style="width:100%">${isEdit ? escapeHtml(existing.text) : ''}</textarea>
      <div style="display:flex;gap:8px">
        <select id="calAddSection" style="flex:1" ${isEdit ? 'disabled title="' + (lang === 'en' ? 'Move between sections from the section page' : 'Перемещение между разделами — со страницы раздела') + '"' : ''}>
          ${sections.map(s => `<option value="${s.slug}" ${s.slug === defaultSlug ? 'selected' : ''}>${s.icon} ${lang === 'en' ? s.titleEn : s.titleRu}</option>`).join('')}
        </select>
        <input id="calAddTime" type="time" value="${timeStr}" style="width:auto" />
      </div>
      <div>
        <div style="font-size:11px;color:var(--text-muted);margin-bottom:6px;text-transform:uppercase;letter-spacing:0.1em;font-family:'JetBrains Mono',monospace">${lang === 'en' ? 'Duration' : 'Длительность'}</div>
        <div class="cal-duration-chips">
          <button type="button" class="cal-dur-chip ${existingDur === 30 ? 'active' : ''}" data-dur="30">30 ${lang === 'en' ? 'min' : 'мин'}</button>
          <button type="button" class="cal-dur-chip ${existingDur === 60 ? 'active' : ''}" data-dur="60">1 ${lang === 'en' ? 'hr' : 'ч'}</button>
          <button type="button" class="cal-dur-chip ${existingDur === 180 ? 'active' : ''}" data-dur="180">3 ${lang === 'en' ? 'hrs' : 'ч'}</button>
          <input id="calAddDurCustom" type="number" min="5" step="5" placeholder="${lang === 'en' ? 'custom (min)' : 'своё (мин)'}" style="width:110px" value="${[30,60,180].includes(existingDur) ? '' : existingDur}" />
        </div>
      </div>
      ${isEdit ? `<label style="display:inline-flex;align-items:center;gap:8px;font-size:13px;color:var(--text-secondary);cursor:pointer">
        <input type="checkbox" id="calAddDone" style="width:auto;margin:0" ${existing.done ? 'checked' : ''} />
        ${lang === 'en' ? 'Done' : 'Выполнено'}
      </label>` : ''}
    </div>
    <div class="modal-actions">
      ${isEdit ? `<button class="btn-danger" id="calAddDelete" style="flex:0 0 auto">🗑</button>` : ''}
      <button class="btn-ghost" id="calAddCancel">${t('cancel')}</button>
      <button class="btn-primary" id="calAddSave">${isEdit ? (lang === 'en' ? 'Save' : 'Сохранить') : (lang === 'en' ? 'Add' : 'Добавить')}</button>
    </div>
  </div>`;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#calAddCancel').addEventListener('click', close);

  let selectedDur = existingDur;
  overlay.querySelectorAll('.cal-dur-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      overlay.querySelectorAll('.cal-dur-chip').forEach(c => c.classList.remove('active'));
      chip.classList.add('active');
      selectedDur = parseInt(chip.getAttribute('data-dur'));
      overlay.querySelector('#calAddDurCustom').value = '';
    });
  });
  overlay.querySelector('#calAddDurCustom').addEventListener('input', (e) => {
    overlay.querySelectorAll('.cal-dur-chip').forEach(c => c.classList.remove('active'));
    if (e.target.value) selectedDur = parseInt(e.target.value) || 30;
  });

  if (isEdit) {
    overlay.querySelector('#calAddDelete').addEventListener('click', async () => {
      const ok = await customConfirm(
        lang === 'en' ? 'Delete this entry?' : 'Удалить эту запись?',
        { icon: '🗑', okText: lang === 'en' ? 'Delete' : 'Удалить' }
      );
      if (!ok) return;
      const d = getSectionData(existingSlug);
      d.entries = (d.entries || []).filter(e => e.id !== opts.id);
      updateSectionData(existingSlug, d);
      showToast(lang === 'en' ? '✓ Deleted' : '✓ Удалено');
      close();
      renderCalendarScreen();
    });
  }

  overlay.querySelector('#calAddSave').addEventListener('click', () => {
    const txt = overlay.querySelector('#calAddText').value.trim();
    if (!txt) return;
    const slug = isEdit ? existingSlug : overlay.querySelector('#calAddSection').value;
    const time = overlay.querySelector('#calAddTime').value;
    const newDeadline = time ? `${dateStr}T${time}` : dateStr;
    const d = getSectionData(slug);
    if (isEdit) {
      d.entries = (d.entries || []).map(e => e.id === opts.id ? {
        ...e, text: txt, deadline: newDeadline, durationMin: selectedDur,
        done: overlay.querySelector('#calAddDone') ? overlay.querySelector('#calAddDone').checked : e.done
      } : e);
      updateSectionData(slug, d);
      showToast(lang === 'en' ? '✓ Saved' : '✓ Сохранено');
    } else {
      d.entries = [{
        id: Date.now().toString(),
        text: txt,
        createdAt: Date.now(),
        subtopicId: null,
        deadline: newDeadline,
        durationMin: selectedDur,
        done: false,
      }, ...(d.entries || [])];
      updateSectionData(slug, d);
      showToast(lang === 'en' ? '✓ Added to calendar' : '✓ Добавлено в календарь');
    }
    close();
    renderCalendarScreen();
  });
}

function renderSection(slug) {
  // Remove dashboard-only quick-add FAB when navigating to a section
  const fab = document.getElementById('quickAddFab');
  if (fab) fab.remove();
  const section = getSection(slug);
  if (!section) { location.hash = '#/'; return; }
  // Finance is a special section with its own UI
  if (slug === 'finance') return renderFinance(section);
  if (slug === 'routines') return renderRoutines(section);
  if (slug === 'nutrition') return renderNutrition(section);
  const title = lang === 'en' ? section.titleEn : section.titleRu;
  const desc = lang === 'en' ? section.descEn : section.descRu;
  document.getElementById('headerTitle').textContent = `${section.icon} ${title}`;
  const printBtn = document.getElementById('printBtn');
  printBtn.style.display = 'inline-flex';
  document.getElementById('printLabel').textContent = t('print');

  const data = getSectionData(slug);
  const subtopics = data.subtopics || [];
  const allEntries = data.entries || [];
  const allLinks = data.links || [];
  const activeSub = data.activeSubtopic || null;
  const activeTag = data.activeTag || null;
  const sortMode = data.sortMode || 'newest'; // newest | oldest | deadline | done | open
  let entries = activeSub ? allEntries.filter(e => e.subtopicId === activeSub) : allEntries;
  const links = activeSub ? allLinks.filter(l => l.subtopicId === activeSub) : allLinks;
  // Apply tag filter
  if (activeTag) {
    entries = entries.filter(e => (e.tags || []).includes(activeTag));
  }
  // Collect all unique tags across this section's entries (for filter bar)
  const allTagsSet = new Set();
  allEntries.forEach(e => (e.tags || []).forEach(t => allTagsSet.add(t)));
  const allTags = Array.from(allTagsSet).sort();

  // Apply sort/filter
  if (sortMode === 'open') entries = entries.filter(e => !e.done);
  else if (sortMode === 'done') entries = entries.filter(e => e.done);
  if (sortMode === 'oldest') entries = [...entries].sort((a, b) => a.createdAt - b.createdAt);
  else if (sortMode === 'deadline') {
    entries = [...entries].sort((a, b) => {
      if (a.deadline && !b.deadline) return -1;
      if (!a.deadline && b.deadline) return 1;
      if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
      return b.createdAt - a.createdAt;
    });
  } else if (sortMode === 'newest' || sortMode === 'open' || sortMode === 'done') {
    entries = [...entries].sort((a, b) => b.createdAt - a.createdAt);
  }

  // ALWAYS: pinned items first, then done at the bottom, preserve sort within groups.
  // This keeps important notes visible and completed tasks out of focus.
  if (sortMode !== 'done') {
    entries = [...entries].sort((a, b) => {
      // Done items always last
      const ad = a.done ? 1 : 0;
      const bd = b.done ? 1 : 0;
      if (ad !== bd) return ad - bd;
      // Among same done-status, pinned first
      const ap = a.pinned ? 0 : 1;
      const bp = b.pinned ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return 0; // preserve underlying sort
    });
  } else {
    // Done-only mode: still show pinned first
    entries = [...entries].sort((a, b) => {
      const ap = a.pinned ? 0 : 1;
      const bp = b.pinned ? 0 : 1;
      return ap - bp;
    });
  }

  let html = `<p class="section-meta">${desc}</p>`;
  if (section.featured) {
    html += '<div class="featured-buttons">';
    section.featured.forEach(f => { html += `<a href="${safeUrl(f.url)}" target="_blank" rel="noopener noreferrer" class="featured-btn">${escapeHtml(f.label)}</a>`; });
    html += '</div>';
  }
  html += '<div class="subtopic-bar">';
  const allCount = allEntries.length + allLinks.length;
  html += `<button class="subtopic-chip ${activeSub === null ? 'active' : ''}" data-sub="">${t('all')} <span class="count">${allCount}</span></button>`;
  subtopics.forEach((st, idx) => {
    const cnt = allEntries.filter(e => e.subtopicId === st.id).length + allLinks.filter(l => l.subtopicId === st.id).length;
    const isActive = activeSub === st.id;
    const canMoveLeft = idx > 0;
    const canMoveRight = idx < subtopics.length - 1;
    html += `<button class="subtopic-chip ${isActive ? 'active' : ''}" data-sub="${st.id}">${
      isActive && canMoveLeft ? `<span class="sub-arrow" data-move-sub="${st.id}|left" title="${lang === 'en' ? 'Move left' : 'Влево'}">‹</span>` : ''
    }${escapeHtml(st.name)} <span class="count">${cnt}</span>${
      isActive && canMoveRight ? `<span class="sub-arrow" data-move-sub="${st.id}|right" title="${lang === 'en' ? 'Move right' : 'Вправо'}">›</span>` : ''
    }${isActive ? `<span class="del-x" data-del-sub="${st.id}">✕</span>` : ''}</button>`;
  });
  html += `<button class="subtopic-chip add" id="addSubtopicBtn">${t('addSubtopic')}</button></div>`;

  // Tag filter bar — only shown if section has any tags
  if (allTags.length > 0) {
    html += `<div class="tag-filter-bar">
      <span class="tag-filter-label">🏷</span>
      <button class="tag-filter-chip ${activeTag === null ? 'active' : ''}" data-tag-filter="">${lang === 'en' ? 'All' : 'Все'}</button>`;
    allTags.forEach(tg => {
      const cnt = allEntries.filter(e => (e.tags || []).includes(tg)).length;
      html += `<button class="tag-filter-chip ${activeTag === tg ? 'active' : ''}" data-tag-filter="${escapeHtml(tg)}">#${escapeHtml(tg)} <span class="tag-filter-count">${cnt}</span></button>`;
    });
    html += `</div>`;
  }

  const sortLabels = lang === 'en'
    ? { newest: '↓ Newest', oldest: '↑ Oldest', deadline: '⏱ By deadline', open: '○ Open only', done: '✓ Done only' }
    : { newest: '↓ Новые', oldest: '↑ Старые', deadline: '⏱ По дедлайну', open: '○ Открытые', done: '✓ Сделанные' };
  const doneCount = allEntries.filter(e => e.done).length;
  html += `<div class="entries-header">
    <h2 class="subsection-title" style="margin:0">${t('entries')} <span class="count">${entries.length}</span></h2>
    <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
      ${doneCount > 0 ? `<button class="btn-ghost" id="clearDoneBtn" style="font-size:11.5px;padding:6px 10px" title="${lang === 'en' ? 'Delete all completed' : 'Удалить все выполненные'}">🗑 ${lang === 'en' ? 'Clear done' : 'Очистить'} (${doneCount})</button>` : ''}
      <select id="sortMode" class="sort-select">
        ${Object.entries(sortLabels).map(([k, v]) => `<option value="${k}" ${k === sortMode ? 'selected' : ''}>${v}</option>`).join('')}
      </select>
    </div>
  </div>`;
  const subSelect = subtopics.length > 0
    ? `<select id="entrySubtopic" style="width:auto;min-width:160px"><option value="">${t('noSubtopic')}</option>${subtopics.map(st => `<option value="${st.id}" ${activeSub === st.id ? 'selected' : ''}>${escapeHtml(st.name)}</option>`).join('')}</select>` : '';
  html += `<div class="card no-print" id="newEntryCard" style="margin-bottom:18px">
    <textarea id="entryInput" rows="3" placeholder="${t('entryPlaceholder')}"></textarea>
    <div style="margin-top:10px;display:flex;gap:8px;flex-wrap:wrap;align-items:center">
      <input type="date" id="entryDeadline" style="width:auto;min-width:160px" />${subSelect}
      <button class="btn-ghost att-add-btn" id="entryAttachBtn" type="button" style="padding:9px 14px;font-size:13px">📎 ${lang === 'en' ? 'Attach' : 'Прикрепить'}</button>
      <input type="file" id="entryAttachFile" multiple accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rar,.mp3,.mp4,.mov" style="display:none" />
      <label style="display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--text-muted);cursor:pointer">
        <input type="checkbox" id="entryNoCheck" style="width:auto;margin:0" />
        📝 ${lang === 'en' ? 'Note only' : 'Просто заметка'}
      </label>
    </div>
    <div id="entryPendingAtts" class="att-pending"></div>
    <div style="margin-top:10px;display:flex;justify-content:flex-end">
      <button class="btn-primary" id="addEntryBtn" disabled>${t('addEntry')}</button>
    </div>
  </div>`;

  if (entries.length === 0) html += `<p class="empty">${t('noEntries')}</p>`;
  else entries.forEach(e => {
    const dateStr = new Date(e.createdAt).toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-IE', { dateStyle: 'short', timeStyle: 'short' });
    const subName = e.subtopicId ? (subtopics.find(st => st.id === e.subtopicId)?.name || '') : '';
    const u = e.deadline ? getUrgency(e.deadline) : '';
    const checkbox = e.noCheck
      ? `<span class="entry-note-marker" title="${lang === 'en' ? 'Note (no task)' : 'Заметка (без задачи)'}">📝</span>`
      : `<button class="tdo-check ${e.done ? 'checked' : ''}" data-toggle-entry="${e.id}" title="${t('todo')}"></button>`;
    if (editingEntryId === e.id) {
      const subSelectEdit = subtopics.length > 0
        ? `<select id="editSubtopic_${e.id}" style="width:auto;min-width:140px;font-size:12px"><option value="">${t('noSubtopic')}</option>${subtopics.map(st => `<option value="${st.id}" ${e.subtopicId === st.id ? 'selected' : ''}>${escapeHtml(st.name)}</option>`).join('')}</select>` : '';
      const editAttachmentsHtml = e.attachments && e.attachments.length > 0
        ? renderAttachmentThumbs(e.attachments, { editable: true })
        : '';
      const currentTagsStr = (e.tags || []).join(', ');
      html += `<div class="entry-card ${e.done ? 'done' : ''}">${checkbox}<div class="entry-content entry-edit-form">
        <textarea id="editText_${e.id}">${escapeHtml(e.text)}</textarea>
        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
          <input type="date" id="editDeadline_${e.id}" value="${e.deadline || ''}" style="width:auto;font-size:12px;padding:7px 10px" />
          ${subSelectEdit}
          <button class="btn-ghost att-add-btn" data-add-att-edit="${e.id}" style="padding:7px 12px;font-size:12px">📎 ${lang === 'en' ? 'Attach' : 'Прикрепить'}</button>
          <input type="file" id="editAttFile_${e.id}" multiple accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rar,.mp3,.mp4,.mov" style="display:none" />
        </div>
        <input type="text" id="editTags_${e.id}" placeholder="${lang === 'en' ? '🏷 Tags (comma-separated, e.g. urgent, work)' : '🏷 Теги (через запятую, напр. срочно, работа)'}" value="${escapeHtml(currentTagsStr)}" style="font-size:12px;padding:7px 10px" />
        <label style="display:flex;align-items:center;gap:8px;font-size:12px;color:var(--text-muted);cursor:pointer;margin:4px 0">
          <input type="checkbox" id="editNoCheck_${e.id}" ${e.noCheck ? 'checked' : ''} style="width:auto;margin:0" />
          ${lang === 'en' ? '📝 Note only (no checkbox / not a task)' : '📝 Просто заметка (без галочки задачи)'}
        </label>
        ${editAttachmentsHtml}
        <div class="entry-edit-actions">
          <button class="btn-primary" data-save-entry="${e.id}">${t('saveEdit')}</button>
          <button class="btn-ghost" data-cancel-edit="${e.id}">${t('cancel')}</button>
        </div>
      </div></div>`;
    } else {
      const attachmentsHtml = e.attachments && e.attachments.length > 0
        ? renderAttachmentThumbs(e.attachments, { editable: false })
        : '';
      const tagsHtml = (e.tags && e.tags.length > 0)
        ? e.tags.map(tag => `<span class="entry-tag-chip" data-filter-tag="${escapeHtml(tag)}">#${escapeHtml(tag)}</span>`).join('')
        : '';
      html += `<div class="entry-card ${e.done ? 'done' : ''} ${e.pinned ? 'pinned' : ''}">${checkbox}<div class="entry-content"><div class="entry-text">${linkifyText(e.text)}</div>${attachmentsHtml}<div class="entry-meta"><div class="left">${e.pinned ? `<span class="pin-indicator">📌</span>` : ''}<span>${dateStr}</span>${subName ? `<span class="entry-tag">${escapeHtml(subName)}</span>` : ''}${tagsHtml}${e.deadline ? `<span class="deadline-pill ${u}">${formatDeadline(e.deadline)}</span>` : ''}</div><div style="display:flex;gap:6px;align-items:center"><button class="btn-pin no-print ${e.pinned ? 'pinned' : ''}" data-pin-entry="${e.id}" title="${e.pinned ? (lang === 'en' ? 'Unpin' : 'Открепить') : (lang === 'en' ? 'Pin' : 'Закрепить')}">${e.pinned ? '📌' : '📍'}</button><button class="btn-edit no-print" data-edit-entry="${e.id}" title="${t('edit')}">✎ <span class="btn-label">${t('edit')}</span></button><button class="btn-danger no-print" data-del-entry="${e.id}" title="${t('delete')}">🗑 <span class="btn-label">${t('delete')}</span></button></div></div></div></div>`;
    }
  });

  html += `<h2 class="subsection-title">🔗 ${t('links')} <span class="count">${links.length}</span></h2>`;
  let linkSubSelectHtml = subtopics.length > 0
    ? `<div class="form-row"><div class="form-grow"><select id="linkSubtopic" style="font-size:13px"><option value="">${t('noSubtopic')}</option>${subtopics.map(st => `<option value="${st.id}" ${activeSub === st.id ? 'selected' : ''}>${escapeHtml(st.name)}</option>`).join('')}</select></div></div>` : '';
  html += `<div class="no-print" style="margin-bottom:14px">
    <button class="btn-ghost" id="showLinkFormBtn">+ ${t('addLink')}</button>
    <div id="linkFormWrap" style="display:none;margin-top:10px"><div class="card">
      <div class="form-row"><div class="form-grow"><input id="linkTitleInput" placeholder="${t('linkTitle')}"></div></div>
      <div class="form-row"><div class="form-grow"><input id="linkUrlInput" placeholder="${t('linkUrl')}"></div></div>
      ${linkSubSelectHtml}
      <div style="display:flex;gap:8px;justify-content:flex-end"><button class="btn-ghost" id="cancelLinkBtn">${t('cancel')}</button><button class="btn-primary" id="saveLinkBtn">${t('save')}</button></div>
    </div></div>
  </div>`;
  if (links.length === 0) html += `<p class="empty">${t('noLinks')}</p>`;
  else links.forEach(l => {
    const subName = l.subtopicId ? (subtopics.find(st => st.id === l.subtopicId)?.name || '') : '';
    html += `<div class="link-card"><div style="min-width:0;flex:1"><div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap"><a href="${safeUrl(l.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(l.title)}</a>${subName ? `<span class="entry-tag">${escapeHtml(subName)}</span>` : ''}</div><div class="url" style="word-break:break-all">${escapeHtml(l.url)}</div></div><div class="link-actions"><a href="${safeUrl(l.url)}" target="_blank" rel="noopener noreferrer" class="btn-ghost" style="padding:6px 10px;font-size:12px">${t('openLink')}</a><button class="btn-danger no-print" data-del-link="${l.id}" title="${t('delete')}">🗑 <span class="btn-label">${t('delete')}</span></button></div></div>`;
  });

  document.getElementById('content').innerHTML = html;
  attachSectionHandlers(slug);
}

function attachSectionHandlers(slug) {
  const data = getSectionData(slug);
  const sortSel = document.getElementById('sortMode');
  if (sortSel) {
    sortSel.addEventListener('change', () => {
      updateSectionData(slug, { sortMode: sortSel.value });
      renderSection(slug);
    });
  }
  document.querySelectorAll('.subtopic-chip[data-sub]').forEach(chip => {
    chip.addEventListener('click', (e) => {
      if (e.target.closest('[data-del-sub]')) return;
      const sub = chip.getAttribute('data-sub');
      updateSectionData(slug, { activeSubtopic: sub === '' ? null : sub });
      renderSection(slug);
    });
  });
  // Tag filter bar — click to filter by tag, click "All" to clear
  document.querySelectorAll('[data-tag-filter]').forEach(chip => {
    chip.addEventListener('click', (e) => {
      e.stopPropagation();
      const tag = chip.getAttribute('data-tag-filter');
      updateSectionData(slug, { activeTag: tag === '' ? null : tag });
      renderSection(slug);
    });
  });
  // Inline tag chips on entries — click to filter by that tag
  document.querySelectorAll('.entry-tag-chip[data-filter-tag]').forEach(chip => {
    chip.addEventListener('click', (e) => {
      e.stopPropagation();
      const tag = chip.getAttribute('data-filter-tag');
      updateSectionData(slug, { activeTag: tag });
      renderSection(slug);
    });
  });
  document.querySelectorAll('[data-move-sub]').forEach(x => {
    x.addEventListener('click', (e) => {
      e.stopPropagation();
      const [subId, dir] = x.getAttribute('data-move-sub').split('|');
      const d = getSectionData(slug);
      const list = [...(d.subtopics || [])];
      const idx = list.findIndex(s => s.id === subId);
      if (idx < 0) return;
      const swapWith = dir === 'left' ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= list.length) return;
      [list[idx], list[swapWith]] = [list[swapWith], list[idx]];
      d.subtopics = list;
      updateSectionData(slug, d);
      renderSection(slug);
    });
  });
  document.querySelectorAll('[data-del-sub]').forEach(x => {
    x.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!await customConfirm(t('deleteSubtopic'), { icon: '🗂' })) return;
      const subId = x.getAttribute('data-del-sub');
      const d = getSectionData(slug);
      d.subtopics = d.subtopics.filter(s => s.id !== subId);
      d.entries = d.entries.map(en => en.subtopicId === subId ? { ...en, subtopicId: null } : en);
      d.links = d.links.map(l => l.subtopicId === subId ? { ...l, subtopicId: null } : l);
      d.activeSubtopic = null;
      updateSectionData(slug, d);
      showToast(lang === 'en' ? '✓ Subtopic deleted' : '✓ Подтема удалена');
      renderSection(slug);
    });
  });
  document.getElementById('addSubtopicBtn').addEventListener('click', () => {
    const name = prompt(t('subtopicName'));
    if (!name || !name.trim()) return;
    const d = getSectionData(slug);
    const newSub = { id: 'st_' + Date.now(), name: name.trim() };
    d.subtopics = [...(d.subtopics || []), newSub]; d.activeSubtopic = newSub.id;
    updateSectionData(slug, d); renderSection(slug);
  });
  const entryInput = document.getElementById('entryInput');
  const addBtn = document.getElementById('addEntryBtn');
  entryInput.addEventListener('input', () => { addBtn.disabled = !entryInput.value.trim() && pendingAttachments.length === 0; });

  // Pending attachments for new entry (before save)
  let pendingAttachments = [];
  const renderPendingAtts = () => {
    const container = document.getElementById('entryPendingAtts');
    if (!container) return;
    container.innerHTML = renderAttachmentThumbs(pendingAttachments, { editable: true });
    wireAttachmentThumbs(container, pendingAttachments, {
      onDelete: (id) => {
        pendingAttachments = pendingAttachments.filter(a => a.id !== id);
        renderPendingAtts();
        addBtn.disabled = !entryInput.value.trim() && pendingAttachments.length === 0;
      }
    });
  };

  document.getElementById('entryAttachBtn').addEventListener('click', () => {
    document.getElementById('entryAttachFile').click();
  });
  document.getElementById('entryAttachFile').addEventListener('change', async (ev) => {
    const files = Array.from(ev.target.files || []);
    if (files.length === 0) return;
    await _handlePastedFiles(files);
    ev.target.value = '';
  });

  // ─── Cmd+V / Ctrl+V paste support ───
  // Listen on textarea and also document-level (so paste anywhere on the section page works).
  async function _handlePastedFiles(files) {
    if (!files || files.length === 0) return;
    const btn = document.getElementById('entryAttachBtn');
    if (!btn) return;
    const orig = btn.textContent;
    btn.disabled = true;
    btn.textContent = '⏳ ' + (lang === 'en' ? 'Uploading...' : 'Загрузка...');
    for (const f of files) {
      try {
        const meta = await uploadAttachment(f);
        pendingAttachments.push(meta);
      } catch (err) {
        alert((lang === 'en' ? 'Upload failed: ' : 'Ошибка загрузки: ') + err.message);
      }
    }
    btn.disabled = false;
    btn.textContent = orig;
    renderPendingAtts();
    addBtn.disabled = !entryInput.value.trim() && pendingAttachments.length === 0;
    if (files.length > 0) {
      showToast(lang === 'en' ? `📎 ${files.length} file${files.length > 1 ? 's' : ''} attached` : `📎 Прикреплено: ${files.length}`);
    }
  }

  const pasteHandler = async (e) => {
    if (!e.clipboardData) return;
    const items = Array.from(e.clipboardData.items || []);
    const files = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) {
          // For pasted images from clipboard (e.g. screenshot), name them nicely
          if (!f.name || f.name === 'image.png' || f.name === 'unknown') {
            const ext = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
            const renamed = new File([f], `paste_${Date.now()}.${ext}`, { type: f.type });
            files.push(renamed);
          } else {
            files.push(f);
          }
        }
      }
    }
    if (files.length > 0) {
      e.preventDefault(); // Prevent the file path from being pasted as text
      await _handlePastedFiles(files);
    }
    // If only text was pasted, let default behavior happen (insert into textarea)
  };
  entryInput.addEventListener('paste', pasteHandler);
  // Global paste only when entryInput is NOT focused. Otherwise pasteHandler
  // on the textarea already handled it and we'd double-upload.
  const globalPasteHandler = async (e) => {
    if (!e.clipboardData) return;
    const hasFile = Array.from(e.clipboardData.items || []).some(it => it.kind === 'file');
    if (!hasFile) return;
    const ae = document.activeElement;
    // Skip if any input/textarea is focused — its own paste handler (if any) will run.
    // Critically, entryInput is INPUT-like so its handler already fired.
    if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')) return;
    await pasteHandler(e);
  };
  // Remove any previous global paste handler before adding a new one
  // (renderSection may run multiple times during navigation).
  if (window._phospheraGlobalPaste) {
    document.removeEventListener('paste', window._phospheraGlobalPaste);
  }
  window._phospheraGlobalPaste = globalPasteHandler;
  document.addEventListener('paste', globalPasteHandler);

  // ─── Drag & drop files from Finder/Explorer onto the entry area ───
  const entryArea = document.getElementById('newEntryCard') || entryInput.closest('.card') || entryInput.parentElement;
  if (entryArea) {
    const dragEnter = (e) => { e.preventDefault(); e.stopPropagation(); entryArea.classList.add('drag-over'); };
    const dragLeave = (e) => { e.preventDefault(); e.stopPropagation(); if (e.target === entryArea) entryArea.classList.remove('drag-over'); };
    const dragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
    const drop = async (e) => {
      e.preventDefault(); e.stopPropagation();
      entryArea.classList.remove('drag-over');
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length > 0) await _handlePastedFiles(files);
    };
    entryArea.addEventListener('dragenter', dragEnter);
    entryArea.addEventListener('dragleave', dragLeave);
    entryArea.addEventListener('dragover', dragOver);
    entryArea.addEventListener('drop', drop);
  }

  addBtn.addEventListener('click', () => {
    const txt = entryInput.value.trim();
    if (!txt && pendingAttachments.length === 0) return;
    const subSel = document.getElementById('entrySubtopic');
    const dateInp = document.getElementById('entryDeadline');
    const noCheckInp = document.getElementById('entryNoCheck');
    let subId = subSel ? subSel.value || null : null;
    if (!subId && data.activeSubtopic) subId = data.activeSubtopic;
    const deadline = dateInp && dateInp.value ? dateInp.value : null;
    const isNoCheck = noCheckInp ? noCheckInp.checked : false;
    const d = getSectionData(slug);
    const newEntry = {
      id: Date.now().toString(),
      text: txt || (pendingAttachments.length === 1 ? pendingAttachments[0].name : (lang === 'en' ? `${pendingAttachments.length} attachments` : `${pendingAttachments.length} вложений`)),
      createdAt: Date.now(),
      subtopicId: subId || null,
      deadline: deadline || null,
      done: false,
      noCheck: isNoCheck,
    };
    if (pendingAttachments.length > 0) {
      newEntry.attachments = pendingAttachments.map(a => ({ id: a.id, name: a.name, type: a.type, size: a.size }));
    }
    d.entries = [newEntry, ...(d.entries || [])];
    updateSectionData(slug, d);
    showToast(lang === 'en' ? '✓ Note added' : '✓ Заметка добавлена');
    pendingAttachments = [];
    renderSection(slug);
  });
  // Clear done button
  const clearDoneBtn = document.getElementById('clearDoneBtn');
  if (clearDoneBtn) {
    clearDoneBtn.addEventListener('click', async () => {
      const d = getSectionData(slug);
      const doneCount = (d.entries || []).filter(e => e.done).length;
      if (doneCount === 0) return;
      const msg = lang === 'en'
        ? `Delete all ${doneCount} completed task${doneCount > 1 ? 's' : ''}? This can't be undone.`
        : `Удалить все ${doneCount} выполненных задач? Это нельзя отменить.`;
      if (!await customConfirm(msg, { icon: '🗑' })) return;
      d.entries = (d.entries || []).filter(e => !e.done);
      updateSectionData(slug, d);
      showToast(lang === 'en' ? `✓ Cleared ${doneCount} completed` : `✓ Удалено: ${doneCount}`);
      renderSection(slug);
    });
  }
  document.querySelectorAll('[data-toggle-entry]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-toggle-entry');
      const d = getSectionData(slug);
      d.entries = (d.entries || []).map(en => {
        if (en.id !== id) return en;
        const nowDone = !en.done;
        return { ...en, done: nowDone, doneAt: nowDone ? Date.now() : null };
      });
      updateSectionData(slug, d); renderSection(slug);
    });
  });
  document.querySelectorAll('[data-del-entry]').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (!await customConfirm(t('confirmDelete'))) return;
      const id = btn.getAttribute('data-del-entry');
      const d = getSectionData(slug);
      const entry = (d.entries || []).find(e => e.id === id);
      if (entry && entry.attachments && entry.attachments.length > 0) {
        for (const a of entry.attachments) {
          await deleteAttachment(a.id);
        }
      }
      d.entries = (d.entries || []).filter(e => e.id !== id);
      updateSectionData(slug, d);
      showToast(lang === 'en' ? '✓ Note deleted' : '✓ Заметка удалена');
      renderSection(slug);
    });
  });
  document.querySelectorAll('[data-edit-entry]').forEach(btn => {
    btn.addEventListener('click', () => {
      editingEntryId = btn.getAttribute('data-edit-entry');
      renderSection(slug);
    });
  });
  document.querySelectorAll('[data-pin-entry]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-pin-entry');
      const d = getSectionData(slug);
      d.entries = (d.entries || []).map(en =>
        en.id === id ? { ...en, pinned: !en.pinned, mtime: Date.now() } : en
      );
      const entry = d.entries.find(e => e.id === id);
      updateSectionData(slug, d);
      showToast(entry?.pinned
        ? (lang === 'en' ? '📌 Pinned' : '📌 Закреплено')
        : (lang === 'en' ? 'Unpinned' : 'Откреплено'));
      renderSection(slug);
    });
  });
  document.querySelectorAll('[data-cancel-edit]').forEach(btn => {
    btn.addEventListener('click', () => {
      editingEntryId = null;
      renderSection(slug);
    });
  });
  document.querySelectorAll('[data-save-entry]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-save-entry');
      const newText = document.getElementById('editText_' + id).value.trim();
      const newDeadline = document.getElementById('editDeadline_' + id).value || null;
      const subSelEdit = document.getElementById('editSubtopic_' + id);
      const newSubId = subSelEdit ? (subSelEdit.value || null) : null;
      const tagsInp = document.getElementById('editTags_' + id);
      const newTags = tagsInp
        ? tagsInp.value.split(',').map(t => t.trim().toLowerCase().replace(/^#/, '')).filter(t => t.length > 0)
        : [];
      const noCheckInp = document.getElementById('editNoCheck_' + id);
      const newNoCheck = noCheckInp ? noCheckInp.checked : false;
      const d = getSectionData(slug);
      const existing = (d.entries || []).find(en => en.id === id);
      if (!newText && (!existing.attachments || existing.attachments.length === 0)) return;
      d.entries = (d.entries || []).map(en => en.id === id ? {
        ...en,
        text: newText || en.text,
        deadline: newDeadline,
        subtopicId: newSubId,
        tags: newTags,
        noCheck: newNoCheck,
        // If marked as note-only, ensure 'done' is false (no toggle state needed)
        done: newNoCheck ? false : en.done
      } : en);
      updateSectionData(slug, d);
      editingEntryId = null;
      renderSection(slug);
    });
  });

  // Attach files in edit mode
  document.querySelectorAll('[data-add-att-edit]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-add-att-edit');
      document.getElementById('editAttFile_' + id).click();
    });
  });
  document.querySelectorAll('input[id^="editAttFile_"]').forEach(input => {
    input.addEventListener('change', async (ev) => {
      const id = input.id.replace('editAttFile_', '');
      const files = Array.from(ev.target.files || []);
      if (files.length === 0) return;
      const newMetas = [];
      for (const f of files) {
        try {
          const meta = await uploadAttachment(f);
          newMetas.push(meta);
        } catch (err) {
          alert((lang === 'en' ? 'Upload failed: ' : 'Ошибка загрузки: ') + err.message);
        }
      }
      if (newMetas.length > 0) {
        const d = getSectionData(slug);
        d.entries = (d.entries || []).map(en => en.id === id ? { ...en, attachments: [...(en.attachments || []), ...newMetas.map(m => ({ id: m.id, name: m.name, type: m.type, size: m.size }))] } : en);
        updateSectionData(slug, d);
        renderSection(slug);
      }
      ev.target.value = '';
    });
  });

  // Wire thumbnails on all rendered entries (display + edit-mode delete)
  document.querySelectorAll('.entry-card').forEach(card => {
    const attRow = card.querySelector('.att-row');
    if (!attRow) return;
    const editForm = card.querySelector('.entry-edit-form');
    // If we're in edit mode, attRow has editable buttons
    const isEdit = !!editForm;
    if (isEdit) {
      // Find which entry this is by save-entry button
      const saveBtn = card.querySelector('[data-save-entry]');
      const id = saveBtn ? saveBtn.getAttribute('data-save-entry') : null;
      const d = getSectionData(slug);
      const entry = (d.entries || []).find(e => e.id === id);
      if (!entry) return;
      wireAttachmentThumbs(attRow, entry.attachments || [], {
        onDelete: async (attId) => {
          await deleteAttachment(attId);
          const dd = getSectionData(slug);
          dd.entries = (dd.entries || []).map(en => en.id === id ? { ...en, attachments: (en.attachments || []).filter(a => a.id !== attId) } : en);
          updateSectionData(slug, dd);
          renderSection(slug);
        }
      });
    } else {
      // Display only — find entry by edit-entry button (it's there in non-edit mode)
      const editBtn = card.querySelector('[data-edit-entry]');
      const id = editBtn ? editBtn.getAttribute('data-edit-entry') : null;
      const d = getSectionData(slug);
      const entry = (d.entries || []).find(e => e.id === id);
      if (!entry) return;
      wireAttachmentThumbs(attRow, entry.attachments || []);
    }
  });
  const showBtn = document.getElementById('showLinkFormBtn');
  const wrap = document.getElementById('linkFormWrap');
  showBtn.addEventListener('click', () => { wrap.style.display = 'block'; showBtn.style.display = 'none'; document.getElementById('linkTitleInput').focus(); });
  document.getElementById('cancelLinkBtn').addEventListener('click', () => { wrap.style.display = 'none'; showBtn.style.display = 'inline-flex'; document.getElementById('linkTitleInput').value = ''; document.getElementById('linkUrlInput').value = ''; });
  document.getElementById('saveLinkBtn').addEventListener('click', () => {
    const title = document.getElementById('linkTitleInput').value.trim();
    let url = document.getElementById('linkUrlInput').value.trim();
    if (!title || !url) return;
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
    const subSel = document.getElementById('linkSubtopic');
    let subId = subSel ? subSel.value || null : null;
    if (!subId && data.activeSubtopic) subId = data.activeSubtopic;
    const d = getSectionData(slug);
    d.links = [{ id: Date.now().toString(), title, url, createdAt: Date.now(), subtopicId: subId }, ...(d.links || [])];
    updateSectionData(slug, d); renderSection(slug);
  });
  document.querySelectorAll('[data-del-link]').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (!await customConfirm(t('confirmDelete'))) return;
      const id = btn.getAttribute('data-del-link');
      const d = getSectionData(slug);
      d.links = (d.links || []).filter(l => l.id !== id);
      updateSectionData(slug, d);
      showToast(lang === 'en' ? '✓ Link deleted' : '✓ Ссылка удалена');
      renderSection(slug);
    });
  });
}

// ─── FINANCE SECTION RENDER ───
function renderFinance(section) {
  const slug = 'finance';
  const title = lang === 'en' ? section.titleEn : section.titleRu;
  document.getElementById('headerTitle').textContent = `${section.icon} ${title}`;
  document.getElementById('printBtn').style.display = 'inline-flex';
  document.getElementById('printLabel').textContent = t('print');

  const { txs, legacy } = getFinanceTransactions();
  const stats = calculateFinanceStats(txs);
  const sym = '€'; // base currency
  const today = toLocalDateStr();
  const banks = getBanks();

  // Build daily chart for current month
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const dailyArr = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const key = toLocalDateStr(new Date(now.getFullYear(), now.getMonth(), d));
    dailyArr.push({ day: d, total: stats.dailyExpenses[key] || 0 });
  }
  const maxDaily = Math.max(...dailyArr.map(x => x.total), 1);

  let html = `<div class="fin-shell">

    <!-- Total balance widget -->
    <div class="fin-balance-card">
      <div class="fin-balance-label">${t('finBalance')}</div>
      <div class="fin-balance-value ${stats.balance < 0 ? 'neg' : 'pos'}">${stats.balance >= 0 ? '' : '−'}${sym}${Math.abs(stats.balance).toFixed(2)}</div>
      <div class="fin-balance-meta">
        ${t('finThisMonth')}:
        <span style="color:#34D399"> ${fmtMoney(stats.monthIncome, true)}</span>
        <span style="color:#F87171"> ${stats.monthExpense > 0 ? '−' + sym + stats.monthExpense.toFixed(2) : sym + '0.00'}</span>
        <span style="color:var(--text-muted)"> · ${t('finNet')} ${fmtMoney(stats.monthNet, true)}</span>
      </div>
    </div>

    <!-- Add transaction widget -->
    <div class="fin-add-card">
      <div class="fin-add-title">${t('finAddTx')}</div>
      <div class="fin-tx-type-toggle">
        <button class="fin-type-btn fin-type-expense active" data-type="expense">− ${t('finExpenseType')}</button>
        <button class="fin-type-btn fin-type-income" data-type="income">+ ${t('finIncomeType')}</button>
      </div>
      <div class="fin-add-row">
        <input type="number" id="finAmount" placeholder="${t('finAmount')} (€)" step="0.01" min="0" />
        <select id="finCurrency" title="${t('finCurrency')}" style="flex:0 0 90px">${FIN_CURRENCIES.map(c => `<option value="${c.code}" ${c.code === 'EUR' ? 'selected' : ''}>${c.symbol} ${c.code}</option>`).join('')}</select>
      </div>
      <select id="finBank">
        ${banks.map(b => `<option value="${b.id}">${b.name} (${getCurrencySymbol(b.currency)})</option>`).join('')}
      </select>
      <select id="finCategory">
        ${FIN_CATEGORIES.filter(c => c.type === 'expense').map(c => `<option value="${c.id}">${c.icon} ${lang === 'en' ? c.en : c.ru}</option>`).join('')}
      </select>
      <input type="text" id="finNote" placeholder="${t('finNote')}" />
      <input type="date" id="finDate" value="${today}" />
      <button class="btn-primary" id="finAddBtn">+ ${t('finAddTx')}</button>
    </div>
  </div>`;

  // ─── BANKS PANEL ───
  html += `<div class="fin-banks-panel">
    <div class="fin-banks-header">
      <div class="fin-banks-title">🏦 ${lang === 'en' ? 'Banks & wallets' : 'Банки и кошельки'}</div>
      <button class="btn-ghost fin-add-bank-btn" id="finAddBankBtn">+ ${lang === 'en' ? 'Add bank' : 'Добавить банк'}</button>
    </div>
    <div class="fin-banks-grid">`;
  banks.forEach(b => {
    const balance = getBankBalance(b.id);
    const colorClass = balance < 0 ? 'neg' : (balance > 0 ? 'pos' : 'zero');
    html += `<div class="fin-bank-card" data-bank-id="${b.id}">
      <div class="fin-bank-top">
        <div class="fin-bank-name">${escapeHtml(b.name)}</div>
        <span class="fin-bank-currency">${getCurrencySymbol(b.currency)} ${b.currency}</span>
      </div>
      <div class="fin-bank-balance ${colorClass}">€${balance.toFixed(2)}</div>
      <div class="fin-bank-actions">
        <button class="fin-bank-edit" data-edit-bank="${b.id}" title="${lang === 'en' ? 'Edit balance' : 'Изменить баланс'}">✎</button>
        ${!b.builtin ? `<button class="fin-bank-del" data-del-bank="${b.id}" title="${lang === 'en' ? 'Delete bank' : 'Удалить банк'}">🗑</button>` : ''}
      </div>
    </div>`;
  });
  html += `</div></div>`;

  // ─── Stats panel ───
  if (stats.byCategory.length > 0 || dailyArr.some(d => d.total > 0)) {
    html += `<div class="fin-stats-row">`;
    if (stats.byCategory.length > 0) {
      html += `<div class="fin-stats-card">
        <div class="fin-stats-title">${t('finTopCategories')}</div>`;
      stats.byCategory.slice(0, 5).forEach(item => {
        const cat = getFinCategory(item.cat);
        const pct = stats.monthExpense > 0 ? (item.total / stats.monthExpense * 100) : 0;
        html += `<div class="fin-cat-row">
          <span class="fin-cat-icon">${cat ? cat.icon : '💸'}</span>
          <span class="fin-cat-name">${cat ? (lang === 'en' ? cat.en : cat.ru) : item.cat}</span>
          <span class="fin-cat-bar"><span class="fin-cat-bar-fill" style="width:${pct}%"></span></span>
          <span class="fin-cat-amount">${sym}${item.total.toFixed(2)}</span>
        </div>`;
      });
      html += `</div>`;
    }
    html += `<div class="fin-stats-card">
      <div class="fin-stats-title">${t('finDailyChart')}</div>
      <div class="fin-chart">`;
    dailyArr.forEach(d => {
      const h = (d.total / maxDaily) * 100;
      const isToday = d.day === now.getDate();
      html += `<div class="fin-chart-bar ${isToday ? 'today' : ''}" style="height:${Math.max(h, 2)}%" title="${d.day}: ${sym}${d.total.toFixed(2)}"></div>`;
    });
    html += `</div></div>`;
    html += `</div>`;
  }

  // ─── Recent transactions ───
  html += `<h2 class="subsection-title">📋 ${t('finRecent')} <span class="count">${txs.length}</span></h2>`;
  if (txs.length === 0) {
    html += `<p class="empty">${t('finNoTx')}</p>`;
  } else {
    html += `<div class="fin-tx-list">`;
    txs.slice(0, 50).forEach(tx => {
      const cat = getFinCategory(tx.category);
      const txCurrency = tx.currency || 'EUR';
      const txSym = getCurrencySymbol(txCurrency);
      const bank = tx.bankId ? getBank(tx.bankId) : null;
      const dateStr = new Date(tx.txDate || tx.createdAt).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-IE', { month: 'short', day: 'numeric' });
      const sign = tx.txType === 'income' ? '+' : '−';
      const colorClass = tx.txType === 'income' ? 'pos' : 'neg';
      const amountDisplay = txCurrency === 'EUR' ? `${sign}€${tx.amount.toFixed(2)}` : `${sign}${txSym}${tx.amount.toFixed(2)}`;
      html += `<div class="fin-tx-item">
        <span class="fin-tx-icon">${cat ? cat.icon : (tx.txType === 'income' ? '💵' : '💸')}</span>
        <div class="fin-tx-body">
          <div class="fin-tx-cat">${cat ? (lang === 'en' ? cat.en : cat.ru) : tx.category}${bank ? ` <span class="fin-tx-bank">· ${escapeHtml(bank.name)}</span>` : ''}</div>
          ${tx.note ? `<div class="fin-tx-note">${escapeHtml(tx.note)}</div>` : ''}
          <div class="fin-tx-date">${dateStr}</div>
        </div>
        <div class="fin-tx-amount ${colorClass}">${amountDisplay}</div>
        <button class="btn-danger no-print" data-del-tx="${tx.id}" title="${t('finDelete')}">🗑</button>
      </div>`;
    });
    html += `</div>`;
  }

  // Legacy text notes
  if (legacy.length > 0) {
    html += `<h2 class="subsection-title">📝 ${t('finLegacy')} <span class="count">${legacy.length}</span></h2>`;
    legacy.forEach(e => {
      const dateStr = new Date(e.createdAt).toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-IE', { dateStyle: 'short' });
      html += `<div class="entry-card">
        <div class="entry-content">
          <div class="entry-text">${escapeHtml(e.text)}</div>
          <div class="entry-meta">
            <div class="left"><span>${dateStr}</span></div>
            <button class="btn-danger no-print" data-del-tx="${e.id}">🗑</button>
          </div>
        </div>
      </div>`;
    });
  }

  document.getElementById('content').innerHTML = html;
  attachFinanceHandlers();
}

function attachFinanceHandlers() {
  const slug = 'finance';
  let txType = 'expense';

  // Type toggle
  document.querySelectorAll('.fin-type-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      txType = btn.getAttribute('data-type');
      document.querySelectorAll('.fin-type-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const sel = document.getElementById('finCategory');
      sel.innerHTML = FIN_CATEGORIES.filter(c => c.type === txType)
        .map(c => `<option value="${c.id}">${c.icon} ${lang === 'en' ? c.en : c.ru}</option>`).join('');
    });
  });

  // Add transaction
  document.getElementById('finAddBtn').addEventListener('click', () => {
    const amount = parseFloat(document.getElementById('finAmount').value);
    if (isNaN(amount) || amount <= 0) {
      alert(lang === 'en' ? 'Enter a valid amount in €' : 'Введи правильную сумму в €');
      return;
    }
    const category = document.getElementById('finCategory').value;
    const currency = document.getElementById('finCurrency').value;
    const bankId = document.getElementById('finBank').value;
    const note = document.getElementById('finNote').value.trim();
    const dateStr = document.getElementById('finDate').value;
    const txDate = dateStr ? new Date(dateStr + 'T12:00:00').getTime() : Date.now();

    const d = getSectionData(slug);
    d.entries = [{
      id: Date.now().toString() + Math.random().toString(36).slice(2, 5),
      text: note || (FIN_CATEGORIES.find(c => c.id === category)?.en || category),
      createdAt: Date.now(),
      txDate, txType, amount, currency, category, bankId, note,
      done: false,
    }, ...(d.entries || [])];
    updateSectionData(slug, d);
    renderSection(slug);
  });

  // Delete transaction
  document.querySelectorAll('[data-del-tx]').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (!await customConfirm(t('confirmDelete'))) return;
      const id = btn.getAttribute('data-del-tx');
      const d = getSectionData(slug);
      d.entries = (d.entries || []).filter(e => e.id !== id);
      updateSectionData(slug, d);
      renderSection(slug);
    });
  });

  // ─── BANK HANDLERS ───
  // Edit bank balance (manualBalance)
  document.querySelectorAll('[data-edit-bank]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-edit-bank');
      const bank = getBank(id);
      if (!bank) return;
      const cur = bank.manualBalance || 0;
      const promptMsg = lang === 'en'
        ? `Set baseline balance for ${bank.name} (in €):\n(Current transactions in this bank will be added on top)`
        : `Установи начальный баланс для ${bank.name} (в €):\n(Текущие транзакции на этом банке прибавятся сверху)`;
      const v = prompt(promptMsg, cur.toString());
      if (v === null) return;
      const newBalance = parseFloat(v.replace(',', '.'));
      if (isNaN(newBalance)) {
        alert(lang === 'en' ? 'Invalid number' : 'Неверное число');
        return;
      }
      const banks = getBanks();
      const idx = banks.findIndex(b => b.id === id);
      banks[idx] = { ...banks[idx], manualBalance: newBalance };
      saveBanks(banks);
      renderSection(slug);
    });
  });

  // Delete custom bank
  document.querySelectorAll('[data-del-bank]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-del-bank');
      const bank = getBank(id);
      if (!bank) return;
      const ok = await customConfirm(
        lang === 'en' ? `Delete bank "${bank.name}"? Transactions linked to it will keep their data but lose the bank link.` : `Удалить банк "${bank.name}"? Транзакции останутся, но потеряют привязку к банку.`,
        { icon: '🗑' }
      );
      if (!ok) return;
      saveBanks(getBanks().filter(b => b.id !== id));
      renderSection(slug);
    });
  });

  // Add new bank
  document.getElementById('finAddBankBtn').addEventListener('click', () => {
    const name = prompt(lang === 'en' ? 'Bank/wallet name:' : 'Название банка/кошелька:', '');
    if (!name || !name.trim()) return;
    const currencyOptions = FIN_CURRENCIES.map(c => `${c.code}`).join(', ');
    const currency = prompt(
      (lang === 'en' ? 'Currency (one of: ' : 'Валюта (одна из: ') + currencyOptions + '):',
      'EUR'
    );
    if (!currency) return;
    const cur = currency.trim().toUpperCase();
    if (!FIN_CURRENCIES.find(c => c.code === cur)) {
      alert(lang === 'en' ? 'Unknown currency. Try again.' : 'Неизвестная валюта. Попробуй ещё раз.');
      return;
    }
    const balanceStr = prompt(lang === 'en' ? 'Starting balance in €:' : 'Начальный баланс в €:', '0');
    if (balanceStr === null) return;
    const balance = parseFloat(balanceStr.replace(',', '.')) || 0;
    const id = 'bank_custom_' + Date.now() + Math.random().toString(36).slice(2, 5);
    const banks = getBanks();
    banks.push({ id, name: name.trim(), currency: cur, manualBalance: balance, builtin: false });
    saveBanks(banks);
    renderSection(slug);
  });
}

// ─── ROUTINES (recurring habits) ───
// Schedule types: daily | weekdays | weekends | weekly | monthly | every_n_days
// Each routine: { id, name, icon, schedule, dayOfWeek?, dayOfMonth?, nDays?, history: ['YYYY-MM-DD', ...], createdAt }

function getRoutines() {
  const d = getSectionData('routines');
  return d.routines || [];
}
function saveRoutines(routines) {
  const d = getSectionData('routines');
  d.routines = routines;
  updateSectionData('routines', d);
}
function isRoutineDueOn(routine, dateStr) {
  // dateStr: YYYY-MM-DD
  const date = new Date(dateStr + 'T12:00:00');
  const dow = date.getDay(); // 0=Sun, 6=Sat
  const dom = date.getDate();
  const created = routine.createdAt ? new Date(routine.createdAt) : new Date(0);
  const createdStr = toLocalDateStr(created);
  if (dateStr < createdStr) return false;
  switch (routine.schedule) {
    case 'daily': return true;
    case 'weekdays': return dow >= 1 && dow <= 5;
    case 'weekends': return dow === 0 || dow === 6;
    case 'weekly':
      return dow === (routine.dayOfWeek ?? new Date(routine.createdAt || Date.now()).getDay());
    case 'monthly':
      return dom === (routine.dayOfMonth ?? new Date(routine.createdAt || Date.now()).getDate());
    case 'every_n_days': {
      const n = routine.nDays || 2;
      const startDate = new Date(routine.createdAt || Date.now());
      const diffDays = Math.floor((date - startDate) / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays % n === 0;
    }
    default: return false;
  }
}
function isRoutineDoneOn(routine, dateStr) {
  return (routine.history || []).includes(dateStr);
}
function calculateRoutineStreak(routine) {
  // Streak = consecutive dueDays where routine was done, counting back from today.
  // If today is due-day and not done yet — streak counts back from yesterday.
  const today = new Date(); today.setHours(12, 0, 0, 0);
  const todayStr = toLocalDateStr(today);
  let streak = 0;
  let cursor = new Date(today);
  // If today is due and done, count it; if today is due and not done, start from yesterday.
  if (isRoutineDueOn(routine, todayStr)) {
    if (isRoutineDoneOn(routine, todayStr)) {
      streak = 1;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      cursor.setDate(cursor.getDate() - 1);
    }
  } else {
    cursor.setDate(cursor.getDate() - 1);
  }
  // Walk backwards for up to 365 days
  for (let i = 0; i < 365; i++) {
    const cs = toLocalDateStr(cursor);
    if (isRoutineDueOn(routine, cs)) {
      if (isRoutineDoneOn(routine, cs)) streak++;
      else break;
    }
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}
function getTodayRoutines() {
  // Return routines due TODAY with done status (used for dashboard widget)
  const todayStr = toLocalDateStr();
  return getRoutines()
    .filter(r => isRoutineDueOn(r, todayStr))
    .map(r => ({ ...r, doneToday: isRoutineDoneOn(r, todayStr) }));
}
function toggleRoutineDone(routineId, dateStr) {
  const routines = getRoutines();
  const idx = routines.findIndex(r => r.id === routineId);
  if (idx < 0) return;
  const r = routines[idx];
  const history = new Set(r.history || []);
  if (history.has(dateStr)) history.delete(dateStr);
  else history.add(dateStr);
  routines[idx] = { ...r, history: [...history].sort() };
  saveRoutines(routines);
}

// ─────────────────────────────────────────────────────────────────────
// NUTRITION TRACKER
// ─────────────────────────────────────────────────────────────────────
// Three tabs:
//   1. Today: macro counter (calories/protein/fat/carbs) + water intake.
//   2. Fridge: inventory with expiry dates + low-stock alerts.
//   3. Add: manual food entry (name + macros per 100g + portion → total).
// Data is stored in section data (`nutrition`) under custom keys:
//   _foodLog: { date: 'YYYY-MM-DD', items: [{ id, name, kcal, prot, fat, carb, time }] }[]
//   _fridge: [{ id, name, qty, unit, expiry }]
//   _targets: { kcal, prot, fat, carb, water }  // daily goals
//   _waterLog: { date: 'YYYY-MM-DD', glasses: int }[]
//   _foods: [{ name, kcal100, prot100, fat100, carb100 }]  // saved frequent foods for quick re-add

const DEFAULT_NUTRITION_TARGETS = { kcal: 2200, prot: 130, fat: 70, carb: 270, water: 8 };

function getNutritionData() {
  const sd = getSectionData('nutrition');
  if (!sd._foodLog) sd._foodLog = [];
  if (!sd._fridge) sd._fridge = [];
  if (!sd._targets) sd._targets = { ...DEFAULT_NUTRITION_TARGETS };
  if (!sd._waterLog) sd._waterLog = [];
  if (!sd._foods) sd._foods = [];
  return sd;
}

function saveNutrition(patch) {
  const sd = getNutritionData();
  Object.assign(sd, patch);
  updateSectionData('nutrition', sd);
}

function getFoodLogForDate(dateStr) {
  const sd = getNutritionData();
  const entry = sd._foodLog.find(d => d.date === dateStr);
  return entry ? entry.items : [];
}

function addFoodLogItem(item) {
  const sd = getNutritionData();
  const today = toLocalDateStr();
  let day = sd._foodLog.find(d => d.date === today);
  if (!day) {
    day = { date: today, items: [] };
    sd._foodLog.push(day);
  }
  day.items.push({ ...item, id: 'fl_' + Date.now() + '_' + Math.random().toString(36).slice(2, 5), time: new Date().toLocaleTimeString(lang === 'en' ? 'en-US' : 'ru-RU', { hour: '2-digit', minute: '2-digit' }) });
  saveNutrition({ _foodLog: sd._foodLog });
}

function removeFoodLogItem(dateStr, itemId) {
  const sd = getNutritionData();
  const day = sd._foodLog.find(d => d.date === dateStr);
  if (!day) return;
  day.items = day.items.filter(i => i.id !== itemId);
  saveNutrition({ _foodLog: sd._foodLog });
}

function getDayTotals(items) {
  return items.reduce((a, i) => ({
    kcal: a.kcal + (i.kcal || 0),
    prot: a.prot + (i.prot || 0),
    fat: a.fat + (i.fat || 0),
    carb: a.carb + (i.carb || 0),
  }), { kcal: 0, prot: 0, fat: 0, carb: 0 });
}

let nutritionTab = 'today'; // 'today' | 'fridge' | 'add'
let nutritionEditingTargets = false;

function _renderNutritionNotes(slug) {
  // We want subtopics + entries/tags/attachments to work just like a regular section.
  // Trick: temporarily route renderSection to a different container — but easier:
  // we just call renderSection and it will replace #content. So actually we let
  // renderSection take over the whole content area.
  // The user can still switch tabs via the nutr-tabs above (rendered earlier inside #content,
  // but renderSection replaces it). So we re-render nutrition shell with notes tab active,
  // and below the tabs, manually rebuild section-style UI inline.
  // Simplest: just call the standard section renderer, which gives full features.
  // Workaround: temporarily mark current tab as 'notes', then call renderSection(slug),
  // which replaces #content. Tab switching back will happen via the Notes tab being
  // rendered inside renderSection's output, which it isn't. So instead, we keep
  // user inside renderNutrition by providing a "← Back to nutrition" button at top.
  // For now: just open the standard section route programmatically.
  // BUT user explicitly wants subtopics in nutrition — best UX is dedicated rendering
  // inside Notes tab keeping nutrition tabs visible.

  const container = document.getElementById('nutrNotesContainer');
  if (!container) return;
  const data = getSectionData(slug);
  const allEntries = (data.entries || []).filter(e => !e._foodLogItem); // exclude food log items
  const subtopics = data.subtopics || [];
  const activeSub = data.activeSubtopic || null;
  const entries = activeSub ? allEntries.filter(e => e.subtopicId === activeSub) : allEntries;

  let html = '';
  // Subtopic bar
  html += '<div class="subtopic-bar" style="margin-top:14px">';
  html += `<button class="subtopic-chip ${activeSub === null ? 'active' : ''}" data-nutrnote-sub="">${t('all')} <span class="count">${allEntries.length}</span></button>`;
  subtopics.forEach((st, idx) => {
    const cnt = allEntries.filter(e => e.subtopicId === st.id).length;
    const isActive = activeSub === st.id;
    html += `<button class="subtopic-chip ${isActive ? 'active' : ''}" data-nutrnote-sub="${st.id}">${escapeHtml(st.name)} <span class="count">${cnt}</span>${isActive ? `<span class="del-x" data-nutrnote-del-sub="${st.id}">✕</span>` : ''}</button>`;
  });
  html += `<button class="subtopic-chip add" id="nutrNoteAddSubBtn">${t('addSubtopic')}</button></div>`;

  // Add entry form
  const subSelect = subtopics.length > 0
    ? `<select id="nutrNoteEntrySub" style="width:auto;min-width:160px"><option value="">${t('noSubtopic')}</option>${subtopics.map(st => `<option value="${st.id}" ${activeSub === st.id ? 'selected' : ''}>${escapeHtml(st.name)}</option>`).join('')}</select>` : '';
  html += `<div class="card no-print" style="margin:14px 0 18px">
    <textarea id="nutrNoteText" rows="3" placeholder="${lang === 'en' ? 'Meal idea, recipe note...' : 'Идея блюда, заметка по рецепту...'}"></textarea>
    <div style="margin-top:10px;display:flex;gap:8px;flex-wrap:wrap;align-items:center">
      ${subSelect}
      <label style="display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--text-muted);cursor:pointer">
        <input type="checkbox" id="nutrNoteNoCheck" checked style="width:auto;margin:0" />
        📝 ${lang === 'en' ? 'Note only' : 'Просто заметка'}
      </label>
    </div>
    <div style="margin-top:10px;display:flex;justify-content:flex-end">
      <button class="btn-primary" id="nutrNoteAddBtn">${t('addEntry')}</button>
    </div>
  </div>`;

  // Entries list
  if (entries.length === 0) {
    html += `<p class="empty">${lang === 'en' ? 'No notes yet. Add your meal ideas above.' : 'Пока пусто. Добавь идеи блюд выше.'}</p>`;
  } else {
    html += '<div class="entry-list">';
    entries.forEach(e => {
      const dateStr = new Date(e.createdAt).toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-IE', { dateStyle: 'short', timeStyle: 'short' });
      const subName = e.subtopicId ? (subtopics.find(st => st.id === e.subtopicId)?.name || '') : '';
      const marker = e.noCheck
        ? `<span class="entry-note-marker">📝</span>`
        : `<button class="tdo-check ${e.done ? 'checked' : ''}" data-nutrnote-toggle="${e.id}"></button>`;
      html += `<div class="entry-card ${e.done ? 'done' : ''}">
        ${marker}
        <div class="entry-content">
          <div class="entry-text">${linkifyText(e.text)}</div>
          <div class="entry-meta">
            <div class="left"><span>${dateStr}</span>${subName ? `<span class="entry-tag">${escapeHtml(subName)}</span>` : ''}</div>
            <div style="display:flex;gap:6px;align-items:center">
              <button class="btn-danger" data-nutrnote-del="${e.id}">🗑</button>
            </div>
          </div>
        </div>
      </div>`;
    });
    html += '</div>';
  }
  container.innerHTML = html;

  // Wire handlers
  container.querySelectorAll('[data-nutrnote-sub]').forEach(chip => {
    chip.addEventListener('click', (ev) => {
      if (ev.target.closest('[data-nutrnote-del-sub]')) return;
      const sub = chip.getAttribute('data-nutrnote-sub');
      const d = getSectionData(slug);
      d.activeSubtopic = sub === '' ? null : sub;
      updateSectionData(slug, d);
      _renderNutritionNotes(slug);
    });
  });
  container.querySelectorAll('[data-nutrnote-del-sub]').forEach(x => {
    x.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      if (!await customConfirm(t('deleteSubtopic'), { icon: '🗂' })) return;
      const subId = x.getAttribute('data-nutrnote-del-sub');
      const d = getSectionData(slug);
      d.subtopics = (d.subtopics || []).filter(s => s.id !== subId);
      d.entries = (d.entries || []).map(en => en.subtopicId === subId ? { ...en, subtopicId: null } : en);
      d.activeSubtopic = null;
      updateSectionData(slug, d);
      _renderNutritionNotes(slug);
    });
  });
  const addSubBtn = document.getElementById('nutrNoteAddSubBtn');
  if (addSubBtn) addSubBtn.addEventListener('click', async () => {
    const name = await customPrompt(t('addSubtopic'), { placeholder: lang === 'en' ? 'e.g. Breakfast, Lunch, Dinner' : 'напр. Завтрак, Обед, Ужин' });
    if (!name || !name.trim()) return;
    const d = getSectionData(slug);
    d.subtopics = [...(d.subtopics || []), { id: 'sub_' + Date.now(), name: name.trim() }];
    updateSectionData(slug, d);
    _renderNutritionNotes(slug);
  });
  const addBtn = document.getElementById('nutrNoteAddBtn');
  if (addBtn) addBtn.addEventListener('click', () => {
    const txtInp = document.getElementById('nutrNoteText');
    const txt = txtInp.value.trim();
    if (!txt) return;
    const subSel = document.getElementById('nutrNoteEntrySub');
    const noCheckInp = document.getElementById('nutrNoteNoCheck');
    const d = getSectionData(slug);
    d.entries = [{
      id: Date.now().toString(),
      text: txt,
      createdAt: Date.now(),
      subtopicId: subSel ? (subSel.value || null) : (d.activeSubtopic || null),
      deadline: null,
      done: false,
      noCheck: noCheckInp ? noCheckInp.checked : true,
    }, ...(d.entries || [])];
    updateSectionData(slug, d);
    showToast(lang === 'en' ? '✓ Added' : '✓ Добавлено');
    _renderNutritionNotes(slug);
  });
  container.querySelectorAll('[data-nutrnote-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-nutrnote-toggle');
      const d = getSectionData(slug);
      d.entries = (d.entries || []).map(en => {
        if (en.id !== id) return en;
        const nowDone = !en.done;
        return { ...en, done: nowDone, doneAt: nowDone ? Date.now() : null };
      });
      updateSectionData(slug, d);
      _renderNutritionNotes(slug);
    });
  });
  container.querySelectorAll('[data-nutrnote-del]').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (!await customConfirm(t('confirmDelete'))) return;
      const id = btn.getAttribute('data-nutrnote-del');
      const d = getSectionData(slug);
      d.entries = (d.entries || []).filter(en => en.id !== id);
      updateSectionData(slug, d);
      _renderNutritionNotes(slug);
    });
  });
}

function renderNutrition(section) {
  const slug = 'nutrition';
  const title = lang === 'en' ? section.titleEn : section.titleRu;
  document.getElementById('headerTitle').textContent = `${section.icon} ${title}`;
  document.getElementById('printBtn').style.display = 'none';

  const sd = getNutritionData();
  const todayStr = toLocalDateStr();
  const items = getFoodLogForDate(todayStr);
  const totals = getDayTotals(items);
  const targets = sd._targets || DEFAULT_NUTRITION_TARGETS;
  const waterEntry = sd._waterLog.find(w => w.date === todayStr);
  const waterToday = waterEntry ? waterEntry.glasses : 0;

  // Tabs
  let html = `<div class="nutr-tabs">
    <button class="nutr-tab ${nutritionTab === 'today' ? 'active' : ''}" data-nutr-tab="today">📊 ${lang === 'en' ? 'Today' : 'Сегодня'}</button>
    <button class="nutr-tab ${nutritionTab === 'fridge' ? 'active' : ''}" data-nutr-tab="fridge">🧊 ${lang === 'en' ? 'Fridge' : 'Холодильник'}</button>
    <button class="nutr-tab ${nutritionTab === 'add' ? 'active' : ''}" data-nutr-tab="add">➕ ${lang === 'en' ? 'Add food' : 'Добавить'}</button>
    <button class="nutr-tab ${nutritionTab === 'notes' ? 'active' : ''}" data-nutr-tab="notes">📝 ${lang === 'en' ? 'Ideas' : 'Идеи'}</button>
  </div>`;

  if (nutritionTab === 'today') {
    html += _renderNutritionToday(totals, targets, items, waterToday, todayStr);
  } else if (nutritionTab === 'fridge') {
    html += _renderNutritionFridge(sd._fridge);
  } else if (nutritionTab === 'add') {
    html += _renderNutritionAdd(sd._foods);
  } else if (nutritionTab === 'notes') {
    // Notes tab — defer to standard renderSection so subtopics, tags, photo gallery,
    // and all standard features work for meal ideas (breakfast / lunch / dinner / etc.)
    document.getElementById('content').innerHTML = `<div class="nutr-shell">
      <div class="nutr-tabs">
        <button class="nutr-tab" data-nutr-tab="today">📊 ${lang === 'en' ? 'Today' : 'Сегодня'}</button>
        <button class="nutr-tab" data-nutr-tab="fridge">🧊 ${lang === 'en' ? 'Fridge' : 'Холодильник'}</button>
        <button class="nutr-tab" data-nutr-tab="add">➕ ${lang === 'en' ? 'Add food' : 'Добавить'}</button>
        <button class="nutr-tab active" data-nutr-tab="notes">📝 ${lang === 'en' ? 'Ideas' : 'Идеи'}</button>
      </div>
      <div id="nutrNotesContainer"></div>
    </div>`;
    // Wire tab switching
    document.querySelectorAll('[data-nutr-tab]').forEach(btn => {
      btn.addEventListener('click', () => {
        nutritionTab = btn.getAttribute('data-nutr-tab');
        renderNutrition(getSection(slug));
      });
    });
    // Render the standard section UI into nutrNotesContainer (with redirect)
    _renderNutritionNotes(slug);
    return;
  }

  document.getElementById('content').innerHTML = `<div class="nutr-shell">${html}</div>`;
  _attachNutritionHandlers(slug);
}

function _renderNutritionToday(totals, targets, items, water, dateStr) {
  const pct = (val, tgt) => tgt > 0 ? Math.min(100, Math.round((val / tgt) * 100)) : 0;
  const macros = [
    { key: 'kcal', label: lang === 'en' ? 'Calories' : 'Калории', icon: '🔥', unit: 'kcal', color: '#F59E0B' },
    { key: 'prot', label: lang === 'en' ? 'Protein' : 'Белки',   icon: '💪', unit: 'g',    color: '#EF4444' },
    { key: 'fat',  label: lang === 'en' ? 'Fat' : 'Жиры',         icon: '🥑', unit: 'g',    color: '#FBBF24' },
    { key: 'carb', label: lang === 'en' ? 'Carbs' : 'Углеводы',   icon: '🍞', unit: 'g',    color: '#A374F0' },
  ];

  let html = `<div class="nutr-targets-row">
    <h3 style="margin:0">${lang === 'en' ? 'Daily progress' : 'Прогресс дня'}</h3>
    <button class="btn-ghost" id="nutrEditTargets" style="font-size:11px;padding:6px 10px">⚙ ${lang === 'en' ? 'Targets' : 'Цели'}</button>
  </div>`;

  if (nutritionEditingTargets) {
    html += `<div class="nutr-targets-edit">
      <div class="nutr-targets-grid">
        ${macros.map(m => `<label>
          <span>${m.icon} ${m.label} (${m.unit})</span>
          <input type="number" min="0" data-target="${m.key}" value="${targets[m.key]}" />
        </label>`).join('')}
        <label>
          <span>💧 ${lang === 'en' ? 'Water (glasses)' : 'Вода (стаканы)'}</span>
          <input type="number" min="0" data-target="water" value="${targets.water}" />
        </label>
      </div>
      <div style="display:flex;gap:8px;margin-top:10px">
        <button class="btn-primary" id="nutrSaveTargets" style="flex:1">${lang === 'en' ? 'Save' : 'Сохранить'}</button>
        <button class="btn-ghost" id="nutrCancelTargets">${lang === 'en' ? 'Cancel' : 'Отмена'}</button>
      </div>
    </div>`;
  }

  // Macro rings/bars
  html += `<div class="nutr-macro-grid">`;
  macros.forEach(m => {
    const val = totals[m.key] || 0;
    const tgt = targets[m.key] || 0;
    const p = pct(val, tgt);
    html += `<div class="nutr-macro-card" style="--macro-color: ${m.color}">
      <div class="nutr-macro-head">
        <span class="nutr-macro-icon">${m.icon}</span>
        <span class="nutr-macro-label">${m.label}</span>
      </div>
      <div class="nutr-macro-vals">
        <span class="nutr-macro-current">${Math.round(val)}</span>
        <span class="nutr-macro-sep">/</span>
        <span class="nutr-macro-target">${tgt}</span>
        <span class="nutr-macro-unit">${m.unit}</span>
      </div>
      <div class="nutr-macro-bar">
        <div class="nutr-macro-bar-fill" style="width:${p}%"></div>
      </div>
      <div class="nutr-macro-pct">${p}%</div>
    </div>`;
  });
  html += `</div>`;

  // Water tracker
  const wpct = pct(water, targets.water);
  html += `<div class="nutr-water-card">
    <div class="nutr-water-head">
      <span>💧 ${lang === 'en' ? 'Water' : 'Вода'}</span>
      <span>${water} / ${targets.water} ${lang === 'en' ? 'glasses' : 'стаканов'} (${wpct}%)</span>
    </div>
    <div class="nutr-water-glasses">
      ${Array.from({ length: Math.max(targets.water, water) }, (_, i) =>
        `<button class="nutr-glass ${i < water ? 'filled' : ''}" data-water-glass="${i}">💧</button>`
      ).join('')}
    </div>
    <div style="display:flex;gap:6px;margin-top:8px">
      <button class="btn-ghost" id="nutrWaterMinus" style="font-size:12px">−</button>
      <button class="btn-primary" id="nutrWaterPlus" style="flex:1;font-size:12px">+ ${lang === 'en' ? 'Add glass' : 'Добавить стакан'}</button>
    </div>
  </div>`;

  // Today's food log
  html += `<div class="nutr-log-card">
    <h3>${lang === 'en' ? "Today's meals" : 'Сегодня съедено'}</h3>`;
  if (items.length === 0) {
    html += `<p class="empty">${lang === 'en' ? 'No meals logged yet today.' : 'Сегодня ничего не добавлено.'}</p>`;
  } else {
    html += `<div class="nutr-log-list">`;
    items.forEach(item => {
      html += `<div class="nutr-log-item">
        <div class="nutr-log-main">
          <div class="nutr-log-name">${escapeHtml(item.name)}</div>
          <div class="nutr-log-meta">
            <span>⏱ ${item.time}</span>
            <span>🔥 ${Math.round(item.kcal || 0)}</span>
            <span>💪 ${Math.round(item.prot || 0)}г</span>
            <span>🥑 ${Math.round(item.fat || 0)}г</span>
            <span>🍞 ${Math.round(item.carb || 0)}г</span>
          </div>
        </div>
        <button class="btn-danger" data-del-food="${item.id}" title="${t('delete')}">🗑</button>
      </div>`;
    });
    html += `</div>`;
  }
  html += `</div>`;
  return html;
}

function _renderNutritionFridge(fridge) {
  const today = toLocalDateStr();
  // Sort: expiring soonest first; expired separately
  const sorted = [...fridge].sort((a, b) => {
    if (!a.expiry && !b.expiry) return 0;
    if (!a.expiry) return 1;
    if (!b.expiry) return -1;
    return a.expiry.localeCompare(b.expiry);
  });

  let html = `<div class="nutr-fridge-add">
    <h3>🧊 ${lang === 'en' ? 'Add to fridge' : 'Добавить в холодильник'}</h3>
    <div class="nutr-fridge-add-row">
      <input type="text" id="fridgeName" placeholder="${lang === 'en' ? 'Name (e.g. milk)' : 'Название (напр. молоко)'}" style="flex:2" />
      <input type="text" id="fridgeQty" placeholder="${lang === 'en' ? '1 L' : '1 л'}" style="flex:1" />
      <input type="date" id="fridgeExpiry" title="${lang === 'en' ? 'Expiry' : 'Срок годности'}" />
      <button class="btn-primary" id="fridgeAddBtn">+</button>
    </div>
  </div>`;

  html += `<div class="nutr-fridge-list">`;
  if (sorted.length === 0) {
    html += `<p class="empty">${lang === 'en' ? 'Fridge is empty.' : 'Холодильник пуст.'}</p>`;
  } else {
    sorted.forEach(item => {
      let urgency = 'normal';
      let daysLeft = null;
      if (item.expiry) {
        const dl = Math.floor((new Date(item.expiry + 'T00:00:00').getTime() - new Date(today + 'T00:00:00').getTime()) / 86400000);
        daysLeft = dl;
        if (dl < 0) urgency = 'overdue';
        else if (dl <= 1) urgency = 'urgent';
        else if (dl <= 3) urgency = 'soon';
      }
      const expiryLabel = item.expiry
        ? (daysLeft < 0
          ? (lang === 'en' ? `Expired ${-daysLeft}d ago` : `Просрочено на ${-daysLeft}д`)
          : daysLeft === 0 ? (lang === 'en' ? 'Today!' : 'Сегодня!')
          : daysLeft === 1 ? (lang === 'en' ? 'Tomorrow' : 'Завтра')
          : (lang === 'en' ? `${daysLeft} days` : `${daysLeft}д`))
        : '—';
      html += `<div class="nutr-fridge-item ${urgency}">
        <div class="nutr-fridge-body">
          <div class="nutr-fridge-name">${escapeHtml(item.name)}</div>
          <div class="nutr-fridge-meta">
            ${item.qty ? `<span class="nutr-fridge-qty">${escapeHtml(item.qty)}</span>` : ''}
            ${item.expiry ? `<span class="nutr-fridge-expiry ${urgency}">${expiryLabel}</span>` : ''}
          </div>
        </div>
        <button class="btn-danger" data-del-fridge="${item.id}" title="${t('delete')}">🗑</button>
      </div>`;
    });
  }
  html += `</div>`;
  return html;
}

function _renderNutritionAdd(savedFoods) {
  let html = `<div class="nutr-add-card">
    <h3>➕ ${lang === 'en' ? 'Log food' : 'Записать еду'}</h3>
    <p class="nutr-add-hint">${lang === 'en' ? 'Enter per 100g, then portion → totals auto-calculate.' : 'Введи на 100г, затем порция → итоги посчитаются автоматически.'}</p>

    <div class="nutr-add-form">
      <input type="text" id="foodName" placeholder="${lang === 'en' ? 'Food name (e.g. chicken breast)' : 'Название (напр. куриная грудка)'}" />

      <div class="nutr-100g-grid">
        <label><span>🔥 ${lang === 'en' ? 'Cal /100g' : 'Кал /100г'}</span><input type="number" id="food100Kcal" min="0" step="0.1" /></label>
        <label><span>💪 ${lang === 'en' ? 'Prot /100g' : 'Бел /100г'}</span><input type="number" id="food100Prot" min="0" step="0.1" /></label>
        <label><span>🥑 ${lang === 'en' ? 'Fat /100g' : 'Жир /100г'}</span><input type="number" id="food100Fat" min="0" step="0.1" /></label>
        <label><span>🍞 ${lang === 'en' ? 'Carb /100g' : 'Угл /100г'}</span><input type="number" id="food100Carb" min="0" step="0.1" /></label>
      </div>

      <label class="nutr-portion-label">
        <span>⚖ ${lang === 'en' ? 'Portion (grams)' : 'Порция (грамм)'}</span>
        <input type="number" id="foodPortion" min="0" step="1" value="100" />
      </label>

      <div class="nutr-totals" id="nutrTotalsPreview">
        <div class="nutr-totals-label">${lang === 'en' ? 'This portion:' : 'Эта порция:'}</div>
        <div class="nutr-totals-vals">
          <span>🔥 <b id="ttKcal">0</b></span>
          <span>💪 <b id="ttProt">0</b>г</span>
          <span>🥑 <b id="ttFat">0</b>г</span>
          <span>🍞 <b id="ttCarb">0</b>г</span>
        </div>
      </div>

      <div style="display:flex;gap:8px;margin-top:12px">
        <label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer">
          <input type="checkbox" id="foodSave" style="width:auto" /> ${lang === 'en' ? 'Save for later' : 'Сохранить'}
        </label>
        <button class="btn-primary" id="foodLogBtn" style="flex:1">${lang === 'en' ? 'Add to today' : 'Добавить'}</button>
      </div>
    </div>

    ${savedFoods.length > 0 ? `
    <div class="nutr-saved-foods">
      <h4>${lang === 'en' ? 'Saved foods (tap to fill form)' : 'Сохранённые (нажми чтобы заполнить)'}</h4>
      <div class="nutr-saved-list">
        ${savedFoods.map((f, idx) => `<button class="nutr-saved-chip" data-saved-food="${idx}">${escapeHtml(f.name)} <span class="nutr-saved-del" data-del-saved="${idx}">✕</span></button>`).join('')}
      </div>
    </div>` : ''}
  </div>`;
  return html;
}

function _attachNutritionHandlers(slug) {
  // Tab switching
  document.querySelectorAll('[data-nutr-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      nutritionTab = btn.getAttribute('data-nutr-tab');
      renderNutrition(getSection(slug));
    });
  });

  // ── Today tab ──
  const editTargetsBtn = document.getElementById('nutrEditTargets');
  if (editTargetsBtn) editTargetsBtn.addEventListener('click', () => {
    nutritionEditingTargets = true;
    renderNutrition(getSection(slug));
  });
  const saveTargetsBtn = document.getElementById('nutrSaveTargets');
  if (saveTargetsBtn) saveTargetsBtn.addEventListener('click', () => {
    const newTargets = {};
    document.querySelectorAll('[data-target]').forEach(inp => {
      newTargets[inp.getAttribute('data-target')] = parseFloat(inp.value) || 0;
    });
    saveNutrition({ _targets: newTargets });
    nutritionEditingTargets = false;
    showToast(lang === 'en' ? '✓ Targets saved' : '✓ Цели сохранены');
    renderNutrition(getSection(slug));
  });
  const cancelTargetsBtn = document.getElementById('nutrCancelTargets');
  if (cancelTargetsBtn) cancelTargetsBtn.addEventListener('click', () => {
    nutritionEditingTargets = false;
    renderNutrition(getSection(slug));
  });

  // Water tracking
  const waterPlus = document.getElementById('nutrWaterPlus');
  if (waterPlus) waterPlus.addEventListener('click', () => {
    const sd = getNutritionData();
    const today = toLocalDateStr();
    let entry = sd._waterLog.find(w => w.date === today);
    if (!entry) { entry = { date: today, glasses: 0 }; sd._waterLog.push(entry); }
    entry.glasses = (entry.glasses || 0) + 1;
    saveNutrition({ _waterLog: sd._waterLog });
    renderNutrition(getSection(slug));
  });
  const waterMinus = document.getElementById('nutrWaterMinus');
  if (waterMinus) waterMinus.addEventListener('click', () => {
    const sd = getNutritionData();
    const today = toLocalDateStr();
    const entry = sd._waterLog.find(w => w.date === today);
    if (entry && entry.glasses > 0) {
      entry.glasses -= 1;
      saveNutrition({ _waterLog: sd._waterLog });
    }
    renderNutrition(getSection(slug));
  });
  document.querySelectorAll('[data-water-glass]').forEach(btn => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.getAttribute('data-water-glass'));
      const sd = getNutritionData();
      const today = toLocalDateStr();
      let entry = sd._waterLog.find(w => w.date === today);
      if (!entry) { entry = { date: today, glasses: 0 }; sd._waterLog.push(entry); }
      // If clicked glass is already filled, remove down to that point. Otherwise fill up to it.
      entry.glasses = entry.glasses === idx + 1 ? idx : idx + 1;
      saveNutrition({ _waterLog: sd._waterLog });
      renderNutrition(getSection(slug));
    });
  });

  // Delete food log item
  document.querySelectorAll('[data-del-food]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-del-food');
      removeFoodLogItem(toLocalDateStr(), id);
      renderNutrition(getSection(slug));
    });
  });

  // ── Fridge tab ──
  const fridgeBtn = document.getElementById('fridgeAddBtn');
  if (fridgeBtn) fridgeBtn.addEventListener('click', () => {
    const name = document.getElementById('fridgeName').value.trim();
    if (!name) return;
    const qty = document.getElementById('fridgeQty').value.trim();
    const expiry = document.getElementById('fridgeExpiry').value || null;
    const sd = getNutritionData();
    sd._fridge.push({ id: 'fr_' + Date.now(), name, qty: qty || null, expiry });
    saveNutrition({ _fridge: sd._fridge });
    showToast(lang === 'en' ? '✓ Added to fridge' : '✓ Добавлено');
    renderNutrition(getSection(slug));
  });
  document.querySelectorAll('[data-del-fridge]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-del-fridge');
      const sd = getNutritionData();
      sd._fridge = sd._fridge.filter(f => f.id !== id);
      saveNutrition({ _fridge: sd._fridge });
      renderNutrition(getSection(slug));
    });
  });

  // ── Add tab ──
  const updateTotals = () => {
    const k = parseFloat(document.getElementById('food100Kcal')?.value) || 0;
    const p = parseFloat(document.getElementById('food100Prot')?.value) || 0;
    const f = parseFloat(document.getElementById('food100Fat')?.value) || 0;
    const c = parseFloat(document.getElementById('food100Carb')?.value) || 0;
    const portion = parseFloat(document.getElementById('foodPortion')?.value) || 0;
    const mult = portion / 100;
    document.getElementById('ttKcal').textContent = Math.round(k * mult);
    document.getElementById('ttProt').textContent = Math.round(p * mult * 10) / 10;
    document.getElementById('ttFat').textContent = Math.round(f * mult * 10) / 10;
    document.getElementById('ttCarb').textContent = Math.round(c * mult * 10) / 10;
  };
  ['food100Kcal','food100Prot','food100Fat','food100Carb','foodPortion'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('input', updateTotals);
  });
  const logBtn = document.getElementById('foodLogBtn');
  if (logBtn) logBtn.addEventListener('click', () => {
    const name = document.getElementById('foodName').value.trim();
    if (!name) return;
    const k100 = parseFloat(document.getElementById('food100Kcal').value) || 0;
    const p100 = parseFloat(document.getElementById('food100Prot').value) || 0;
    const f100 = parseFloat(document.getElementById('food100Fat').value) || 0;
    const c100 = parseFloat(document.getElementById('food100Carb').value) || 0;
    const portion = parseFloat(document.getElementById('foodPortion').value) || 100;
    const mult = portion / 100;
    addFoodLogItem({
      name,
      kcal: k100 * mult,
      prot: p100 * mult,
      fat: f100 * mult,
      carb: c100 * mult,
      portion,
    });
    // Save to library if checked
    const saveCheckbox = document.getElementById('foodSave');
    if (saveCheckbox && saveCheckbox.checked) {
      const sd = getNutritionData();
      const exists = sd._foods.find(f => f.name.toLowerCase() === name.toLowerCase());
      if (!exists) {
        sd._foods.push({ name, kcal100: k100, prot100: p100, fat100: f100, carb100: c100 });
        saveNutrition({ _foods: sd._foods });
      }
    }
    showToast(lang === 'en' ? `✓ ${name} logged` : `✓ ${name} добавлено`);
    nutritionTab = 'today';
    renderNutrition(getSection(slug));
  });
  document.querySelectorAll('[data-saved-food]').forEach(chip => {
    chip.addEventListener('click', (e) => {
      if (e.target.closest('[data-del-saved]')) return;
      const idx = parseInt(chip.getAttribute('data-saved-food'));
      const sd = getNutritionData();
      const f = sd._foods[idx];
      if (!f) return;
      document.getElementById('foodName').value = f.name;
      document.getElementById('food100Kcal').value = f.kcal100;
      document.getElementById('food100Prot').value = f.prot100;
      document.getElementById('food100Fat').value = f.fat100;
      document.getElementById('food100Carb').value = f.carb100;
      updateTotals();
    });
  });
  document.querySelectorAll('[data-del-saved]').forEach(x => {
    x.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = parseInt(x.getAttribute('data-del-saved'));
      const sd = getNutritionData();
      sd._foods.splice(idx, 1);
      saveNutrition({ _foods: sd._foods });
      renderNutrition(getSection(slug));
    });
  });
}

function renderRoutines(section) {
  const slug = 'routines';
  const title = lang === 'en' ? section.titleEn : section.titleRu;
  document.getElementById('headerTitle').textContent = `${section.icon} ${title}`;
  document.getElementById('printBtn').style.display = 'inline-flex';
  document.getElementById('printLabel').textContent = t('print');

  const routines = getRoutines();
  const todayStr = toLocalDateStr();

  // ─── Add routine card ───
  let html = `<div class="rt-shell">
    <div class="rt-add-card">
      <div class="rt-add-title">+ ${lang === 'en' ? 'New routine' : 'Новая рутина'}</div>
      <div class="rt-add-row">
        <input id="rtIcon" type="text" maxlength="4" placeholder="🏃" value="🔁" style="font-size:20px;text-align:center;width:60px;flex:0 0 60px" />
        <input id="rtName" type="text" placeholder="${lang === 'en' ? 'Workout, Vitamins, Read 30 min...' : 'Тренировка, Витамины, Чтение 30 мин...'}" style="flex:1" />
      </div>
      <select id="rtSchedule">
        <option value="daily">${lang === 'en' ? 'Every day' : 'Каждый день'}</option>
        <option value="weekdays">${lang === 'en' ? 'Weekdays (Mon–Fri)' : 'Будни (Пн–Пт)'}</option>
        <option value="weekends">${lang === 'en' ? 'Weekends (Sat–Sun)' : 'Выходные (Сб–Вс)'}</option>
        <option value="weekly">${lang === 'en' ? 'Once a week (this day)' : 'Раз в неделю (в этот день)'}</option>
        <option value="monthly">${lang === 'en' ? 'Once a month (this date)' : 'Раз в месяц (это число)'}</option>
        <option value="every_n_days">${lang === 'en' ? 'Every N days' : 'Каждые N дней'}</option>
      </select>
      <input id="rtNDays" type="number" min="2" max="30" placeholder="${lang === 'en' ? 'N (days)' : 'N (дней)'}" value="3" style="display:none" />
      <button class="btn-primary" id="rtAddBtn">+ ${lang === 'en' ? 'Add routine' : 'Добавить рутину'}</button>
    </div>
  </div>`;

  // ─── Today section ───
  const todayRoutines = routines.filter(r => isRoutineDueOn(r, todayStr));
  if (todayRoutines.length > 0) {
    html += `<h2 class="subsection-title">📅 ${lang === 'en' ? "Today" : 'Сегодня'} <span class="count">${todayRoutines.length}</span></h2>`;
    html += `<div class="rt-list">`;
    todayRoutines.forEach(r => {
      const done = isRoutineDoneOn(r, todayStr);
      const streak = calculateRoutineStreak(r);
      html += `<div class="rt-item ${done ? 'done' : ''}" data-rt-id="${r.id}">
        <button class="rt-check ${done ? 'checked' : ''}" data-toggle-rt="${r.id}" data-rt-date="${todayStr}" title="${lang === 'en' ? 'Toggle done' : 'Отметить'}"></button>
        <span class="rt-icon">${r.icon || '🔁'}</span>
        <div class="rt-body">
          <div class="rt-name">${escapeHtml(r.name)}</div>
          <div class="rt-meta">${describeSchedule(r)}${streak >= 2 ? ` · 🔥 ${streak} ${lang === 'en' ? 'days' : 'дней'}` : ''}</div>
        </div>
      </div>`;
    });
    html += `</div>`;
  }

  // ─── All routines (with heatmap) ───
  if (routines.length > 0) {
    html += `<h2 class="subsection-title">🔁 ${lang === 'en' ? 'All routines' : 'Все рутины'} <span class="count">${routines.length}</span></h2>`;
    html += `<div class="rt-cards">`;
    routines.forEach(r => {
      const streak = calculateRoutineStreak(r);
      const heatmap = renderRoutineHeatmap(r);
      const dueToday = isRoutineDueOn(r, todayStr);
      const doneToday = isRoutineDoneOn(r, todayStr);
      html += `<div class="rt-card">
        <div class="rt-card-head">
          <span class="rt-icon-big">${r.icon || '🔁'}</span>
          <div class="rt-card-info">
            <div class="rt-card-name">${escapeHtml(r.name)}</div>
            <div class="rt-card-sched">${describeSchedule(r)}</div>
          </div>
          <button class="btn-danger no-print" data-del-rt="${r.id}" title="${t('delete')}">🗑</button>
        </div>
        <div class="rt-card-stats">
          <div class="rt-stat"><div class="rt-stat-val">${streak}</div><div class="rt-stat-lbl">🔥 ${lang === 'en' ? 'streak' : 'серия'}</div></div>
          <div class="rt-stat"><div class="rt-stat-val">${(r.history || []).length}</div><div class="rt-stat-lbl">✓ ${lang === 'en' ? 'done total' : 'всего'}</div></div>
          ${dueToday ? `<div class="rt-stat"><div class="rt-stat-val">${doneToday ? '✓' : '○'}</div><div class="rt-stat-lbl">${lang === 'en' ? 'today' : 'сегодня'}</div></div>` : ''}
        </div>
        <div class="rt-heatmap-wrap">
          <div class="rt-heatmap-label">${lang === 'en' ? 'Last 30 days' : 'Последние 30 дней'}</div>
          <div class="rt-heatmap">${heatmap}</div>
        </div>
      </div>`;
    });
    html += `</div>`;
  } else {
    html += `<p class="empty">${lang === 'en' ? 'No routines yet. Add your first habit above to get started.' : 'Рутин пока нет. Добавь первую привычку сверху чтобы начать.'}</p>`;
  }

  document.getElementById('content').innerHTML = html;
  attachRoutinesHandlers();
}

function describeSchedule(r) {
  if (r.schedule === 'daily') return lang === 'en' ? 'Every day' : 'Каждый день';
  if (r.schedule === 'weekdays') return lang === 'en' ? 'Mon–Fri' : 'Пн–Пт';
  if (r.schedule === 'weekends') return lang === 'en' ? 'Sat–Sun' : 'Сб–Вс';
  if (r.schedule === 'weekly') {
    const days = lang === 'en' ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'] : ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'];
    return (lang === 'en' ? 'Every ' : 'Каждый ') + days[r.dayOfWeek ?? 1];
  }
  if (r.schedule === 'monthly') return (lang === 'en' ? 'On day ' : 'В день ') + (r.dayOfMonth || 1);
  if (r.schedule === 'every_n_days') return (lang === 'en' ? `Every ${r.nDays} days` : `Каждые ${r.nDays} дней`);
  return r.schedule;
}

function renderRoutineHeatmap(routine) {
  // Show last 30 days. Each cell: "due+done"=full, "due+missed"=empty, "not due"=neutral
  const cells = [];
  const today = new Date(); today.setHours(12, 0, 0, 0);
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const ds = toLocalDateStr(d);
    const due = isRoutineDueOn(routine, ds);
    const done = isRoutineDoneOn(routine, ds);
    let cls = 'rt-cell';
    if (!due) cls += ' empty';
    else if (done) cls += ' done';
    else cls += ' missed';
    cells.push(`<div class="${cls}" title="${ds}${done ? ' ✓' : (due ? ' ○' : '')}"></div>`);
  }
  return cells.join('');
}

function attachRoutinesHandlers() {
  const slug = 'routines';
  // Schedule dropdown changes nDays visibility
  const schedSel = document.getElementById('rtSchedule');
  const nDaysInp = document.getElementById('rtNDays');
  schedSel.addEventListener('change', () => {
    nDaysInp.style.display = schedSel.value === 'every_n_days' ? '' : 'none';
  });

  // Add routine
  document.getElementById('rtAddBtn').addEventListener('click', () => {
    const name = document.getElementById('rtName').value.trim();
    if (!name) { alert(lang === 'en' ? 'Enter a name' : 'Введи название'); return; }
    const icon = (document.getElementById('rtIcon').value || '🔁').trim().slice(0, 4);
    const schedule = schedSel.value;
    const r = {
      id: 'rt_' + Date.now() + '_' + Math.random().toString(36).slice(2, 5),
      name, icon, schedule,
      createdAt: Date.now(),
      history: [],
    };
    if (schedule === 'every_n_days') r.nDays = Math.max(2, Math.min(30, parseInt(nDaysInp.value) || 3));
    if (schedule === 'weekly') r.dayOfWeek = new Date().getDay();
    if (schedule === 'monthly') r.dayOfMonth = new Date().getDate();
    saveRoutines([...getRoutines(), r]);
    renderSection(slug);
  });

  // Toggle done
  document.querySelectorAll('[data-toggle-rt]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-toggle-rt');
      const date = btn.getAttribute('data-rt-date');
      toggleRoutineDone(id, date);
      renderSection(slug);
    });
  });

  // Delete routine
  document.querySelectorAll('[data-del-rt]').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (!await customConfirm(t('confirmDelete'), { icon: '🔁' })) return;
      const id = btn.getAttribute('data-del-rt');
      saveRoutines(getRoutines().filter(r => r.id !== id));
      renderSection(slug);
    });
  });
}

function renderChat() {
  const root = document.getElementById('chatRoot');
  if (!chatOpen) {
    // Docked bar (not a floating circle) — a full interface element that's
    // always reachable in one tap, from any screen. Tapping it expands into
    // the exact same full chat panel below.
    root.innerHTML = `<button class="chat-dock-bar no-print" id="chatFab">
      <span class="chat-dock-icon">✨</span>
      <span class="chat-dock-placeholder">${lang === 'en' ? 'Ask or add anything...' : 'Спроси или добавь...'}</span>
    </button>`;
    document.getElementById('chatFab').addEventListener('click', () => { chatOpen = true; renderChat(); });
    return;
  }

  const modeClass = hasApiKey() ? 'live' : '';
  const modeLabel = hasApiKey() ? '● ' + t('chatModeLive') : t('chatModeMock');

  if (chatView === 'settings') {
    const apiKey = localStorage.getItem('phosphera-anthropic-key') || '';
    root.innerHTML = `
      <div class="chat-panel no-print">
        <div class="chat-header">
          <div class="chat-title">⚙ ${t('chatSettings')}</div>
          <div class="chat-header-actions">
            <button id="chatBackBtn">←</button>
            <button id="chatCloseBtn">×</button>
          </div>
        </div>
        <div class="chat-settings-pane">
          <label>${t('chatApiKeyLabel')}</label>
          <input type="password" class="ai-key-input" id="apiKeyInput" placeholder="sk-ant-..." value="${escapeHtml(apiKey)}" />
          <p class="hint">${t('chatApiKeyHint')}</p>
          <div style="display:flex;gap:8px">
            <button class="btn-primary" id="saveKeyBtn" style="flex:1">${t('chatSaveKey')}</button>
            ${apiKey ? `<button class="btn-ghost" id="removeKeyBtn">${t('chatRemoveKey')}</button>` : ''}
          </div>
        </div>
      </div>`;
    document.getElementById('chatBackBtn').addEventListener('click', () => { chatView = 'main'; renderChat(); });
    document.getElementById('chatCloseBtn').addEventListener('click', () => { chatOpen = false; renderChat(); });
    document.getElementById('saveKeyBtn').addEventListener('click', () => {
      const v = document.getElementById('apiKeyInput').value.trim();
      if (v) localStorage.setItem('phosphera-anthropic-key', v);
      else localStorage.removeItem('phosphera-anthropic-key');
      chatView = 'main'; renderChat();
      if (getRoute().type === 'dashboard') renderDashboard();
    });
    if (apiKey) document.getElementById('removeKeyBtn').addEventListener('click', () => {
      localStorage.removeItem('phosphera-anthropic-key');
      chatView = 'main'; renderChat();
      if (getRoute().type === 'dashboard') renderDashboard();
    });
    return;
  }

  const messagesHtml = chatMessages.length === 0 ? renderChatEmpty() : chatMessages.map(m => renderChatMessage(m)).join('');
  const thinkingHtml = chatThinking ? `<div class="chat-thinking">${t('chatThinking')} <span class="dot"></span><span class="dot"></span><span class="dot"></span></div>` : '';
  const suggestionHtml = pendingSuggestion ? renderSuggestion(pendingSuggestion) : '';

  root.innerHTML = `
    <div class="chat-panel no-print">
      <div class="chat-header">
        <div class="chat-title"><span>✨</span> ${t('chatTitle')}</div>
        <div class="chat-header-actions">
          <button id="chatClearBtn" title="${lang === 'en' ? 'Clear chat' : 'Очистить чат'}">↻</button>
          <button id="chatSettingsBtn" title="${t('chatSettings')}">⚙</button>
          <button id="chatCloseBtn">×</button>
        </div>
      </div>
      ${isOwnerUser() ? `<div class="chat-mode-tabs">
        <button class="chat-mode-tab ${chatMode === 'assistant' ? 'active' : ''}" data-mode="assistant">🤖 ${lang === 'en' ? 'Assistant' : 'Помощник'}</button>
        <button class="chat-mode-tab ${chatMode === 'quickadd' ? 'active' : ''}" data-mode="quickadd">✏️ ${lang === 'en' ? 'Quick add' : 'Быстро'}</button>
      </div>` : ''}
      <div class="chat-mode-banner ${modeClass}">${modeLabel}</div>
      <div class="chat-messages" id="chatMessages">
        ${messagesHtml}${thinkingHtml}${suggestionHtml}
      </div>
      ${pendingChatImage ? `<div class="chat-img-preview"><img src="${pendingChatImage.dataUrl}" alt="" /><button id="chatImgRemove" title="${lang === 'en' ? 'Remove photo' : 'Убрать фото'}">✕</button></div>` : ''}
      <div class="chat-input-bar">
        ${chatMode === 'assistant' ? `<button class="chat-attach-btn" id="chatAttachBtn" type="button" title="${lang === 'en' ? 'Attach photo' : 'Прикрепить фото'}">📎</button>
        <input type="file" id="chatAttachFile" accept="image/*" style="display:none" />` : ''}
        <textarea id="chatInput" placeholder="${chatMode === 'assistant' ? (lang === 'en' ? 'Ask anything, I see all your notes...' : 'Спроси что угодно, я вижу все твои заметки...') : t('chatPlaceholder')}" rows="1"></textarea>
        <button class="btn-primary" id="chatSendBtn">→</button>
      </div>
    </div>`;

  // Mode switcher
  document.querySelectorAll('.chat-mode-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      chatMode = btn.getAttribute('data-mode');
      localStorage.setItem('phosphera-chat-mode', chatMode);
      pendingSuggestion = null;
      renderChat();
    });
  });
  // Clear chat
  document.getElementById('chatClearBtn').addEventListener('click', async () => {
    const ok = await customConfirm(lang === 'en' ? 'Clear conversation history?' : 'Очистить историю чата?', { icon: '↻', okText: lang === 'en' ? 'Clear' : 'Очистить' });
    if (!ok) return;
    chatMessages = [];
    pendingSuggestion = null;
    saveChat();
    renderChat();
  });
  document.getElementById('chatSettingsBtn').addEventListener('click', () => { chatView = 'settings'; renderChat(); });
  document.getElementById('chatCloseBtn').addEventListener('click', () => { chatOpen = false; renderChat(); });
  document.getElementById('chatSendBtn').addEventListener('click', sendChatMessage);
  const inp = document.getElementById('chatInput');
  inp.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } });
  inp.addEventListener('input', () => { inp.style.height = 'auto'; inp.style.height = Math.min(inp.scrollHeight, 120) + 'px'; });
  inp.focus();

  const attachBtn = document.getElementById('chatAttachBtn');
  if (attachBtn) attachBtn.addEventListener('click', () => document.getElementById('chatAttachFile').click());
  const attachFile = document.getElementById('chatAttachFile');
  if (attachFile) attachFile.addEventListener('change', async (ev) => {
    const file = (ev.target.files || [])[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast(lang === 'en' ? 'Photo too big (max 5MB)' : 'Фото слишком большое (макс. 5МБ)', { type: 'error' });
      ev.target.value = '';
      return;
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    pendingChatImage = { dataUrl, mediaType: file.type || 'image/jpeg', base64: dataUrl.split(',')[1] };
    ev.target.value = '';
    renderChat();
  });
  const imgRemove = document.getElementById('chatImgRemove');
  if (imgRemove) imgRemove.addEventListener('click', () => { pendingChatImage = null; renderChat(); });

  document.querySelectorAll('.chat-example').forEach(b => {
    b.addEventListener('click', () => { document.getElementById('chatInput').value = b.textContent; sendChatMessage(); });
  });

  if (pendingSuggestion) {
    document.getElementById('suggAddBtn').addEventListener('click', () => applySuggestion(pendingSuggestion));
    document.getElementById('suggCancelBtn').addEventListener('click', () => { pendingSuggestion = null; renderChat(); });
    const changeSel = document.getElementById('suggChangeSec');
    if (changeSel) changeSel.addEventListener('change', () => {
      pendingSuggestion.section = changeSel.value;
      // Reset subtopic when changing section (subtopic ids are section-specific)
      pendingSuggestion.subtopicId = null;
      renderChat();
    });
    const changeSub = document.getElementById('suggChangeSub');
    if (changeSub) changeSub.addEventListener('change', () => {
      pendingSuggestion.subtopicId = changeSub.value || null;
      renderChat();
    });
    const changeDate = document.getElementById('suggChangeDate');
    if (changeDate) changeDate.addEventListener('change', () => {
      pendingSuggestion.deadline = changeDate.value || null;
      renderChat();
    });
  }

  const msgs = document.getElementById('chatMessages');
  msgs.scrollTop = msgs.scrollHeight;
}

function renderChatEmpty() {
  if (chatMode === 'assistant') {
    const examples = lang === 'en' ? [
      'What should I focus on today?',
      'Find my notes about work',
      'How many overdue tasks do I have?',
      'Move all study tasks to next week'
    ] : [
      'Что мне сделать сегодня?',
      'Найди мои заметки про работу',
      'Сколько у меня просроченных задач?',
      'Перенеси все учебные задачи на след неделю'
    ];
    const lead = lang === 'en' ? '🤖 I can see all your notes. Ask me anything, or tell me to do something.' : '🤖 Я вижу все твои заметки. Спроси что угодно, или попроси что-то сделать.';
    return `<div class="chat-empty-hint"><div class="lead">${lead}</div><div class="examples">${examples.map(e => `<button class="chat-example">${escapeHtml(e)}</button>`).join('')}</div></div>`;
  }
  return `<div class="chat-empty-hint"><div class="lead">✨ ${t('chatLeadHint')}</div><div class="examples">${T[lang].chatExamples.map(e => `<button class="chat-example">${escapeHtml(e)}</button>`).join('')}</div></div>`;
}
function renderChatMessage(m) {
  const img = m.image ? `<img src="${m.image}" class="chat-msg-img" alt="" />` : '';
  return `<div class="chat-msg ${m.role}">${img}${m.text ? escapeHtml(m.text) : ''}</div>`;
}
function renderSuggestion(s) {
  const section = getSection(s.section);
  if (!section) return '';
  const sectionTitle = lang === 'en' ? section.titleEn : section.titleRu;
  const allSecs = getAllSectionsOrdered();
  const sectionOptions = allSecs.map(sec => `<option value="${sec.slug}" ${sec.slug === s.section ? 'selected' : ''}>${sec.icon} ${lang === 'en' ? sec.titleEn : sec.titleRu}</option>`).join('');

  // Subtopic options for the chosen section
  const sd = getSectionData(s.section);
  const subtopics = sd.subtopics || [];
  const subtopicOptions = subtopics.length > 0
    ? `<option value="">— ${lang === 'en' ? 'no subtopic' : 'без подтемы'}</option>` + subtopics.map(st =>
        `<option value="${st.id}" ${st.id === s.subtopicId ? 'selected' : ''}>${escapeHtml(st.name)}</option>`
      ).join('')
    : '';

  // Deadline pill class for urgency colour
  const u = s.deadline ? getUrgency(s.deadline) : '';
  const subtopicName = s.subtopicId ? (subtopics.find(st => st.id === s.subtopicId)?.name || '') : '';

  return `
    <div class="chat-suggestion-card">
      <div class="chat-sugg-preview">
        <div class="chat-sugg-text">"${escapeHtml(s.text)}"</div>
        <div class="chat-sugg-tags">
          <span class="chat-sugg-pill section">${section.icon} ${escapeHtml(sectionTitle)}</span>
          ${subtopicName ? `<span class="chat-sugg-pill subtopic">↳ ${escapeHtml(subtopicName)}</span>` : ''}
          ${s.deadline ? `<span class="chat-sugg-pill deadline ${u}">📅 ${formatDeadline(s.deadline)}</span>` : ''}
        </div>
      </div>
      <div class="chat-sugg-controls">
        <label class="chat-sugg-control">
          <span class="chat-sugg-control-lbl">${lang === 'en' ? 'Section' : 'Раздел'}</span>
          <select id="suggChangeSec" class="chat-sugg-select">${sectionOptions}</select>
        </label>
        ${subtopics.length > 0 ? `<label class="chat-sugg-control">
          <span class="chat-sugg-control-lbl">${lang === 'en' ? 'Subtopic' : 'Подтема'}</span>
          <select id="suggChangeSub" class="chat-sugg-select">${subtopicOptions}</select>
        </label>` : ''}
        <label class="chat-sugg-control">
          <span class="chat-sugg-control-lbl">${lang === 'en' ? 'Deadline' : 'Срок'}</span>
          <input type="date" id="suggChangeDate" class="chat-sugg-select" value="${s.deadline || ''}" />
        </label>
      </div>
      <div class="chat-sugg-actions-row">
        <button class="btn-ghost" id="suggCancelBtn">${t('chatCancel')}</button>
        <button class="btn-primary" id="suggAddBtn">✓ ${t('chatAddIt')}</button>
      </div>
    </div>`;
}

async function sendChatMessage() {
  const inp = document.getElementById('chatInput');
  const text = inp.value.trim();
  const image = pendingChatImage;
  if (!text && !image) return;
  chatMessages.push({ role: 'user', text, image: image ? image.dataUrl : null }); saveChat();
  inp.value = ''; inp.style.height = 'auto';
  pendingChatImage = null;
  chatThinking = true; pendingSuggestion = null; renderChat();

  // Non-owners are locked to quickadd mode (no full AI assistant)
  const effectiveMode = isOwnerUser() ? chatMode : 'quickadd';

  if (effectiveMode === 'quickadd') {
    const result = await claudeCategorize(text);
    pendingSuggestion = {
      text,
      section: result.section,
      subtopicId: result.subtopicId || null,
      deadline: result.deadline
    };
    chatThinking = false;
    saveChat();
    renderChat();
    return;
  }

  // Assistant mode: full conversation with Claude + tool use
  const history = chatMessages
    .slice(0, -1)
    .filter(m => m.role === 'user' || m.role === 'ai')
    .map(m => ({ role: m.role === 'ai' ? 'assistant' : 'user', content: m.text || '' }));

  const result = await claudeAssistant(text, history, image);
  chatThinking = false;
  chatMessages.push({ role: 'ai', text: result.text });
  saveChat();
  renderChat();
  if (result.changed) {
    if (getRoute().type === 'dashboard') renderDashboard();
    else if (getRoute().type === 'calendar') renderCalendarScreen();
    else if (getRoute().type === 'section') renderSection(getRoute().slug);
  }
}

function applySuggestion(s) {
  const slug = s.section;
  const d = getSectionData(slug);
  d.entries = [{
    id: Date.now().toString(),
    text: s.text,
    createdAt: Date.now(),
    subtopicId: s.subtopicId || null,
    deadline: s.deadline,
    done: false
  }, ...(d.entries || [])];
  updateSectionData(slug, d);
  const section = getSection(slug);
  const sectionTitle = lang === 'en' ? section.titleEn : section.titleRu;
  chatMessages.push({ role: 'ai', text: `${t('chatAdded')} ${section.icon} ${sectionTitle} ✓` });
  saveChat();
  showToast((lang === 'en' ? 'Added to ' : 'Добавлено в ') + section.icon + ' ' + sectionTitle);
  pendingSuggestion = null;
  renderChat();
  if (getRoute().type === 'dashboard') renderDashboard();
  else renderSection(getRoute().slug);
}

function saveChat() { localStorage.setItem('phosphera-chat', JSON.stringify(chatMessages)); }
function escapeHtml(s) { return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }

// Convert URLs in text to clickable links (output is safe-escaped HTML).
// Detects http(s)://, www., and plain domain.tld patterns.
function linkifyText(text) {
  if (!text) return '';
  const safeText = escapeHtml(text);
  // Match http(s)://… or www.… (URL chars, up to whitespace or end)
  const urlRe = /\b(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
  return safeText.replace(urlRe, (m) => {
    let href = m;
    if (!/^https?:\/\//i.test(href)) href = 'https://' + href;
    // Use safeUrl to block javascript: etc.
    const safe = (typeof safeUrl === 'function') ? safeUrl(href) : href;
    if (!safe) return m;
    return `<a href="${safe}" target="_blank" rel="noopener noreferrer" class="inline-link" onclick="event.stopPropagation()">${m}</a>`;
  });
}

// ─── Auto-grow textareas ───
// Any textarea on the page expands to fit its content (up to a max).
// We attach this on document-level via event delegation so it works for
// dynamically rendered textareas without having to wire each one individually.
function _autoGrowTextarea(el) {
  if (!el || el.tagName !== 'TEXTAREA') return;
  if (el.dataset.autoGrowMaxPx === 'off') return; // opt-out
  el.style.height = 'auto';
  // Cap to ~60% of viewport height so a giant note doesn't push UI off-screen
  const cap = Math.max(120, Math.floor(window.innerHeight * 0.6));
  const desired = Math.min(el.scrollHeight + 2, cap);
  el.style.height = desired + 'px';
  // If content exceeds cap, allow scroll inside the textarea
  el.style.overflowY = el.scrollHeight > cap ? 'auto' : 'hidden';
}

// Apply on input + on initial render (focus / DOM mutation)
document.addEventListener('input', (e) => {
  if (e.target && e.target.tagName === 'TEXTAREA') _autoGrowTextarea(e.target);
}, true);
document.addEventListener('focusin', (e) => {
  if (e.target && e.target.tagName === 'TEXTAREA') _autoGrowTextarea(e.target);
}, true);
// Tab key in textareas inserts a tab character (indent) instead of changing focus
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Tab' || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
  const el = e.target;
  if (!el || el.tagName !== 'TEXTAREA') return;
  // Don't break Shift+Tab (let it move focus backward)
  e.preventDefault();
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const value = el.value;
  // Insert tab (2 spaces for portability; real Tab is `\t`)
  const indent = '  ';
  el.value = value.slice(0, start) + indent + value.slice(end);
  el.selectionStart = el.selectionEnd = start + indent.length;
  // Trigger input event to re-grow
  el.dispatchEvent(new Event('input', { bubbles: true }));
}, true);
// Re-grow all visible textareas after re-renders (debounced)
let _agTimer = null;
function _autoGrowAllTextareas() {
  clearTimeout(_agTimer);
  _agTimer = setTimeout(() => {
    document.querySelectorAll('textarea').forEach(_autoGrowTextarea);
  }, 30);
}
// Hook into renderApp / renderDashboard / renderSection — observe DOM changes
const _agObserver = new MutationObserver(() => _autoGrowAllTextareas());
window.addEventListener('load', () => {
  const content = document.getElementById('content');
  if (content) _agObserver.observe(content, { childList: true, subtree: true });
  _autoGrowAllTextareas();
});
window.addEventListener('resize', _autoGrowAllTextareas);

// Safe URL for embedding in href attributes.
// Validates that protocol is http/https. Falls back to harmless "about:blank"
// if validation fails, preventing javascript:/data:/file: URI attacks and
// attribute-context HTML injection like `https://x.com" onmouseover="..."`.
function safeUrl(url) {
  if (!url || typeof url !== 'string') return 'about:blank';
  try {
    const u = new URL(url, location.href);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return 'about:blank';
    return escapeHtml(u.href);
  } catch (e) {
    return 'about:blank';
  }
}

// ─── Toast notifications ───
function _ensureToastContainer() {
  let c = document.getElementById('toastContainer');
  if (!c) {
    c = document.createElement('div');
    c.id = 'toastContainer';
    c.className = 'toast-container';
    document.body.appendChild(c);
  }
  return c;
}
function showToast(message, opts = {}) {
  const container = _ensureToastContainer();
  const toast = document.createElement('div');
  const variant = opts.type || 'success'; // 'success' | 'error' | 'info'
  toast.className = `toast toast-${variant}`;
  const icon = opts.icon ?? (variant === 'error' ? '⚠' : variant === 'info' ? 'ℹ' : '✓');
  toast.innerHTML = `<span class="toast-icon">${icon}</span><span class="toast-msg">${escapeHtml(message)}</span>`;
  container.appendChild(toast);
  // Force reflow so transition fires
  void toast.offsetWidth;
  toast.classList.add('visible');
  const duration = opts.duration ?? 2200;
  setTimeout(() => {
    toast.classList.remove('visible');
    setTimeout(() => { try { toast.remove(); } catch (e) {} }, 350);
  }, duration);
}

// ─── ATTACHMENTS ───
// Each attachment lives in two places:
//   1. localStorage cache: 'phosphera-att-{id}' → dataUrl (for instant display)
//   2. Firestore: /users/{uid}/attachments/{id} → {id, name, type, size, dataUrl, createdAt}
// ──────────────────────────────────────────────────────────────────────
// GOOGLE DRIVE BACKEND (for media attachments)
// ──────────────────────────────────────────────────────────────────────
// Storage strategy:
//   - If user has connected Drive → upload to /Drive/phosphera attachments/
//     Firestore only stores metadata (driveFileId, thumbnailUrl, name, size)
//   - If NOT connected → fallback to Firestore base64 (legacy behavior)
//   - Old Firestore-stored attachments keep working forever (storage:'firestore')
//
// Auth flow uses Google Identity Services (new GIS, NOT old gapi auth2).
// Access tokens last 1hr, then we silently refresh via prompt:'' (no UI shown
// if user already approved + still has Google session).

// Drive config (hardcoded fallback in case the module-scoped one didn't initialize yet)
const DRIVE_CONFIG = {
  DRIVE_CLIENT_ID: '1014477342117-fuuvo7p628j8fqep4vkolbg6b4b98ebf.apps.googleusercontent.com',
  DRIVE_SCOPE: 'https://www.googleapis.com/auth/drive.file',
  DRIVE_FOLDER_NAME: 'phosphera attachments',
};
let driveAccessToken = null;
let driveTokenExpiry = 0;
let driveFolderId = null; // cached id of "phosphera attachments" folder
let _gisLoaded = false;
let _gisLoading = null;

// Status helpers
function isDriveConnected() {
  if (driveAccessToken && Date.now() < driveTokenExpiry) return true;
  // Check cached token in localStorage
  try {
    const cached = JSON.parse(localStorage.getItem('phosphera-drive-token') || 'null');
    if (cached && cached.token && cached.expiry > Date.now() + 60000) {
      driveAccessToken = cached.token;
      driveTokenExpiry = cached.expiry;
      driveFolderId = cached.folderId || null;
      return true;
    }
  } catch (e) {}
  return false;
}
function isDriveEnabled() {
  return localStorage.getItem('phosphera-drive-enabled') === '1';
}
function setDriveEnabled(on) {
  if (on) localStorage.setItem('phosphera-drive-enabled', '1');
  else localStorage.removeItem('phosphera-drive-enabled');
}

// Load Google Identity Services library (one time)
async function loadGoogleIdentityServices() {
  if (_gisLoaded) return;
  if (_gisLoading) return _gisLoading;
  _gisLoading = new Promise((resolve, reject) => {
    if (window.google && window.google.accounts) { _gisLoaded = true; return resolve(); }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = () => { _gisLoaded = true; resolve(); };
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
  return _gisLoading;
}

// Request an access token. interactive=true shows the consent popup if needed.
async function requestDriveToken(interactive = false) {
  await loadGoogleIdentityServices();
  if (!DRIVE_CONFIG.DRIVE_CLIENT_ID) throw new Error('Drive client id not configured');
  return new Promise((resolve, reject) => {
    const tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: DRIVE_CONFIG.DRIVE_CLIENT_ID,
      scope: DRIVE_CONFIG.DRIVE_SCOPE,
      callback: (resp) => {
        if (resp.error) { reject(new Error(resp.error)); return; }
        if (!resp.access_token) { reject(new Error('No access token received')); return; }
        driveAccessToken = resp.access_token;
        driveTokenExpiry = Date.now() + (resp.expires_in * 1000);
        try {
          localStorage.setItem('phosphera-drive-token', JSON.stringify({
            token: driveAccessToken, expiry: driveTokenExpiry, folderId: driveFolderId
          }));
        } catch (e) {}
        resolve(driveAccessToken);
      },
      error_callback: (err) => reject(new Error(err.type || 'token error')),
    });
    // prompt='' = silent if user has approved before; 'consent' = always show consent
    tokenClient.requestAccessToken({ prompt: interactive ? 'consent' : '' });
  });
}

// Ensure we have a valid token (silent refresh when possible)
async function ensureDriveToken() {
  if (isDriveConnected()) return driveAccessToken;
  // Token expired or never granted — try silent first, fallback to interactive only if user explicitly initiated
  try {
    return await requestDriveToken(false);
  } catch (e) {
    throw new Error('Drive token expired; please reconnect Drive in Settings');
  }
}

// Interactive connect: user clicked "Connect Drive". Shows the popup.
async function connectDrive() {
  try {
    await requestDriveToken(true);
    setDriveEnabled(true);
    // Pre-create / locate the phosphera folder
    await getOrCreateDriveFolder();
    console.log('[phosphera] Drive connected successfully');
    return true;
  } catch (e) {
    console.error('[phosphera] Drive connect failed:', e);
    setDriveEnabled(false);
    throw e;
  }
}

// Disconnect: revoke token + forget folder id. Files in Drive remain.
async function disconnectDrive() {
  if (driveAccessToken) {
    try {
      await loadGoogleIdentityServices();
      google.accounts.oauth2.revoke(driveAccessToken);
    } catch (e) { console.warn('Drive revoke error:', e); }
  }
  driveAccessToken = null;
  driveTokenExpiry = 0;
  driveFolderId = null;
  try { localStorage.removeItem('phosphera-drive-token'); } catch (e) {}
  setDriveEnabled(false);
  console.log('[phosphera] Drive disconnected (files remain in Drive)');
}

// Find or create the phosphera folder. Returns folder id.
async function getOrCreateDriveFolder() {
  if (driveFolderId) return driveFolderId;
  await ensureDriveToken();
  // Search for existing folder named DRIVE_FOLDER_NAME at root that we created
  const searchQ = encodeURIComponent(
    `name='${DRIVE_CONFIG.DRIVE_FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`
  );
  let res = await fetch(`https://www.googleapis.com/drive/v3/files?q=${searchQ}&fields=files(id,name)`, {
    headers: { Authorization: `Bearer ${driveAccessToken}` }
  });
  let data = await res.json();
  if (data.files && data.files.length > 0) {
    driveFolderId = data.files[0].id;
  } else {
    // Create the folder
    res = await fetch('https://www.googleapis.com/drive/v3/files', {
      method: 'POST',
      headers: { Authorization: `Bearer ${driveAccessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: DRIVE_CONFIG.DRIVE_FOLDER_NAME,
        mimeType: 'application/vnd.google-apps.folder'
      })
    });
    data = await res.json();
    if (data.error) throw new Error('Drive folder create failed: ' + data.error.message);
    driveFolderId = data.id;
  }
  // Update cache with folder id
  try {
    const cached = JSON.parse(localStorage.getItem('phosphera-drive-token') || '{}');
    cached.folderId = driveFolderId;
    localStorage.setItem('phosphera-drive-token', JSON.stringify(cached));
  } catch (e) {}
  return driveFolderId;
}

// Upload a Blob to Drive. Returns { id, thumbnailLink, webContentLink }
async function uploadBlobToDrive(blob, filename, mimeType) {
  await ensureDriveToken();
  const folderId = await getOrCreateDriveFolder();
  const metadata = { name: filename, mimeType, parents: [folderId] };
  // multipart upload
  const boundary = '-------phosphera_' + Math.random().toString(36).slice(2);
  const arrayBuffer = await blob.arrayBuffer();
  const u8 = new Uint8Array(arrayBuffer);
  // Build multipart body manually so we can include binary
  const header = `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n--${boundary}\r\nContent-Type: ${mimeType}\r\n\r\n`;
  const footer = `\r\n--${boundary}--`;
  const enc = new TextEncoder();
  const headerBytes = enc.encode(header);
  const footerBytes = enc.encode(footer);
  const body = new Uint8Array(headerBytes.length + u8.length + footerBytes.length);
  body.set(headerBytes, 0);
  body.set(u8, headerBytes.length);
  body.set(footerBytes, headerBytes.length + u8.length);
  const res = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,thumbnailLink,webContentLink,name', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${driveAccessToken}`,
      'Content-Type': `multipart/related; boundary=${boundary}`
    },
    body: body
  });
  const data = await res.json();
  if (data.error) throw new Error('Drive upload failed: ' + data.error.message);
  return data;
}

// Download a file from Drive as a Blob (returns object URL for <img>)
const _driveBlobCache = new Map(); // fileId → objectURL
async function getDriveFileObjectUrl(fileId) {
  if (_driveBlobCache.has(fileId)) return _driveBlobCache.get(fileId);
  await ensureDriveToken();
  const res = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
    headers: { Authorization: `Bearer ${driveAccessToken}` }
  });
  if (!res.ok) throw new Error('Drive fetch failed: ' + res.status);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  _driveBlobCache.set(fileId, url);
  return url;
}

async function deleteDriveFile(fileId) {
  await ensureDriveToken();
  const res = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${driveAccessToken}` }
  });
  if (!res.ok && res.status !== 404) throw new Error('Drive delete failed: ' + res.status);
}

// ──────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────
// ATTACHMENT LAYER (auto-routes to Drive if connected, else Firestore)
// ──────────────────────────────────────────────────────────────────────
// The note's `attachments` field stores ONLY metadata: [{id, name, type, size}]
// — keeping the main doc small.

// Size limits depend on backend:
//   - Drive: 100MB safety cap (real Drive limit is 5TB, but huge files = long upload)
//   - Firestore: 700KB raw (since base64 expands to ~933KB which fits under 1MB doc limit)
const MAX_ATT_SIZE_DRIVE = 100 * 1024 * 1024; // 100 MB
const MAX_ATT_SIZE_FIRESTORE = 700 * 1024;     // 700 KB raw
const ATT_CACHE_PREFIX = 'phosphera-att-';

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

// Compress image client-side before upload. For Drive we allow larger results;
// for Firestore we MUST shrink aggressively to fit under 1MB doc limit.
async function compressImage(file, opts = {}) {
  const maxDim = opts.maxDim || 1600;
  const quality = opts.quality || 0.82;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
        else { width = Math.round(width * maxDim / height); height = maxDim; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(dataUrl);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// Iteratively compress until dataUrl fits target size. Used only for Firestore fallback.
async function compressImageToFit(file, targetBytes) {
  // Try progressively smaller dimensions/quality until it fits or we give up.
  const attempts = [
    { maxDim: 1600, quality: 0.82 },
    { maxDim: 1280, quality: 0.78 },
    { maxDim: 1024, quality: 0.72 },
    { maxDim: 800,  quality: 0.65 },
    { maxDim: 640,  quality: 0.55 },
    { maxDim: 480,  quality: 0.45 },
  ];
  for (const opts of attempts) {
    const dataUrl = await compressImage(file, opts);
    // dataUrl.length is base64 size; real bytes ~ length * 0.75
    if (dataUrl.length * 0.75 <= targetBytes) return dataUrl;
  }
  // Return last attempt even if it slightly exceeds
  return await compressImage(file, attempts[attempts.length - 1]);
}

async function uploadAttachment(file) {
  if (!file) throw new Error('No file');
  const useDrive = isDriveEnabled() && isDriveConnected();
  const isImage = file.type && file.type.startsWith('image/');

  // Validate size based on backend
  if (useDrive) {
    if (file.size > MAX_ATT_SIZE_DRIVE) {
      throw new Error((lang === 'en' ? 'File too large (max 100MB on Drive): ' : 'Файл слишком большой (макс 100МБ на Drive): ') + (file.size / 1024 / 1024).toFixed(1) + 'MB');
    }
  } else {
    // Firestore backend — only allow large files if they're images (will be heavily compressed)
    if (!isImage && file.size > MAX_ATT_SIZE_FIRESTORE) {
      throw new Error((lang === 'en'
        ? `File too large (max ${(MAX_ATT_SIZE_FIRESTORE / 1024).toFixed(0)}KB without Drive). Connect Google Drive in Settings for larger files.`
        : `Файл слишком большой (макс ${(MAX_ATT_SIZE_FIRESTORE / 1024).toFixed(0)}КБ без Drive). Подключи Google Drive в настройках для больших файлов.`)
        + ' (' + (file.size / 1024).toFixed(0) + 'KB)');
    }
    // For images, we'll compress to fit — no hard reject at this stage
  }

  const id = 'att_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);

  // ─── Drive backend (if connected) ───
  if (useDrive) {
    try {
      let blob, finalType;
      if (isImage) {
        const dataUrl = await compressImage(file, { maxDim: 2400, quality: 0.88 });
        const r = await fetch(dataUrl);
        blob = await r.blob();
        finalType = 'image/jpeg';
        try { localStorage.setItem(ATT_CACHE_PREFIX + id, dataUrl); } catch (e) {}
      } else {
        blob = file;
        finalType = file.type || 'application/octet-stream';
      }
      const driveResult = await uploadBlobToDrive(blob, `phosphera_${id}_${file.name}`, finalType);
      try {
        const url = URL.createObjectURL(blob);
        _driveBlobCache.set(driveResult.id, url);
      } catch (e) {}
      const meta = {
        id,
        name: file.name,
        type: finalType,
        size: blob.size,
        createdAt: Date.now(),
        storage: 'drive',
        driveFileId: driveResult.id,
        thumbnailUrl: driveResult.thumbnailLink || null
      };
      if (firebaseUser && window.phospheraAuth.saveAttachment) {
        try {
          await window.phospheraAuth.saveAttachment(firebaseUser.uid, id, meta);
        } catch (e) { console.warn('Failed to save drive meta to Firestore:', e); }
      }
      return meta;
    } catch (e) {
      console.error('Drive upload failed, falling back to Firestore:', e);
    }
  }

  // ─── Firestore fallback ───
  let dataUrl;
  if (isImage) {
    try {
      // Aggressively compress for Firestore (must fit under 1MB doc limit)
      dataUrl = await compressImageToFit(file, 750 * 1024);
    } catch (e) {
      throw new Error((lang === 'en' ? 'Image compression failed: ' : 'Сжатие не удалось: ') + e.message);
    }
  } else {
    dataUrl = await readFileAsDataURL(file);
  }
  // Final safety check after compression
  if (dataUrl.length > 950 * 1024) {
    throw new Error(lang === 'en'
      ? 'Image too large even after compression. Connect Google Drive in Settings for full quality.'
      : 'Изображение слишком большое даже после сжатия. Подключи Google Drive для полного качества.');
  }
  const meta = {
    id,
    name: file.name,
    type: file.type || 'application/octet-stream',
    size: dataUrl.length,
    createdAt: Date.now(),
    storage: 'firestore',
  };
  const fullData = { ...meta, dataUrl };
  try { localStorage.setItem(ATT_CACHE_PREFIX + id, dataUrl); } catch (e) {}
  if (firebaseUser && window.phospheraAuth.saveAttachment) {
    try {
      await window.phospheraAuth.saveAttachment(firebaseUser.uid, id, fullData);
    } catch (e) {
      console.warn('Failed to upload attachment to cloud:', e);
    }
  }
  return meta;
}

async function loadAttachmentDataUrl(attId) {
  // 1. Local cache first (fastest)
  const cached = localStorage.getItem(ATT_CACHE_PREFIX + attId);
  if (cached) return cached;
  if (!firebaseUser) return null;
  // 2. Load metadata from Firestore to determine backend
  let att = null;
  try {
    att = await window.phospheraAuth.loadAttachment(firebaseUser.uid, attId);
  } catch (e) { console.warn('loadAttachment error', e); return null; }
  if (!att) return null;
  // 3a. Drive-stored attachment — fetch from Drive
  if (att.storage === 'drive' && att.driveFileId) {
    try {
      const url = await getDriveFileObjectUrl(att.driveFileId);
      // Return objectURL (works in <img src=...>). Not cached as dataUrl
      // because object URLs are small references.
      return url;
    } catch (e) {
      console.warn('Drive fetch failed for', attId, e);
      return null;
    }
  }
  // 3b. Firestore-stored (legacy)
  if (att.dataUrl) {
    try { localStorage.setItem(ATT_CACHE_PREFIX + attId, att.dataUrl); } catch (e) {}
    return att.dataUrl;
  }
  return null;
}

async function deleteAttachment(attId) {
  try { localStorage.removeItem(ATT_CACHE_PREFIX + attId); } catch(e) {}
  // Look up backend before removing from Firestore (we need driveFileId)
  if (firebaseUser && window.phospheraAuth.loadAttachment) {
    try {
      const att = await window.phospheraAuth.loadAttachment(firebaseUser.uid, attId);
      if (att && att.storage === 'drive' && att.driveFileId) {
        try { await deleteDriveFile(att.driveFileId); }
        catch (e) { console.warn('Drive delete failed (orphan in Drive):', e); }
        // Free cached object URL
        const cachedUrl = _driveBlobCache.get(att.driveFileId);
        if (cachedUrl) { try { URL.revokeObjectURL(cachedUrl); } catch (e) {} _driveBlobCache.delete(att.driveFileId); }
      }
    } catch (e) { /* ignore */ }
  }
  if (firebaseUser && window.phospheraAuth.deleteAttachment) {
    try { await window.phospheraAuth.deleteAttachment(firebaseUser.uid, attId); }
    catch (e) { console.warn('deleteAttachment error', e); }
  }
}

function renderAttachmentThumbs(attachments, options = {}) {
  if (!attachments || attachments.length === 0) return '';
  const editable = options.editable !== false;
  const imageCount = attachments.filter(a => a.type && a.type.startsWith('image/')).length;
  let html = `<div class="att-row ${imageCount > 1 ? 'has-gallery' : ''}">`;
  // Optional small badge above the row showing how many photos for quick visual cue
  if (imageCount > 1) {
    html = `<div class="att-row-wrap"><div class="att-gallery-badge" title="${lang === 'en' ? 'Tap any photo to browse' : 'Тапни любое фото для листания'}">📷 ${imageCount}</div>` + html;
  }
  attachments.forEach(a => {
    const isImage = a.type && a.type.startsWith('image/');
    if (isImage) {
      html += `<div class="att-thumb img" data-att-id="${a.id}" data-att-name="${escapeHtml(a.name)}" data-att-type="${escapeHtml(a.type)}">
        <img alt="${escapeHtml(a.name)}" loading="lazy" />
        ${editable ? `<button class="att-del-btn" data-del-att="${a.id}" title="Delete">×</button>` : ''}
      </div>`;
    } else {
      const ext = a.name.split('.').pop().toLowerCase().slice(0, 4);
      let icon = '📄';
      if (['pdf'].includes(ext)) icon = '📕';
      else if (['doc','docx'].includes(ext)) icon = '📘';
      else if (['xls','xlsx','csv'].includes(ext)) icon = '📊';
      else if (['zip','rar','7z'].includes(ext)) icon = '🗜';
      else if (['mp3','wav','m4a'].includes(ext)) icon = '🎵';
      else if (['mp4','mov','avi'].includes(ext)) icon = '🎬';
      html += `<div class="att-thumb file" data-att-id="${a.id}" data-att-name="${escapeHtml(a.name)}" data-att-type="${escapeHtml(a.type)}">
        <div class="att-file-icon">${icon}</div>
        <div class="att-file-name">${escapeHtml(a.name.length > 18 ? a.name.slice(0, 16) + '…' : a.name)}</div>
        ${editable ? `<button class="att-del-btn" data-del-att="${a.id}" title="Delete">×</button>` : ''}
      </div>`;
    }
  });
  html += `</div>`;
  if (imageCount > 1) html += `</div>`;
  return html;
}

// Wire up thumbnails: load images lazily, attach click handlers for lightbox/download
function wireAttachmentThumbs(container, attachments, options = {}) {
  if (!container || !attachments || attachments.length === 0) return;
  // Lazy-load images
  container.querySelectorAll('.att-thumb.img img').forEach(async (imgEl) => {
    const wrapper = imgEl.closest('.att-thumb');
    const attId = wrapper.getAttribute('data-att-id');
    const dataUrl = await loadAttachmentDataUrl(attId);
    if (dataUrl) imgEl.src = dataUrl;
    else imgEl.alt = '⚠ Failed to load';
  });
  // Click — open lightbox (image) or download (file)
  container.querySelectorAll('.att-thumb').forEach(thumb => {
    thumb.addEventListener('click', async (e) => {
      if (e.target.closest('.att-del-btn')) return;
      const attId = thumb.getAttribute('data-att-id');
      const name = thumb.getAttribute('data-att-name');
      const type = thumb.getAttribute('data-att-type');
      const dataUrl = await loadAttachmentDataUrl(attId);
      if (!dataUrl) {
        alert(lang === 'en' ? 'Failed to load attachment' : 'Не удалось загрузить вложение');
        return;
      }
      if (type && type.startsWith('image/')) {
        // Build gallery context — all images in this attachment row
        openLightbox(dataUrl, name, {
          items: attachments,
          currentId: attId,
        });
      } else {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = name;
        a.click();
      }
    });
  });
  // Delete buttons
  if (options.onDelete) {
    container.querySelectorAll('[data-del-att]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const attId = btn.getAttribute('data-del-att');
        const ok = await customConfirm(lang === 'en' ? 'Delete this attachment?' : 'Удалить это вложение?', { icon: '🗑' });
        if (!ok) return;
        await deleteAttachment(attId);
        options.onDelete(attId);
      });
    });
  }
}

// Gallery-aware lightbox.
// Old signature: openLightbox(dataUrl, name) — single image (still works)
// New signature: openLightbox(dataUrl, name, gallery) — gallery = { items: [{id, name, type}], currentIdx }
// When `gallery` is provided, arrow keys / swipe / dot navigation move between images.
async function openLightbox(dataUrl, name, gallery) {
  const lb = document.createElement('div');
  lb.className = 'att-lightbox';
  const isImage = (dataUrl || '').startsWith('data:image/');

  // Determine if gallery has more than 1 image to enable navigation UI
  let imageItems = [];
  let currentIdx = 0;
  if (gallery && gallery.items && gallery.items.length > 0) {
    imageItems = gallery.items.filter(it => it.type && it.type.startsWith('image/'));
    currentIdx = imageItems.findIndex(it => it.id === gallery.currentId);
    if (currentIdx < 0) currentIdx = 0;
  }
  const hasGallery = imageItems.length > 1;

  lb.innerHTML = `
    <button class="att-lb-close">×</button>
    ${isImage ? `<button class="att-lb-copy" title="${lang === 'en' ? 'Copy image' : 'Копировать'}">⎘</button>` : ''}
    <button class="att-lb-download" title="${lang === 'en' ? 'Download' : 'Скачать'}">⬇</button>
    ${hasGallery ? `<button class="att-lb-nav att-lb-prev" title="${lang === 'en' ? 'Previous' : 'Назад'}">‹</button>
                    <button class="att-lb-nav att-lb-next" title="${lang === 'en' ? 'Next' : 'Вперёд'}">›</button>` : ''}
    <img src="${dataUrl}" alt="${escapeHtml(name)}" />
    <div class="att-lb-name">${escapeHtml(name)}${hasGallery ? `  <span class="att-lb-counter">${currentIdx + 1} / ${imageItems.length}</span>` : ''}</div>
    ${hasGallery ? `<div class="att-lb-dots">${imageItems.map((_, i) => `<span class="att-lb-dot ${i === currentIdx ? 'active' : ''}" data-go-idx="${i}"></span>`).join('')}</div>` : ''}
    <div class="att-lb-toast" id="attLbToast"></div>
  `;
  document.body.appendChild(lb);
  requestAnimationFrame(() => lb.classList.add('visible'));

  // Mutable state for current item
  let currentDataUrl = dataUrl;
  let currentName = name;

  const close = () => {
    lb.classList.remove('visible');
    setTimeout(() => lb.remove(), 280);
    document.removeEventListener('keydown', onKey);
  };
  const showToast = (msg) => {
    const toast = lb.querySelector('#attLbToast');
    toast.textContent = msg;
    toast.classList.add('visible');
    setTimeout(() => toast.classList.remove('visible'), 1800);
  };

  // Navigate to a specific image index (gallery mode only)
  const goTo = async (newIdx) => {
    if (!hasGallery) return;
    if (newIdx < 0) newIdx = imageItems.length - 1;
    if (newIdx >= imageItems.length) newIdx = 0;
    currentIdx = newIdx;
    const item = imageItems[newIdx];
    const imgEl = lb.querySelector('img');
    imgEl.classList.add('switching');
    const url = await loadAttachmentDataUrl(item.id);
    if (!url) {
      imgEl.classList.remove('switching');
      showToast(lang === 'en' ? '⚠ Failed to load' : '⚠ Не загрузилось');
      return;
    }
    currentDataUrl = url;
    currentName = item.name;
    imgEl.src = url;
    imgEl.alt = item.name;
    setTimeout(() => imgEl.classList.remove('switching'), 50);
    // Update name + counter + dots
    const nameEl = lb.querySelector('.att-lb-name');
    if (nameEl) nameEl.innerHTML = `${escapeHtml(item.name)}  <span class="att-lb-counter">${newIdx + 1} / ${imageItems.length}</span>`;
    lb.querySelectorAll('.att-lb-dot').forEach((d, i) => d.classList.toggle('active', i === newIdx));
  };

  lb.querySelector('.att-lb-close').addEventListener('click', close);
  lb.addEventListener('click', (e) => { if (e.target === lb) close(); });

  if (hasGallery) {
    lb.querySelector('.att-lb-prev').addEventListener('click', (e) => { e.stopPropagation(); goTo(currentIdx - 1); });
    lb.querySelector('.att-lb-next').addEventListener('click', (e) => { e.stopPropagation(); goTo(currentIdx + 1); });
    lb.querySelectorAll('.att-lb-dot').forEach(dot => {
      dot.addEventListener('click', (e) => { e.stopPropagation(); goTo(parseInt(dot.getAttribute('data-go-idx'))); });
    });
    // Touch swipe between images
    let startX = 0, startY = 0, dragging = false, startTime = 0;
    lb.addEventListener('touchstart', (e) => {
      if (e.target.closest('.att-lb-close, .att-lb-nav, .att-lb-dot, .att-lb-download, .att-lb-copy')) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      dragging = true;
      startTime = Date.now();
    }, { passive: true });
    lb.addEventListener('touchend', (e) => {
      if (!dragging) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      const dt = Date.now() - startTime;
      dragging = false;
      // Only horizontal swipes count, and not too vertical
      if (Math.abs(dy) > Math.abs(dx) * 1.2) return;
      if (Math.abs(dx) > 50 || (dt < 200 && Math.abs(dx) > 25)) {
        if (dx < 0) goTo(currentIdx + 1);
        else goTo(currentIdx - 1);
      }
    }, { passive: true });
  }

  lb.querySelector('.att-lb-download').addEventListener('click', (e) => {
    e.stopPropagation();
    const a = document.createElement('a');
    a.href = currentDataUrl;
    a.download = currentName;
    a.click();
  });
  const copyBtn = lb.querySelector('.att-lb-copy');
  if (copyBtn) {
    copyBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      try {
        const res = await fetch(currentDataUrl);
        const blob = await res.blob();
        if (navigator.clipboard && window.ClipboardItem) {
          let toClip = blob;
          if (blob.type !== 'image/png') {
            toClip = await new Promise((resolve, reject) => {
              const img = new Image();
              img.onload = () => {
                const c = document.createElement('canvas');
                c.width = img.naturalWidth; c.height = img.naturalHeight;
                c.getContext('2d').drawImage(img, 0, 0);
                c.toBlob(b => b ? resolve(b) : reject(new Error('canvas.toBlob failed')), 'image/png');
              };
              img.onerror = () => reject(new Error('img load failed'));
              img.src = currentDataUrl;
            });
          }
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': toClip })]);
          showToast(lang === 'en' ? '✓ Copied' : '✓ Скопировано');
        } else {
          throw new Error('Clipboard API unavailable');
        }
      } catch (err) {
        try {
          await navigator.clipboard.writeText(currentDataUrl);
          showToast(lang === 'en' ? '✓ URL copied' : '✓ URL скопирован');
        } catch (err2) {
          showToast(lang === 'en' ? '✗ Copy failed' : '✗ Не вышло');
        }
      }
    });
  }
  const onKey = (e) => {
    if (e.key === 'Escape') return close();
    if (hasGallery && e.key === 'ArrowLeft') { e.preventDefault(); goTo(currentIdx - 1); }
    else if (hasGallery && e.key === 'ArrowRight') { e.preventDefault(); goTo(currentIdx + 1); }
  };
  document.addEventListener('keydown', onKey);
}

// ───── Bookmark add/edit modal ─────
// ─────────────────────────────────────────────────────────────────────
// GLOBAL SEARCH
// ─────────────────────────────────────────────────────────────────────
// Cmd+K / Ctrl+K opens a search modal that fuzzy-matches across all entries,
// links, and section names. Click result → navigates to section.
function openGlobalSearch() {
  // If already open, focus the input
  const existing = document.getElementById('searchOverlay');
  if (existing) {
    const inp = document.getElementById('searchInput');
    if (inp) inp.focus();
    return;
  }

  const overlay = document.createElement('div');
  overlay.id = 'searchOverlay';
  overlay.className = 'search-overlay';
  overlay.innerHTML = `
    <div class="search-box">
      <div class="search-input-row">
        <span class="search-icon">🔍</span>
        <input type="text" id="searchInput" placeholder="${lang === 'en' ? 'Search notes, tasks, links…' : 'Поиск заметок, задач, ссылок…'}" autocomplete="off" />
        <button class="search-close" id="searchClose">✕</button>
      </div>
      <div class="search-results" id="searchResults">
        <div class="search-hint">${lang === 'en' ? 'Type to search across all your notes' : 'Начни печатать чтобы искать по всем заметкам'}</div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const input = document.getElementById('searchInput');
  const results = document.getElementById('searchResults');
  const close = () => { try { overlay.remove(); } catch(e) {} };

  document.getElementById('searchClose').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  let selectedIdx = -1;
  let currentResults = [];

  const runSearch = (query) => {
    const q = query.trim().toLowerCase();
    if (!q) {
      results.innerHTML = `<div class="search-hint">${lang === 'en' ? 'Type to search across all your notes' : 'Начни печатать чтобы искать по всем заметкам'}</div>`;
      currentResults = [];
      selectedIdx = -1;
      return;
    }
    currentResults = searchAcrossAll(q);
    if (currentResults.length === 0) {
      results.innerHTML = `<div class="search-empty">${lang === 'en' ? 'Nothing found for' : 'Ничего не найдено для'} "${escapeHtml(query)}"</div>`;
      return;
    }
    // Group results by section
    const grouped = new Map();
    currentResults.forEach(r => {
      if (!grouped.has(r.sectionSlug)) grouped.set(r.sectionSlug, []);
      grouped.get(r.sectionSlug).push(r);
    });
    let html = '';
    let globalIdx = 0;
    for (const [slug, items] of grouped) {
      const sec = getSection(slug);
      const secTitle = sec ? (lang === 'en' ? sec.titleEn : sec.titleRu) : slug;
      const secIcon = sec ? sec.icon : '📁';
      html += `<div class="search-group">
        <div class="search-group-label">${secIcon} ${escapeHtml(secTitle)}</div>`;
      items.forEach(r => {
        const subBadge = r.subtopicName ? `<span class="search-sub-badge">${escapeHtml(r.subtopicName)}</span>` : '';
        const typeBadge = r.type === 'link'
          ? `<span class="search-type-badge link">🔗</span>`
          : r.entry?.done
            ? `<span class="search-type-badge done">✓</span>`
            : r.entry?.deadline
              ? `<span class="search-type-badge deadline ${getUrgency(r.entry.deadline)}">📅 ${formatDeadline(r.entry.deadline)}</span>`
              : '';
        html += `<button class="search-result" data-search-idx="${globalIdx}" data-search-slug="${r.sectionSlug}">
          <div class="search-result-main">
            <div class="search-result-text">${highlightMatch(r.text, q)}</div>
            <div class="search-result-meta">${subBadge}${typeBadge}</div>
          </div>
        </button>`;
        globalIdx++;
      });
      html += `</div>`;
    }
    results.innerHTML = html;
    selectedIdx = -1;
    // Click handlers
    results.querySelectorAll('[data-search-idx]').forEach(btn => {
      btn.addEventListener('click', () => {
        const slug = btn.getAttribute('data-search-slug');
        close();
        location.hash = `#/s/${slug}`;
      });
    });
  };

  // Debounced input handling
  let searchTimer = null;
  input.addEventListener('input', (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => runSearch(e.target.value), 80);
  });

  // Keyboard navigation
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { close(); return; }
    const items = results.querySelectorAll('[data-search-idx]');
    if (items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedIdx = Math.min(selectedIdx + 1, items.length - 1);
      updateSelection(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedIdx = Math.max(selectedIdx - 1, 0);
      updateSelection(items);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = selectedIdx >= 0 ? items[selectedIdx] : items[0];
      if (target) target.click();
    }
  });

  const updateSelection = (items) => {
    items.forEach((it, i) => it.classList.toggle('selected', i === selectedIdx));
    if (selectedIdx >= 0) items[selectedIdx].scrollIntoView({ block: 'nearest' });
  };

  // Auto-focus + tiny delay so iOS keyboard opens
  setTimeout(() => input.focus(), 50);
}

// Search across all sections — returns ranked results
function searchAcrossAll(query) {
  const q = query.toLowerCase();
  const results = [];
  const sections = getAllSections();
  sections.forEach(sec => {
    const sd = getSectionData(sec.slug);
    if (!sd) return;
    const subtopicMap = {};
    (sd.subtopics || []).forEach(st => { subtopicMap[st.id] = st.name; });
    // Entries
    (sd.entries || []).forEach(e => {
      const text = (e.text || '').toLowerCase();
      if (text.includes(q)) {
        // Score: earlier match = higher
        const score = 1000 - text.indexOf(q);
        results.push({
          type: 'entry',
          sectionSlug: sec.slug,
          text: e.text,
          entry: e,
          subtopicName: e.subtopicId ? subtopicMap[e.subtopicId] : null,
          score: score + (e.done ? -500 : 0) + (e.pinned ? 200 : 0),
        });
      }
    });
    // Links
    (sd.links || []).forEach(l => {
      const haystack = `${l.title || ''} ${l.url || ''}`.toLowerCase();
      if (haystack.includes(q)) {
        results.push({
          type: 'link',
          sectionSlug: sec.slug,
          text: l.title || l.url,
          link: l,
          subtopicName: l.subtopicId ? subtopicMap[l.subtopicId] : null,
          score: 800 - haystack.indexOf(q),
        });
      }
    });
  });
  results.sort((a, b) => b.score - a.score);
  return results.slice(0, 50);
}

function highlightMatch(text, query) {
  const safeText = escapeHtml(text);
  if (!query) return safeText;
  const q = query.toLowerCase();
  const lower = text.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx < 0) return safeText;
  const before = escapeHtml(text.slice(0, idx));
  const match = escapeHtml(text.slice(idx, idx + query.length));
  const after = escapeHtml(text.slice(idx + query.length));
  return `${before}<mark>${match}</mark>${after}`;
}

function openBookmarkModal(bookmarkId) {
  const bookmarks = getBookmarks();
  const isEdit = !!bookmarkId;
  const existing = isEdit ? bookmarks.find(b => b.id === bookmarkId) : null;
  if (isEdit && !existing) return;

  const overlay = document.createElement('div');
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `<div class="modal" style="max-width:420px">
    <div class="modal-icon">${existing?.icon || '🔗'}</div>
    <div class="modal-title">${isEdit ? (lang === 'en' ? 'Edit bookmark' : 'Изменить ссылку') : (lang === 'en' ? 'Add bookmark' : 'Добавить ссылку')}</div>
    <div style="display:flex;flex-direction:column;gap:10px;text-align:left;margin:14px 0">
      <div>
        <div style="font-size:11px;color:var(--text-muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:0.1em;font-family:'JetBrains Mono',monospace">${lang === 'en' ? 'Icon (emoji)' : 'Иконка (эмодзи)'}</div>
        <input id="bmIcon" type="text" maxlength="4" placeholder="🔗" value="${escapeHtml(existing?.icon || '🔗')}" style="font-size:20px;text-align:center;width:80px" />
      </div>
      <div>
        <div style="font-size:11px;color:var(--text-muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:0.1em;font-family:'JetBrains Mono',monospace">${lang === 'en' ? 'Label' : 'Название'}</div>
        <input id="bmLabel" type="text" placeholder="Spotify" value="${escapeHtml(existing?.label || '')}" />
      </div>
      <div>
        <div style="font-size:11px;color:var(--text-muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:0.1em;font-family:'JetBrains Mono',monospace">${lang === 'en' ? 'URL' : 'Адрес'}</div>
        <input id="bmUrl" type="url" placeholder="https://example.com" value="${escapeHtml(existing?.url || '')}" />
      </div>
    </div>
    <div class="modal-actions">
      ${isEdit ? `<button class="btn-danger" id="bmDelete" style="flex:0 0 auto">🗑</button>` : ''}
      <button class="btn-ghost" id="bmCancel">${t('cancel')}</button>
      <button class="btn-primary" id="bmSave">${isEdit ? (lang === 'en' ? 'Save' : 'Сохранить') : (lang === 'en' ? 'Add' : 'Добавить')}</button>
    </div>
  </div>`;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#bmCancel').addEventListener('click', close);

  overlay.querySelector('#bmSave').addEventListener('click', () => {
    const icon = (overlay.querySelector('#bmIcon').value || '🔗').trim().slice(0, 4);
    const label = overlay.querySelector('#bmLabel').value.trim();
    let url = overlay.querySelector('#bmUrl').value.trim();
    if (!label || !url) {
      alert(lang === 'en' ? 'Label and URL are required' : 'Название и адрес обязательны');
      return;
    }
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;

    const list = getBookmarks();
    if (isEdit) {
      const i = list.findIndex(b => b.id === bookmarkId);
      if (i >= 0) list[i] = { ...list[i], icon, label, url };
    } else {
      list.push({ id: 'bm_' + Date.now() + '_' + Math.random().toString(36).slice(2, 5), icon, label, url });
    }
    saveBookmarks(list);
    close();
    renderDashboard();
  });

  if (isEdit) {
    overlay.querySelector('#bmDelete').addEventListener('click', async () => {
      const ok = await customConfirm(
        lang === 'en' ? `Delete bookmark "${existing.label}"?` : `Удалить "${existing.label}"?`,
        { icon: '🗑' }
      );
      if (!ok) return;
      saveBookmarks(getBookmarks().filter(b => b.id !== bookmarkId));
      close();
      renderDashboard();
    });
  }
}

// ───── Admin Panel ─────
async function openAdminPanel() {
  if (!window.phospheraAuth.isAdmin()) {
    alert(lang === 'en' ? 'Admins only.' : 'Только для администраторов.');
    return;
  }
  let whitelist = { emails: [], phones: [] };
  try { whitelist = await window.phospheraAuth.getWhitelist(); }
  catch (e) {
    alert((lang === 'en' ? 'Failed to load whitelist: ' : 'Не удалось загрузить whitelist: ') + (e.message || e));
    return;
  }

  const overlay = document.createElement('div');
  overlay.className = 'cust-overlay';

  const renderPanel = () => {
    overlay.innerHTML = `
      <div class="cust-panel">
        <div class="cust-header">
          <div>
            <div class="cust-h1">🛡 ${lang === 'en' ? 'Admin panel' : 'Админ-панель'}</div>
            <div class="cust-h2">${lang === 'en' ? 'Manage who can access phosphera. Hardcoded admins always have access.' : 'Управляй кто может заходить в phosphera. Захардкоженные админы всегда имеют доступ.'}</div>
          </div>
          <button class="btn-icon cust-close" id="adminClose" title="Close">✕</button>
        </div>
        <div class="cust-body">
          <h3 class="admin-section-h">📧 ${lang === 'en' ? 'Allowed emails' : 'Разрешённые email'}</h3>
          <div class="admin-list" id="adminEmailsList">
            ${(whitelist.emails || []).map(e => `<div class="admin-row">
              <span class="admin-row-icon">📧</span>
              <span class="admin-row-val">${escapeHtml(e)}</span>
              <button class="cust-btn cust-btn-danger" data-del-email="${escapeHtml(e)}">🗑</button>
            </div>`).join('') || `<div class="admin-empty">${lang === 'en' ? 'No additional emails. Only hardcoded admins.' : 'Дополнительных email нет. Только захардкоженные админы.'}</div>`}
          </div>
          <div class="admin-add-row">
            <input id="newEmailInput" type="email" placeholder="${lang === 'en' ? 'friend@email.com' : 'друг@email.com'}" />
            <button class="btn-primary" id="addEmailBtn">+ ${lang === 'en' ? 'Add' : 'Добавить'}</button>
          </div>

          <h3 class="admin-section-h" style="margin-top:24px">📱 ${lang === 'en' ? 'Allowed phone numbers' : 'Разрешённые номера'}</h3>
          <div class="admin-list" id="adminPhonesList">
            ${(whitelist.phones || []).map(p => `<div class="admin-row">
              <span class="admin-row-icon">📱</span>
              <span class="admin-row-val">${escapeHtml(p)}</span>
              <button class="cust-btn cust-btn-danger" data-del-phone="${escapeHtml(p)}">🗑</button>
            </div>`).join('') || `<div class="admin-empty">${lang === 'en' ? 'No additional phones.' : 'Дополнительных номеров нет.'}</div>`}
          </div>
          <div class="admin-add-row">
            <input id="newPhoneInput" type="tel" placeholder="+353 87 1234567" />
            <button class="btn-primary" id="addPhoneBtn">+ ${lang === 'en' ? 'Add' : 'Добавить'}</button>
          </div>

          <div class="admin-info">
            <div style="font-weight:600;margin-bottom:6px">ℹ️ ${lang === 'en' ? 'Important' : 'Важно'}</div>
            <ul style="margin:0;padding-left:18px;color:var(--text-secondary);font-size:12px;line-height:1.6">
              <li>${lang === 'en' ? 'Each user has their own private data — friends cannot see your notes.' : 'У каждого юзера свои приватные данные — друзья НЕ видят твои заметки.'}</li>
              <li>${lang === 'en' ? 'Phone numbers must include + and country code (e.g. +353871234567).' : 'Номера должны быть с + и кодом страны (напр. +353871234567).'}</li>
              <li>${lang === 'en' ? 'Phone auth requires the user phone to also be enabled in your Firebase SMS region settings.' : 'Phone-вход требует чтобы регион номера был включён в Firebase SMS settings.'}</li>
            </ul>
          </div>
        </div>
        <div class="cust-footer">
          <button class="btn-primary" id="adminDone" style="flex:1">✓ ${lang === 'en' ? 'Done' : 'Готово'}</button>
        </div>
      </div>
    `;
    requestAnimationFrame(() => overlay.classList.add('cust-overlay-in'));
    wireHandlers();
  };

  const close = () => {
    overlay.classList.remove('cust-overlay-in');
    setTimeout(() => overlay.remove(), 280);
  };

  const persist = async () => {
    try {
      const result = await window.phospheraAuth.saveWhitelist(whitelist);
      console.log('[phosphera admin] Whitelist persisted to cloud:', result);
      return true;
    } catch (e) {
      console.error('[phosphera admin] Save failed:', e);
      const code = e.code ? ` (${e.code})` : '';
      let hint = '';
      if (e.code === 'permission-denied' || (e.message || '').includes('permission')) {
        hint = lang === 'en'
          ? '\n\nThis is a Firestore Rules issue. Check that /admin/whitelist allows write for your UID.'
          : '\n\nЭто Firestore Rules. Проверь что /admin/whitelist разрешает запись твоему UID.';
      }
      alert((lang === 'en' ? 'Save failed: ' : 'Ошибка сохранения: ') + (e.message || e) + code + hint);
      return false;
    }
  };

  // Normalize: strip non-digits, prepend +. Accepts "+380...", "380...", "+380 73...", "(380) 73-3-3".
  const normalizePhoneInput = (s) => {
    const digits = (s || '').replace(/\D/g, '');
    if (!digits) return '';
    return '+' + digits;
  };

  const wireHandlers = () => {
    overlay.querySelector('#adminClose').addEventListener('click', close);
    overlay.querySelector('#adminDone').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    overlay.querySelector('#addEmailBtn').addEventListener('click', async () => {
      const inp = overlay.querySelector('#newEmailInput');
      const v = inp.value.trim().toLowerCase();
      if (!v || !v.includes('@')) { alert(lang === 'en' ? 'Invalid email' : 'Некорректный email'); return; }
      const existing = (whitelist.emails || []).map(x => (x || '').toLowerCase().trim());
      if (existing.includes(v)) { alert(lang === 'en' ? 'Already in list' : 'Уже в списке'); return; }
      whitelist.emails = [...(whitelist.emails || []), v];
      await persist();
      renderPanel();
    });

    overlay.querySelector('#addPhoneBtn').addEventListener('click', async () => {
      const inp = overlay.querySelector('#newPhoneInput');
      const v = normalizePhoneInput(inp.value);
      if (v.length < 8) {
        alert(lang === 'en' ? 'Enter a valid phone with country code (e.g. +353871234567)' : 'Введи номер с кодом страны (напр. +353871234567)');
        return;
      }
      // Compare normalized vs normalized (so duplicates match even if formats differ)
      const existing = (whitelist.phones || []).map(p => normalizePhoneInput(p));
      if (existing.includes(v)) { alert(lang === 'en' ? 'Already in list' : 'Уже в списке'); return; }
      whitelist.phones = [...(whitelist.phones || []), v];
      await persist();
      renderPanel();
    });

    overlay.querySelectorAll('[data-del-email]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const v = btn.getAttribute('data-del-email');
        const ok = await customConfirm(
          (lang === 'en' ? `Remove ${v} from whitelist?` : `Убрать ${v} из whitelist?`),
          { icon: '🚫' }
        );
        if (!ok) return;
        whitelist.emails = (whitelist.emails || []).filter(x => x !== v);
        await persist();
        renderPanel();
      });
    });
    overlay.querySelectorAll('[data-del-phone]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const v = btn.getAttribute('data-del-phone');
        const ok = await customConfirm(
          (lang === 'en' ? `Remove ${v} from whitelist?` : `Убрать ${v} из whitelist?`),
          { icon: '🚫' }
        );
        if (!ok) return;
        whitelist.phones = (whitelist.phones || []).filter(x => x !== v);
        await persist();
        renderPanel();
      });
    });
  };

  document.body.appendChild(overlay);
  renderPanel();
}

// ───── Link phone number to current account ─────
async function openLinkPhoneFlow() {
  if (!window.phospheraAuth || !window.phospheraAuth.linkPhone) {
    alert(lang === 'en'
      ? 'Phone linking is not configured. See Firebase setup instructions.'
      : 'Привязка номера не настроена. См. инструкции Firebase.');
    return;
  }
  const overlay = document.createElement('div');
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `<div class="modal" style="max-width:420px">
    <div class="modal-icon">📱</div>
    <div class="modal-title">${lang === 'en' ? 'Link phone number' : 'Привязать номер'}</div>
    <div class="modal-text">${lang === 'en' ? 'Add a phone number to sign in with SMS as alternative to Google.' : 'Добавь номер чтобы заходить по SMS как альтернатива Google.'}</div>
    <div id="lpStep1">
      <div style="margin-bottom:8px;color:var(--text-muted);font-size:12px;text-align:left">${lang === 'en' ? 'Phone number (with country code)' : 'Номер с кодом страны'}</div>
      <input id="lpPhone" type="tel" placeholder="+353 87 1234567" style="margin-bottom:12px" />
      <div id="lpRecaptcha" style="margin-bottom:12px;display:flex;justify-content:center"></div>
      <div class="modal-actions">
        <button class="btn-ghost" id="lpCancel1">${t('cancel')}</button>
        <button class="btn-primary" id="lpSendCode">${lang === 'en' ? 'Send code' : 'Отправить код'}</button>
      </div>
    </div>
    <div id="lpStep2" style="display:none">
      <div style="margin-bottom:8px;color:var(--text-muted);font-size:12px;text-align:left">${lang === 'en' ? '6-digit code from SMS' : '6-значный код из SMS'}</div>
      <input id="lpCode" type="text" inputmode="numeric" maxlength="6" placeholder="123456" style="margin-bottom:12px;font-size:20px;text-align:center;letter-spacing:6px;font-family:'JetBrains Mono',monospace" />
      <div class="modal-actions">
        <button class="btn-ghost" id="lpCancel2">${t('cancel')}</button>
        <button class="btn-primary" id="lpVerify">${lang === 'en' ? 'Link phone' : 'Привязать'}</button>
      </div>
    </div>
    <div id="lpError" style="margin-top:12px;color:var(--danger);font-size:13px;display:none;text-align:center"></div>
    <div id="lpSuccess" style="margin-top:12px;color:#34D399;font-size:14px;display:none;text-align:center;font-weight:600"></div>
  </div>`;
  document.body.appendChild(overlay);

  let confirmation = null;
  const close = () => overlay.remove();
  const showError = (m) => { const e = overlay.querySelector('#lpError'); e.textContent = m; e.style.display = ''; };
  const showStep = (n) => {
    overlay.querySelector('#lpStep1').style.display = n === 1 ? '' : 'none';
    overlay.querySelector('#lpStep2').style.display = n === 2 ? '' : 'none';
    overlay.querySelector('#lpError').style.display = 'none';
  };

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#lpCancel1').addEventListener('click', close);
  overlay.querySelector('#lpCancel2').addEventListener('click', close);

  overlay.querySelector('#lpSendCode').addEventListener('click', async () => {
    const phone = overlay.querySelector('#lpPhone').value.trim().replace(/\s/g, '');
    if (!phone || !phone.startsWith('+')) {
      showError(lang === 'en' ? 'Phone must start with + and country code' : 'Номер должен начинаться с + и кода страны');
      return;
    }
    try {
      confirmation = await window.phospheraAuth.linkPhone(phone, 'lpRecaptcha');
      showStep(2);
      setTimeout(() => overlay.querySelector('#lpCode').focus(), 200);
    } catch (err) {
      showError((lang === 'en' ? 'Error: ' : 'Ошибка: ') + (err.message || err));
    }
  });

  overlay.querySelector('#lpVerify').addEventListener('click', async () => {
    const code = overlay.querySelector('#lpCode').value.trim();
    if (code.length !== 6) {
      showError(lang === 'en' ? 'Enter 6 digits' : 'Введи 6 цифр');
      return;
    }
    try {
      await confirmation.confirm(code);
      const ok = overlay.querySelector('#lpSuccess');
      ok.textContent = lang === 'en' ? '✓ Phone linked successfully!' : '✓ Номер привязан!';
      ok.style.display = '';
      setTimeout(() => { close(); renderUserArea(); }, 1500);
    } catch (err) {
      showError((lang === 'en' ? 'Wrong code: ' : 'Неверный код: ') + (err.message || err));
    }
  });
}

// Custom confirm dialog — works in PWA where native confirm() is broken
function customConfirm(message, opts = {}) {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    const okText = opts.okText || (lang === 'en' ? 'Delete' : 'Удалить');
    const cancelText = opts.cancelText || (lang === 'en' ? 'Cancel' : 'Отмена');
    const icon = opts.icon || '🗑';
    const title = opts.title || (lang === 'en' ? 'Are you sure?' : 'Уверен?');
    backdrop.innerHTML = `<div class="modal">
      <div class="modal-icon">${icon}</div>
      <div class="modal-title">${escapeHtml(title)}</div>
      <div class="modal-text">${escapeHtml(message)}</div>
      <div class="modal-actions">
        <button class="btn-ghost" id="modalCancel">${escapeHtml(cancelText)}</button>
        <button class="btn-danger" id="modalOk" style="background: linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 50%), linear-gradient(180deg, #ef4444, #dc2626); color: white; border-color: #b91c1c; font-weight: 600;">${escapeHtml(okText)}</button>
      </div>
    </div>`;
    document.body.appendChild(backdrop);
    const close = (result) => { backdrop.remove(); resolve(result); };
    backdrop.querySelector('#modalCancel').addEventListener('click', () => close(false));
    backdrop.querySelector('#modalOk').addEventListener('click', () => close(true));
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(false); });
  });
}

// ─── Customize tiles modal ───
function openCustomizeTiles() {
  const overlay = document.createElement('div');
  overlay.className = 'cust-overlay';

  const renderRows = () => {
    const all = getAllSectionsOrdered();
    return all.map((s) => {
      const title = lang === 'en' ? s.titleEn : s.titleRu;
      const deleteBtn = s.isCustom ? `<button class="cust-btn cust-btn-danger" data-del-custom="${s.slug}" title="Delete custom tile">🗑</button>` : '';
      return `<div class="cust-row ${s.isCustom ? 'cust-custom' : ''} ${s.hidden ? 'cust-row-hidden' : ''}" data-slug="${s.slug}" draggable="true">
        <span class="cust-drag" title="Drag">⋮⋮</span>
        <span class="cust-icon">${s.icon}</span>
        <span class="cust-title">${escapeHtml(title)}</span>
        <button class="cust-btn" data-rename="${s.slug}" title="${t('rename')}">✎</button>
        <button class="cust-btn ${s.hidden ? 'cust-hidden-btn' : ''}" data-toggle-hide="${s.slug}" title="${s.hidden ? 'Show' : 'Hide'}">${s.hidden ? '👁‍🗨' : '👁'}</button>
        ${deleteBtn}
      </div>`;
    }).join('');
  };

  overlay.innerHTML = `
    <div class="cust-panel">
      <div class="cust-header">
        <div>
          <div class="cust-h1">⚙ ${t('customize')}</div>
          <div class="cust-h2">${t('customizeHint')}</div>
        </div>
        <button class="btn-icon cust-close" id="custClose" title="${t('done')}">✕</button>
      </div>
      <div class="cust-body">
        <div class="cust-list" id="custList">${renderRows()}</div>
        <button class="cust-add-row" id="custAddBtn">+ ${t('addTile')}</button>
      </div>
      <div class="cust-footer">
        <button class="btn-ghost" id="custReset">↺ ${t('resetTiles')}</button>
        <button class="btn-primary" id="custDone">✓ ${t('done')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  // Trigger entrance animation
  requestAnimationFrame(() => overlay.classList.add('cust-overlay-in'));

  const list = overlay.querySelector('#custList');
  const close = () => {
    overlay.classList.remove('cust-overlay-in');
    setTimeout(() => { overlay.remove(); render(); }, 280);
  };

  // ───── FLIP animation: smooth row reorder ─────
  // Records positions before change, applies invert, plays transition
  const flipRows = (mutator) => {
    // FIRST: capture old positions
    const rowsBefore = Array.from(list.querySelectorAll('.cust-row'));
    const oldRects = new Map();
    rowsBefore.forEach(row => oldRects.set(row.getAttribute('data-slug'), row.getBoundingClientRect()));

    // Run the mutation (changes order/state)
    mutator();

    // LAST: re-render contents, then capture new positions
    list.innerHTML = renderRows();
    wireRowHandlers();
    const rowsAfter = Array.from(list.querySelectorAll('.cust-row'));

    rowsAfter.forEach(row => {
      const slug = row.getAttribute('data-slug');
      const oldRect = oldRects.get(slug);
      if (!oldRect) {
        // New row — fade in
        row.style.opacity = '0';
        row.style.transform = 'translateY(-8px) scale(0.95)';
        requestAnimationFrame(() => {
          row.style.transition = 'opacity 0.35s cubic-bezier(0.22, 1, 0.36, 1), transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)';
          row.style.opacity = '';
          row.style.transform = '';
        });
        return;
      }
      const newRect = row.getBoundingClientRect();
      const dx = oldRect.left - newRect.left;
      const dy = oldRect.top - newRect.top;
      if (dx === 0 && dy === 0) return;
      // INVERT: instantly move row back to old position
      row.style.transform = `translate(${dx}px, ${dy}px)`;
      row.style.transition = 'transform 0s';
      // PLAY: animate to new position next frame
      requestAnimationFrame(() => {
        row.style.transition = 'transform 0.42s cubic-bezier(0.22, 1, 0.36, 1)';
        row.style.transform = '';
      });
    });
  };

  // Wire up all row handlers (called after every re-render)
  const wireRowHandlers = () => {
    // Delete custom tile
    list.querySelectorAll('[data-del-custom]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const slug = btn.getAttribute('data-del-custom');
        const sec = getSection(slug);
        const title = sec ? (lang === 'en' ? sec.titleEn : sec.titleRu) : slug;
        const ok = await customConfirm(
          (lang === 'en' ? `Delete custom tile "${title}"? Notes inside will also be deleted.` : `Удалить плитку "${title}"? Все записи внутри тоже удалятся.`),
          { icon: '🗑' }
        );
        if (!ok) return;
        flipRows(() => {
          const s = loadTileSettings();
          s.custom = (s.custom || []).filter(c => c.slug !== slug);
          saveTileSettings(s);
          const data = loadData();
          delete data[slug];
          saveData(data);
        });
      });
    });

    // Hide/show toggle (animates without re-rendering whole panel)
    list.querySelectorAll('[data-toggle-hide]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const slug = btn.getAttribute('data-toggle-hide');
        const row = btn.closest('.cust-row');
        const willHide = !row.classList.contains('cust-row-hidden');
        // Optimistic visual update
        row.classList.toggle('cust-row-hidden');
        // Update icon and label
        btn.classList.toggle('cust-hidden-btn');
        btn.textContent = willHide ? '👁‍🗨' : '👁';
        btn.title = willHide ? 'Show' : 'Hide';
        // Save
        const s = loadTileSettings();
        const hidden = new Set(s.hidden || []);
        if (hidden.has(slug)) hidden.delete(slug); else hidden.add(slug);
        s.hidden = [...hidden];
        saveTileSettings(s);
      });
    });

    // Rename
    list.querySelectorAll('[data-rename]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const slug = btn.getAttribute('data-rename');
        const sec = getAllSections().find(x => x.slug === slug);
        if (!sec) return;
        const s = loadTileSettings();
        s.titles = s.titles || {};
        const cur = s.titles[slug] || {};
        const newName = prompt(t('rename') + ' (' + (lang === 'en' ? 'English' : 'Русский') + ')',
          lang === 'en' ? (cur.en || sec.titleEn) : (cur.ru || sec.titleRu));
        if (newName === null) return;
        const newNameTrim = newName.trim();
        flipRows(() => {
          if (!newNameTrim) {
            if (lang === 'en') delete cur.en; else delete cur.ru;
          } else {
            if (lang === 'en') cur.en = newNameTrim; else cur.ru = newNameTrim;
          }
          s.titles[slug] = cur;
          saveTileSettings(s);
        });
      });
    });

    // Drag and drop reordering (HTML5 drag — desktop)
    let draggedSlug = null;
    let dragGhost = null;
    list.querySelectorAll('.cust-row').forEach(row => {
      row.addEventListener('dragstart', (e) => {
        draggedSlug = row.getAttribute('data-slug');
        row.classList.add('cust-row-dragging');
        e.dataTransfer.effectAllowed = 'move';
        // Try to use a transparent ghost (the row itself stays visible)
        try { e.dataTransfer.setDragImage(new Image(), 0, 0); } catch (err) {}
      });
      row.addEventListener('dragend', () => {
        row.classList.remove('cust-row-dragging');
        list.querySelectorAll('.cust-row').forEach(r => r.classList.remove('cust-row-over-above', 'cust-row-over-below'));
        draggedSlug = null;
      });
      row.addEventListener('dragover', (e) => {
        if (!draggedSlug || draggedSlug === row.getAttribute('data-slug')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        // Visual indicator: hovered row makes space depending on direction
        const rect = row.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const above = e.clientY < midY;
        list.querySelectorAll('.cust-row').forEach(r => r.classList.remove('cust-row-over-above', 'cust-row-over-below'));
        row.classList.add(above ? 'cust-row-over-above' : 'cust-row-over-below');
      });
      row.addEventListener('drop', (e) => {
        e.preventDefault();
        const targetSlug = row.getAttribute('data-slug');
        if (!draggedSlug || draggedSlug === targetSlug) return;
        const above = row.classList.contains('cust-row-over-above');
        list.querySelectorAll('.cust-row').forEach(r => r.classList.remove('cust-row-over-above', 'cust-row-over-below'));
        flipRows(() => {
          const s = loadTileSettings();
          const all = getAllSectionsOrdered();
          let order = (s.order && s.order.length) ? [...s.order] : all.map(x => x.slug);
          all.forEach(sec => { if (!order.includes(sec.slug)) order.push(sec.slug); });
          const fromIdx = order.indexOf(draggedSlug);
          order.splice(fromIdx, 1);
          let toIdx = order.indexOf(targetSlug);
          if (!above) toIdx += 1;
          order.splice(toIdx, 0, draggedSlug);
          s.order = order;
          saveTileSettings(s);
        });
      });
    });

    // Touch drag (mobile) — same idea, with manual position tracking
    list.querySelectorAll('.cust-row').forEach(row => {
      const dragHandle = row.querySelector('.cust-drag');
      let isDragging = false;
      let dragSlug = null;
      dragHandle.addEventListener('touchstart', (e) => {
        isDragging = true;
        dragSlug = row.getAttribute('data-slug');
        row.classList.add('cust-row-dragging');
      }, { passive: true });
      dragHandle.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const t = e.touches[0];
        const overEl = document.elementsFromPoint(t.clientX, t.clientY).find(el => el.classList && el.classList.contains('cust-row') && el !== row);
        list.querySelectorAll('.cust-row').forEach(r => r.classList.remove('cust-row-over-above', 'cust-row-over-below'));
        if (overEl) {
          const rect = overEl.getBoundingClientRect();
          const above = t.clientY < rect.top + rect.height / 2;
          overEl.classList.add(above ? 'cust-row-over-above' : 'cust-row-over-below');
        }
      }, { passive: false });
      dragHandle.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        isDragging = false;
        row.classList.remove('cust-row-dragging');
        const dropOn = list.querySelector('.cust-row-over-above, .cust-row-over-below');
        list.querySelectorAll('.cust-row').forEach(r => r.classList.remove('cust-row-over-above', 'cust-row-over-below'));
        if (!dropOn) return;
        const targetSlug = dropOn.getAttribute('data-slug');
        const above = dropOn.classList.contains('cust-row-over-above');
        flipRows(() => {
          const s = loadTileSettings();
          const all = getAllSectionsOrdered();
          let order = (s.order && s.order.length) ? [...s.order] : all.map(x => x.slug);
          all.forEach(sec => { if (!order.includes(sec.slug)) order.push(sec.slug); });
          const fromIdx = order.indexOf(dragSlug);
          order.splice(fromIdx, 1);
          let toIdx = order.indexOf(targetSlug);
          if (!above) toIdx += 1;
          order.splice(toIdx, 0, dragSlug);
          s.order = order;
          saveTileSettings(s);
        });
      });
    });
  };

  wireRowHandlers();

  // Header / footer button wiring
  overlay.querySelector('#custClose').addEventListener('click', close);
  overlay.querySelector('#custDone').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  overlay.querySelector('#custReset').addEventListener('click', async () => {
    const ok = await customConfirm(
      lang === 'en' ? 'Reset tile settings to defaults? Custom tiles will be removed.' : 'Сбросить настройки плиток? Свои плитки будут удалены.',
      { icon: '↺' }
    );
    if (!ok) return;
    flipRows(() => saveTileSettings({}));
  });

  overlay.querySelector('#custAddBtn').addEventListener('click', () => {
    const icon = prompt(lang === 'en' ? 'Pick an emoji icon (e.g. 🎨, 📖, 🎮):' : 'Выбери эмодзи (напр. 🎨, 📖, 🎮):', '✨');
    if (!icon) return;
    const titleEn = prompt(lang === 'en' ? 'Title (English):' : 'Название (English):', '');
    if (!titleEn || !titleEn.trim()) return;
    const titleRu = prompt(lang === 'en' ? 'Title (Russian, optional):' : 'Название (по-русски, можно пропустить):', titleEn.trim());

    flipRows(() => {
      const s = loadTileSettings();
      s.custom = s.custom || [];
      const baseSlug = 'cust_' + titleEn.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 30) || ('cust_' + Date.now());
      let slug = baseSlug;
      let i = 2;
      while (getAllSections().find(x => x.slug === slug)) {
        slug = baseSlug + '_' + i++;
      }
      s.custom.push({
        slug,
        icon: icon.trim().slice(0, 4),
        titleEn: titleEn.trim(),
        titleRu: (titleRu || titleEn).trim(),
        descEn: 'Custom tile',
        descRu: 'Своя плитка',
      });
      saveTileSettings(s);
    });
  });
}

function render() {
  // langBtn was removed from header; safe-update if it still exists somewhere (legacy)
  const langBtn = document.getElementById('langBtn');
  if (langBtn) langBtn.textContent = lang.toUpperCase();
  renderSidebar();
  const route = getRoute();
  if (route.type === 'dashboard') renderDashboard();
  else if (route.type === 'calendar') renderCalendarScreen();
  else renderSection(route.slug);
  renderUserArea();
}

window.addEventListener('hashchange', () => { if (userMock) render(); });

// Block pinch-zoom and double-tap zoom on iOS Safari (PWA mode)
// Viewport meta isn't always honored when PWA was added to home screen with older meta
document.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false });
document.addEventListener('gesturechange', (e) => e.preventDefault(), { passive: false });
document.addEventListener('gestureend', (e) => e.preventDefault(), { passive: false });
let lastTouchEnd = 0;
document.addEventListener('touchend', (e) => {
  const now = Date.now();
  if (now - lastTouchEnd < 300) e.preventDefault();
  lastTouchEnd = now;
}, { passive: false });

// ─── Mobile keyboard avoidance ───
// iOS/Android don't always resize fixed-bottom elements the instant the
// virtual keyboard opens — there's a lag where the input bar sits hidden
// behind the keyboard until the next reflow. visualViewport reports the
// REAL visible area continuously; mirroring its offset into a CSS var lets
// the docked bar / chat panel react to it directly (via transform, so it's
// instant, not animated) instead of waiting on layout.
function setupKeyboardAvoidance() {
  if (!window.visualViewport) return;
  const vv = window.visualViewport;
  const update = () => {
    const kbOffset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    document.documentElement.style.setProperty('--kb-offset', kbOffset + 'px');
  };
  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  update();
}
setupKeyboardAvoidance();

applyZoom();
renderEffects();

// ─── Fast-path login: skip login screen flash for returning users ───
// Without this, mobile users wait seconds watching the login screen while
// Firebase Auth slowly responds. By pre-setting userMock from localStorage
// cache, renderApp() goes straight to the dashboard. The real Firebase user
// replaces this in the onAuth callback shortly after.
try {
  const cached = JSON.parse(localStorage.getItem('phosphera-cached-user') || 'null');
  if (cached && cached.uid) {
    userMock = {
      name: cached.displayName || (cached.email ? cached.email.split('@')[0] : (cached.phoneNumber || 'User')),
      email: cached.email || '',
      initials: (cached.displayName || cached.email || cached.phoneNumber || 'U')[0].toUpperCase()
    };
    // Pre-set firebaseUser so isOwnerUser() works correctly during initial render
    firebaseUser = cached;
    console.log('[phosphera] Fast-path: restored cached user, skipping login screen flash');
  }
} catch (e) {}

renderApp();

// ─── DIAGNOSTIC COMMANDS (run from DevTools Console) ───
// phospheraDiagnose()  — print current state of all data
// phospheraExport()    — copy a JSON dump of everything to clipboard (backup)
// phospheraSection(slug) — print full data for one section
window.phospheraDiagnose = function() {
  console.group('🌌 phosphera state dump');
  console.log('firebaseUser:', firebaseUser ? { uid: firebaseUser.uid, email: firebaseUser.email } : null);
  console.log('cloudMeta:', cloudMeta);
  console.log('cloudMeta._bookmarks:', cloudMeta ? cloudMeta[BOOKMARKS_DATA_KEY] : 'no cloudMeta');
  console.log('cloudMeta._tileSettings:', cloudMeta ? cloudMeta[TILES_DATA_KEY] : 'no cloudMeta');
  console.log('cloudSections size:', cloudSections.size);
  const slugs = Array.from(cloudSections.keys());
  console.log('Section slugs:', slugs);
  slugs.forEach(slug => {
    const sd = cloudSections.get(slug);
    const entryCount = (sd.entries || []).length;
    const linkCount = (sd.links || []).length;
    const subtopicCount = (sd.subtopics || []).length;
    console.log('  ' + slug + ':', { entries: entryCount, links: linkCount, subtopics: subtopicCount });
  });
  console.log('loadedSlugs (lazy-loaded):', Array.from(loadedSlugs));
  console.log('sectionWatchers active:', sectionWatchers.size);
  console.log('localStorage phosphera-tiles-v1:', localStorage.getItem('phosphera-tiles-v1'));
  console.log('localStorage phosphera-bookmarks:', localStorage.getItem('phosphera-bookmarks'));
  console.log('Custom sections (from localStorage):', localStorage.getItem('phosphera-custom-sections'));
  console.groupEnd();
  return 'See output above. Screenshot it and send to support.';
};

window.phospheraExport = async function() {
  const sections = getAllSections();
  for (const sec of sections) {
    if (!loadedSlugs.has(sec.slug) && firebaseUser && window.phospheraAuth && window.phospheraAuth.loadSection) {
      try {
        const data = await window.phospheraAuth.loadSection(firebaseUser.uid, sec.slug);
        if (data) cloudSections.set(sec.slug, data);
        loadedSlugs.add(sec.slug);
      } catch(e) { console.warn('Load', sec.slug, 'failed:', e); }
    }
  }
  const dump = {
    timestamp: new Date().toISOString(),
    uid: firebaseUser ? firebaseUser.uid : null,
    email: firebaseUser ? firebaseUser.email : null,
    cloudMeta: cloudMeta,
    sections: Object.fromEntries(cloudSections),
  };
  const json = JSON.stringify(dump, null, 2);
  try {
    await navigator.clipboard.writeText(json);
    console.log('✓ Full backup copied to clipboard.', (json.length / 1024).toFixed(1), 'KB');
  } catch(e) {
    console.log('Clipboard failed, printing JSON instead:');
    console.log(json);
  }
  return 'Backup ready (' + (json.length / 1024).toFixed(1) + ' KB).';
};

window.phospheraSection = function(slug) {
  console.log('Section data for', slug, ':', cloudSections.get(slug));
  return cloudSections.get(slug);
};

// MANUAL RECOVERY: re-register a custom section in tile settings.
// Use this if a custom section's data is in Firestore but the section
// disappeared from the sidebar/dashboard because _tileSettings.custom got truncated.
//
// Example: phospheraRestoreCustom('cust_phosphera_ideas', 'phosphera structure', '🗻')
//          phospheraRestoreCustom('cust_consciousness', 'Сознание', '✨')
//          phospheraRestoreCustom('cust_english', 'English', '🗣️')
//          phospheraRestoreCustom('cust_', 'ЭТАЛОННЫЙ АВАТАР', '✨')
window.phospheraRestoreCustom = function(slug, titleRu, icon, titleEn) {
  if (!slug || !slug.startsWith('cust_')) {
    console.error('Slug must start with cust_');
    return;
  }
  const s = loadTileSettings() || {};
  if (!Array.isArray(s.custom)) s.custom = [];
  if (!Array.isArray(s.order)) s.order = [];
  // Check if already present
  if (s.custom.find(c => c.slug === slug)) {
    console.log('Custom section', slug, 'already registered');
    return;
  }
  s.custom.push({
    slug,
    titleEn: titleEn || titleRu,
    titleRu: titleRu,
    descEn: 'Custom tile',
    descRu: 'Своя плитка',
    icon: icon || '✨'
  });
  if (!s.order.includes(slug)) s.order.push(slug);
  saveTileSettings(s);
  console.log('✓ Restored custom section:', slug, titleRu);
  console.log('Reloading in 2 seconds...');
  setTimeout(() => location.reload(), 2000);
};

// Restore ALL known custom sections at once (uses your previous setup)
window.phospheraRestoreAllCustom = function() {
  const sections = [
    { slug: 'cust_phosphera_ideas', titleRu: 'phosphera structure', titleEn: 'phosphera structure', icon: '🗻' },
    { slug: 'cust_consciousness', titleRu: 'Сознание', titleEn: 'Consciousness', icon: '✨' },
    { slug: 'cust_english', titleRu: 'English', titleEn: 'English', icon: '🗣️' },
    { slug: 'cust_tech', titleRu: 'Технологии', titleEn: 'Tech', icon: '📱' },
    { slug: 'cust_', titleRu: 'ЭТАЛОННЫЙ АВАТАР', titleEn: 'ЭТАЛОННЫЙ АВАТАР', icon: '✨' },
  ];
  const s = loadTileSettings() || {};
  if (!Array.isArray(s.custom)) s.custom = [];
  if (!Array.isArray(s.order)) s.order = [];
  let added = 0;
  sections.forEach(sec => {
    if (!s.custom.find(c => c.slug === sec.slug)) {
      s.custom.push({
        slug: sec.slug,
        titleEn: sec.titleEn,
        titleRu: sec.titleRu,
        descEn: 'Custom tile',
        descRu: 'Своя плитка',
        icon: sec.icon
      });
      added++;
    }
    if (!s.order.includes(sec.slug)) s.order.push(sec.slug);
  });
  saveTileSettings(s);
  console.log('✓ Restored', added, 'custom sections');
  console.log('Reloading in 2 seconds...');
  setTimeout(() => location.reload(), 2000);
};
console.log('%c🌌 phosphera diagnostic commands:', 'color: #A374F0; font-weight: bold');
console.log('   phospheraDiagnose()  — show full state');
console.log('   phospheraExport()    — full JSON backup to clipboard');
console.log('   phospheraSection("slug") — view one section data');
console.log('   phospheraRestoreAllCustom() — RECOVER custom sections from history');
console.log('   phospheraRestoreCustom(slug, titleRu, icon, titleEn) — restore one custom section');

// ─── Service Worker registration ───
// Caches app shell for offline access. Firestore handles its own offline queue.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      console.log('[phosphera] Service Worker registered');
      // Listen for updates and prompt user when a new version is ready
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            // New version available — show subtle toast
            try {
              showToast(
                lang === 'en' ? '↻ Update available — reload to apply' : '↻ Доступно обновление — перезагрузи',
                { type: 'info', duration: 8000 }
              );
            } catch (e) {}
          }
        });
      });
    }).catch((err) => console.warn('[phosphera] SW registration failed:', err));
  });
}

// ─── Online/offline indicator ───
function _updateOnlineIndicator() {
  let bar = document.getElementById('offlineBar');
  if (!navigator.onLine) {
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'offlineBar';
      bar.className = 'offline-bar';
      bar.innerHTML = `<span class="dot"></span><span>${lang === 'en' ? 'Offline' : 'Офлайн'}</span>`;
      document.body.appendChild(bar);
      requestAnimationFrame(() => bar.classList.add('visible'));
    }
  } else {
    if (bar) {
      bar.classList.remove('visible');
      setTimeout(() => { try { bar.remove(); } catch (e) {} }, 350);
      // Brief toast confirming we're back online
      try { showToast(lang === 'en' ? '✓ Back online — syncing' : '✓ Снова онлайн — синхронизация', { duration: 1800 }); } catch (e) {}
    }
  }
}
window.addEventListener('online', _updateOnlineIndicator);
window.addEventListener('offline', _updateOnlineIndicator);
_updateOnlineIndicator();
